import { useState, useEffect, useRef } from "react";
import { supabase, signUp, signIn, signOut } from "./supabase.js";
import {
  initDB, getAll, getById, query, add, update, remove,
  createExercise, addExerciseToLibrary, updateExercise, importExerciseLibrary,
  createFitnessPlan, selectExerciseForPlan, assignPlanSection, addExerciseMetric, addExerciseNote,
  createSessionBooking, addPerformanceData, addSessionComment,
  registerClient,
  createThread, replyToThread, getThreadsForUser,
} from "./db.js";
import { OPEN_EXERCISE_LIBRARY } from "./exercise-seed.js";
import AdminPanel from "./AdminPanel.jsx";
import { APP_VERSION, RELEASE_NAME, RELEASE_DATE, CHANGELOG } from "./version.js";

// ── RECOVERY LINK DETECTION (module-level, runs before React mounts) ─────────
// Implicit flow (legacy): token arrives in URL hash — detect before Supabase
// clears it during createClient() initialisation.
// PKCE flow (default v2): a short-lived ?code= arrives in the query string.
// We can't tell at module-load time whether a ?code= is recovery or signup,
// so we just flag it to suppress getSession() and let onAuthStateChange decide.
const _rawHash = window.location.hash.replace(/^#/, "");
const _hashParams = new URLSearchParams(_rawHash);
const IS_RECOVERY_LINK = _hashParams.get("type") === "recovery";
// Arrived via the email-confirmation link (Supabase signup confirmation) — show the
// "welcome / email confirmed" screen ONCE, not on every subsequent login.
const IS_CONFIRM_LINK = _hashParams.get("type") === "signup" || _hashParams.get("type") === "email";
// NOTE: do NOT clear the hash here. The recovery hash carries the access_token,
// and Supabase reads it asynchronously during createClient() init to establish
// the recovery session. Clearing it synchronously at module load wiped the token
// before Supabase could use it — so updateUser() later failed with "session
// missing" ("reset link expired"). Supabase strips the token from the URL itself
// once it has processed it, so no manual clear is needed.
// Expired / already-used reset or magic link: Supabase redirects back with an
// #error=access_denied&error_code=otp_expired hash (no token). Detect it so we can
// send the user to request a fresh link instead of silently dumping them on home.
// Safe to clear this hash (unlike the recovery one, it carries no token).
const IS_EXPIRED_LINK = _hashParams.get("error_code") === "otp_expired" || _hashParams.get("error") === "access_denied";
if (IS_EXPIRED_LINK) {
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}
// PKCE: code in query string — suppress getSession() to avoid racing PASSWORD_RECOVERY
const IS_PKCE_CALLBACK = new URLSearchParams(window.location.search).has("code");
// Signup deep-link: the "create your account" invite email links to /?signup=1
const WANTS_SIGNUP = new URLSearchParams(window.location.search).has("signup");
// Client sign-up deep link from the invite email: /?client=<username>
const CLIENT_SIGNUP_USER = new URLSearchParams(window.location.search).get("client") || "";
// Client set-password deep link, sent after the PT activates: /?setup=<token>
const CLIENT_SETUP_TOKEN = new URLSearchParams(window.location.search).get("setup") || "";

// ── TOTP (RFC 6238) — pure Web Crypto, no dependencies ───────────────────────
// Used for admin 2FA on production. Secret stored in REACT_APP_ADMIN_TOTP_SECRET.
function _base32Decode(input) {
  const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  let bits = 0, val = 0;
  const out = [];
  for (const ch of input.toUpperCase().replace(/=+$/, "")) {
    const idx = CHARS.indexOf(ch);
    if (idx < 0) continue;
    val = (val << 5) | idx;
    bits += 5;
    if (bits >= 8) { out.push((val >>> (bits - 8)) & 0xff); bits -= 8; }
  }
  return new Uint8Array(out);
}
async function verifyTOTP(secret, token, windowSteps = 1) {
  const keyBytes = _base32Decode(secret);
  const key = await crypto.subtle.importKey(
    "raw", keyBytes, { name: "HMAC", hash: "SHA-1" }, false, ["sign"]
  );
  const step = Math.floor(Date.now() / 1000 / 30);
  for (let i = -windowSteps; i <= windowSteps; i++) {
    const counter = new DataView(new ArrayBuffer(8));
    counter.setUint32(4, step + i, false);
    const sig = new Uint8Array(await crypto.subtle.sign("HMAC", key, counter.buffer));
    const offset = sig[19] & 0x0f;
    const otp = ((sig[offset] & 0x7f) << 24 | sig[offset+1] << 16 | sig[offset+2] << 8 | sig[offset+3]) % 1_000_000;
    if (String(otp).padStart(6, "0") === String(token).trim()) return true;
  }
  return false;
}
const TOTP_SECRET   = process.env.REACT_APP_ADMIN_TOTP_SECRET || "";
// 2FA required on production only; staging uses test data so speed matters more
const IS_PRODUCTION = process.env.REACT_APP_ENV === "production";

// ── INIT DB ───────────────────────────────────────────────────────────────────
initDB();

// ── HELPERS ───────────────────────────────────────────────────────────────────
function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

// Finds the fitness plan that matches a session name (e.g. "Body Build" → "Body Build — Upper Body")
function findPlanForSession(sessionName) {
  const plans = getAll("plans");
  const n = (sessionName || "").toLowerCase().trim();
  return plans.find(p => {
    const pn = p.name.toLowerCase();
    const base = pn.split("—")[0].trim();
    return pn.startsWith(n) || base === n || base.startsWith(n) || n.startsWith(base);
  }) || null;
}

// Returns the next upcoming session string for a client, e.g. "30/03/2025 - Body Build 19:00"
function getNextSession(clientId) {
  const sessions = query("upcomingSessions", s => s.clientId === clientId);
  if (!sessions.length) return null;
  const parseDate = d => { const [dd, mm, yy] = d.split("/"); return new Date(yy, mm - 1, dd); };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sorted = [...sessions].sort((a, b) => parseDate(a.date) - parseDate(b.date));
  const next = sorted.find(s => parseDate(s.date) >= today) || sorted[0];
  return `${next.date} - ${next.name} ${next.time}`;
}

// ── SUBSCRIPTION HELPERS ─────────────────────────────────────────────────────
const TRIAL_DAYS = 60;
const TRIAL_KEY  = "mypt_trial_start";
const SUB_KEY    = "mypt_subscriptions";

function getTrialDaysLeft(ptId) {
  try {
    const stored = JSON.parse(localStorage.getItem(TRIAL_KEY) || "{}");
    if (!stored[ptId]) {
      // First login — start trial. Demo: set 55 days ago so 5 days remain.
      const d = new Date(); d.setDate(d.getDate() - 55);
      stored[ptId] = d.toISOString();
      localStorage.setItem(TRIAL_KEY, JSON.stringify(stored));
    }
    const elapsed = Math.floor((Date.now() - new Date(stored[ptId])) / 86400000);
    return Math.max(0, TRIAL_DAYS - elapsed);
  } catch { return TRIAL_DAYS; }
}

function isSubscribed(ptId) {
  try { return !!JSON.parse(localStorage.getItem(SUB_KEY) || "{}")[ptId]; }
  catch { return false; }
}

function markSubscribed(ptId, plan) {
  try {
    const stored = JSON.parse(localStorage.getItem(SUB_KEY) || "{}");
    stored[ptId] = { plan, since: new Date().toISOString() };
    localStorage.setItem(SUB_KEY, JSON.stringify(stored));
  } catch {}
}

// ── DEMO CLIENTS ──────────────────────────────────────────────────────────────
// Seed clients (Clint, Cloe, …) are labelled DEMO. A PT can suppress them from
// their client view; the preference persists in localStorage.
const HIDE_DEMO_KEY = "mypt_hide_demo_clients";
function getHideDemoClients() {
  try { return localStorage.getItem(HIDE_DEMO_KEY) === "1"; } catch { return false; }
}
function setHideDemoClients(v) {
  try { localStorage.setItem(HIDE_DEMO_KEY, v ? "1" : "0"); } catch {}
}
// Small inline "DEMO" chip shown next to seed clients.
function DemoTag() {
  return <span style={{ fontSize:9, fontWeight:700, letterSpacing:0.5, textTransform:"uppercase", padding:"2px 5px", borderRadius:4, background:C.primaryGlow, color:C.primary, border:`1px solid ${C.primary}30`, whiteSpace:"nowrap" }}>Demo</span>;
}

// ── PRODUCT RATING REQUEST ────────────────────────────────────────────────────
// Ask a PT to rate myPTOnline once they're established — after 10 logged sessions
// OR 3 months on the platform. Stored per-PT; we never re-ask once rated, and a
// "Maybe later" snoozes the prompt.
const RATING_KEY                = "mypt_product_ratings"; // { [ptId]: { rating, comment, ptName, date } }
const RATING_PROMPT_KEY         = "mypt_rating_prompt";   // { [ptId]: { snoozeUntil } }
// Real thresholds in production; relaxed on staging/dev so the demo account
// surfaces the prompt and you can preview the established-PT experience.
const RATING_SESSION_THRESHOLD  = IS_PRODUCTION ? 10 : 3;
const RATING_AGE_DAYS_THRESHOLD = IS_PRODUCTION ? 90 : 30; // ~3 months in prod

function getAccountAgeDays(ptId) {
  try {
    const start = JSON.parse(localStorage.getItem(TRIAL_KEY) || "{}")[ptId];
    return start ? Math.floor((Date.now() - new Date(start)) / 86400000) : 0;
  } catch { return 0; }
}

// Sessions attributable to this PT. Falls back to all sessions for the demo PT,
// whose hard-coded id doesn't match the seed clients' ptId.
function getPTSessionCount(ptId, ptName) {
  const clients = getAll("clients");
  const mine = clients.filter(c => c.ptId === ptId || (ptName && c.ptName === ptName));
  const clientIds = new Set(mine.map(c => c.id));
  const matched = getAll("sessions").filter(s => s.ptId === ptId || clientIds.has(s.clientId));
  if (matched.length === 0 && mine.length === 0) return getAll("sessions").length; // demo fallback
  return matched.length;
}

function getProductRating(ptId) {
  try { return JSON.parse(localStorage.getItem(RATING_KEY) || "{}")[ptId] || null; }
  catch { return null; }
}

function saveProductRating(ptId, ptName, rating, comment) {
  try {
    const all = JSON.parse(localStorage.getItem(RATING_KEY) || "{}");
    all[ptId] = { rating, comment: comment || "", ptName: ptName || "", date: new Date().toISOString() };
    localStorage.setItem(RATING_KEY, JSON.stringify(all));
  } catch {}
}

function snoozeRatingPrompt(ptId, days = 14) {
  try {
    const all = JSON.parse(localStorage.getItem(RATING_PROMPT_KEY) || "{}");
    const d = new Date(); d.setDate(d.getDate() + days);
    all[ptId] = { snoozeUntil: d.toISOString() };
    localStorage.setItem(RATING_PROMPT_KEY, JSON.stringify(all));
  } catch {}
}

// Why the prompt should show (or null). Returns the trigger reason for the copy.
function ratingRequestReason(ptId, ptName) {
  if (!ptId) return null;
  if (getProductRating(ptId)) return null;            // already rated — never re-ask
  try {
    const snooze = JSON.parse(localStorage.getItem(RATING_PROMPT_KEY) || "{}")[ptId]?.snoozeUntil;
    if (snooze && Date.now() < new Date(snooze)) return null;
  } catch {}
  const sessions = getPTSessionCount(ptId, ptName);
  if (sessions >= RATING_SESSION_THRESHOLD) return `You've logged ${sessions} sessions on myPTOnline`;
  const ageDays = getAccountAgeDays(ptId);
  if (ageDays >= RATING_AGE_DAYS_THRESHOLD) return `You've been with myPTOnline for ${Math.floor(ageDays / 30)} months`;
  return null;
}

// ── DARK THEME COLORS ─────────────────────────────────────────────────────────
const C = {
  bg:           "#0c0c0e",   // near-black with a very slight warmth — not pure void
  surface:      "#141417",   // clearly distinct from bg
  surfaceAlt:   "#1c1c20",   // card alt / input backgrounds
  border:       "rgba(255,255,255,0.10)", // visible but soft
  borderStrong: "rgba(255,255,255,0.22)", // clear dividers / active borders
  text:         "#f0f0f0",   // slightly off-white — easier on the eyes than pure #fff
  textSub:      "#b8b8c0",   // secondary text — readable grey with a hint of blue
  textMuted:    "#8e8e98",   // labels, timestamps — legible but clearly de-emphasised
  primary:      "#c8ff57",
  primaryDark:  "#a8d940",
  primaryGlow:  "rgba(200,255,87,0.10)",
  danger:       "#ff4d4d",
  dangerLight:  "rgba(255,77,77,0.10)",
  blue:         "#5b9fff",
  blueLight:    "rgba(91,159,255,0.10)",
  amber:        "#ffb347",
  amberLight:   "rgba(255,179,71,0.10)",
  green:        "#30d158",
  greenLight:   "rgba(48,209,88,0.10)",
};

// ── GLOBAL CSS ────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body {
    font-family: 'Space Grotesk', system-ui, sans-serif;
    background: ${C.bg};
    color: ${C.text};
    -webkit-font-smoothing: antialiased;
  }
  input, select, textarea, button { font-family: inherit; outline: none; }
  button { cursor: pointer; border: none; }
  a { color: inherit; text-decoration: none; }

  /* ── BUTTONS ── */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
    letter-spacing: 0.3px; transition: all 0.15s; border: 1px solid transparent;
  }
  .btn-primary {
    background: ${C.primary}; color: #000;
  }
  .btn-primary:hover { background: ${C.primaryDark}; transform: translateY(-1px); }
  .btn-outline {
    background: transparent; color: ${C.text};
    border-color: ${C.borderStrong};
  }
  .btn-outline:hover { border-color: ${C.primary}; color: ${C.primary}; }
  .btn-ghost {
    background: transparent; color: ${C.textSub}; border: none; padding: 8px 12px;
  }
  .btn-ghost:hover { color: ${C.text}; }
  .btn-danger { background: ${C.danger}; color: #fff; }
  .btn-danger:hover { opacity: 0.85; }
  .btn-sm { padding: 6px 14px; font-size: 12px; border-radius: 6px; }
  .btn-lg { padding: 13px 28px; font-size: 15px; border-radius: 10px; }

  /* ── INPUTS ── */
  .input {
    width: 100%; padding: 10px 14px; box-sizing: border-box;
    background: ${C.surfaceAlt}; color: ${C.text};
    border: 1px solid ${C.border}; border-radius: 8px; font-size: 14px;
    transition: border-color 0.15s;
  }
  .input:focus { border-color: ${C.primary}; }
  .input::placeholder { color: ${C.textMuted}; }
  select.input option { background: #1a1a1a; }

  /* ── LABELS ── */
  .label {
    display: block; margin-bottom: 6px;
    font-size: 11px; font-weight: 600; letter-spacing: 0.8px;
    text-transform: uppercase; color: ${C.textMuted};
  }

  /* ── CARDS ── */
  .card {
    background: ${C.surface}; border: 1px solid ${C.border};
    border-radius: 14px; padding: 20px;
  }
  .card-hover { transition: border-color 0.15s, transform 0.15s; }
  .card-hover:hover { border-color: ${C.borderStrong}; transform: translateY(-2px); }

  /* ── BADGES ── */
  .badge {
    display: inline-block; padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 600; letter-spacing: 0.3px;
  }
  .badge-green  { background: ${C.greenLight};  color: ${C.green};  }
  .badge-amber  { background: ${C.amberLight};  color: ${C.amber};  }
  .badge-red    { background: ${C.dangerLight}; color: ${C.danger}; }
  .badge-blue   { background: ${C.blueLight};   color: ${C.blue};   }
  .badge-lime   { background: ${C.primaryGlow}; color: ${C.primary};}

  /* ── DIVIDER ── */
  .divider { border: none; border-top: 1px solid ${C.border}; margin: 16px 0; }

  /* ── STAT CARDS ── */
  .stat-card {
    background: ${C.surfaceAlt}; border: 1px solid ${C.border};
    border-radius: 12px; padding: 20px 16px; text-align: center;
  }
  .stat-num { font-size: 32px; font-weight: 700; color: ${C.primary}; line-height: 1; }
  .stat-label { font-size: 11px; color: ${C.textMuted}; margin-top: 6px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; }

  /* ── NAV TABS ── */
  .tab-bar { display: flex; gap: 2px; background: ${C.surfaceAlt}; border-radius: 10px; padding: 4px; width: fit-content; }
  .tab-btn {
    padding: 7px 16px; border-radius: 7px; font-size: 13px; font-weight: 500;
    background: transparent; color: ${C.textSub}; border: none; transition: all 0.15s;
  }
  .tab-btn.active { background: ${C.surface}; color: ${C.text}; }

  /* ── SCROLLBAR ── */
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #3a3a40; border-radius: 3px; }

  /* ── MODAL BACKDROP ── */
  .modal-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.8);
    backdrop-filter: blur(4px); display: flex; align-items: center;
    justify-content: center; z-index: 200; padding: 24px;
  }
  .modal-card {
    background: ${C.surface}; border: 1px solid ${C.borderStrong};
    border-radius: 18px; width: 100%; padding: 28px;
    max-height: 88vh; overflow-y: auto;
  }

  /* ── ACCENT LINE ── */
  .accent-line { width: 40px; height: 3px; background: ${C.primary}; border-radius: 2px; margin-bottom: 16px; }

  /* ── GLOWING HIGHLIGHT ── */
  .glow-text { color: ${C.primary}; text-shadow: 0 0 30px rgba(200,255,87,0.4); }

  /* ── TABLE ── */
  .dark-table { width: 100%; border-collapse: collapse; }
  .dark-table th {
    padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600;
    letter-spacing: 0.6px; text-transform: uppercase; color: ${C.textMuted};
    border-bottom: 1px solid ${C.border};
  }
  .dark-table td { padding: 14px 16px; font-size: 14px; border-bottom: 1px solid ${C.border}; }
  .dark-table tr:last-child td { border-bottom: none; }
  .dark-table tr:hover td { background: ${C.surfaceAlt}; }

  /* ── PLAN EDIT GRID ── */
  .grid-plan-edit { display: grid; grid-template-columns: 3fr 2fr; gap: 16px; }

  /* ── LAYOUT UTILITIES ── */
  .page-content { max-width: 1200px; margin: 0 auto; padding: 40px 28px; }
  .grid-stats { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; margin-bottom: 32px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  .grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; }
  .grid-sidebar { display: grid; grid-template-columns: 280px 1fr; gap: 24px; }
  .grid-2-form { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
  .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }

  /* ── APP HEADER ──
     Kept as a class, not an inline style: React style objects can't emit the
     duplicate-property fallbacks Safari needs (-webkit-sticky, and the prefixed
     backdrop-filter), so the bar scrolled away on Safari while sticking fine on
     Chromium. flex-shrink:0 stops it collapsing as a column flex item. */
  .app-header {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
    background: rgba(5,5,5,0.95);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid ${C.border};
  }

  /* ── DESKTOP NAV ── */
  .nav-desktop { display: flex; }

  /* ── HAMBURGER ── */
  .hamburger {
    display: none;
    background: none; border: 1px solid ${C.borderStrong}; color: ${C.text};
    width: 36px; height: 36px; border-radius: 8px; font-size: 18px;
    align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0;
  }

  /* ── MOBILE NAV DRAWER ── */
  .mobile-nav {
    display: none; position: fixed; top: 64px; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.95); backdrop-filter: blur(8px);
    padding: 20px 16px; z-index: 99;
    flex-direction: column; gap: 4px; overflow-y: auto;
  }
  .mobile-nav.open { display: flex; }
  .mobile-nav-btn {
    display: flex; width: 100%; text-align: left;
    padding: 14px 16px; border-radius: 10px;
    background: none; color: ${C.textSub}; font-size: 16px; font-weight: 500;
    border: 1px solid transparent; cursor: pointer; transition: all 0.15s;
  }
  .mobile-nav-btn.active { background: ${C.primaryGlow}; color: ${C.primary}; border-color: ${C.primary}30; }
  .mobile-nav-btn:hover { background: ${C.surfaceAlt}; color: ${C.text}; }
  .mobile-nav-divider { border: none; border-top: 1px solid ${C.border}; margin: 12px 0; }

  /* ── TABLET (≤1024px) ── */
  @media (max-width: 1024px) {
    .page-content { padding: 28px 20px; }
    .grid-stats { grid-template-columns: repeat(2,1fr); }
    .grid-sidebar { grid-template-columns: 240px 1fr; }
  }

  /* ── MOBILE (≤767px) ── */
  @media (max-width: 767px) {
    /* Nav */
    .nav-desktop { display: none !important; }
    .hamburger { display: flex; }

    /* Pages */
    .page-content { padding: 20px 16px; }

    /* Grids */
    .grid-stats { grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
    .grid-2 { grid-template-columns: 1fr; gap: 14px; }
    .grid-3 { grid-template-columns: 1fr; gap: 12px; }
    .grid-sidebar { grid-template-columns: 1fr; gap: 14px; }
    .grid-2-form { grid-template-columns: 1fr; }
    .grid-plan-edit { grid-template-columns: 1fr; }

    /* Modals — slide up from bottom */
    .modal-backdrop { padding: 0; align-items: flex-end; }
    .modal-card {
      max-width: 100% !important; width: 100%;
      border-radius: 18px 18px 0 0; border-bottom: none;
      padding: 20px 16px; max-height: 90vh;
    }

    /* Cards */
    .card { padding: 14px; border-radius: 12px; }

    /* Hero */
    .hero-section { padding: 56px 16px 48px !important; }

    /* Buttons */
    .btn-lg { padding: 11px 18px; font-size: 14px; }

    /* Stat cards */
    .stat-num { font-size: 24px; }

    /* Tab bar — horizontal scroll */
    .tab-bar { overflow-x: auto; white-space: nowrap; width: 100%; }

    /* Table — always scrollable on mobile */
    .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  }

  /* ── SMALL MOBILE (≤479px) ── */
  @media (max-width: 479px) {
    .grid-stats { grid-template-columns: 1fr 1fr; }
    .btn { padding: 9px 14px; font-size: 12px; }
    .card { padding: 12px; }
  }
`;

// ── SHARED COMPONENTS ─────────────────────────────────────────────────────────
function Avatar({ name, size = 36, color = C.primary }) {
  const initials = name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `${color}18`, border: `1px solid ${color}30`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 700, color,
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function CloseBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.textSub, width: 32, height: 32, borderRadius: 8, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>×</button>
  );
}

// ── HEADER ────────────────────────────────────────────────────────────────────
function Header({ view, setView, user, setUser }) {
  const [menuOpen, setMenuOpen] = useState(false);

  // The admin panel ships its own sticky header (logo + ADMIN badge + Refresh +
  // red Sign Out), so the app header only duplicated it. Hide the app chrome for
  // admins and let the panel's own header sit at the top of the screen. Its
  // Sign Out clears the admin token and resets the app's user via onSignedOut.
  if (user?.role === "admin") return null;

  const unreadCount = user
    ? getThreadsForUser(user.name).filter(t => {
        const msgs = t.thread || [];
        return msgs.length > 0 && msgs[msgs.length - 1].poster !== user.name;
      }).length
    : 0;

  const navigate = (v) => { setView(v); setMenuOpen(false); };

  const NavBtn = ({ v, label }) => {
    const isMessages = v === "pt-messages" || v === "client-messages";
    const isActive = view === v;
    return (
      <button key={v} onClick={() => navigate(v)} style={{ position: "relative", background: isActive ? C.primaryGlow : "transparent", color: isActive ? C.primary : C.textSub, border: "none", padding: "6px 14px", borderRadius: 7, fontSize: 13, fontWeight: 500, transition: "all 0.15s", whiteSpace: "nowrap" }}>
        {label}
        {isMessages && unreadCount > 0 && (
          <span style={{ position: "absolute", top: 2, right: 2, minWidth: 16, height: 16, borderRadius: 8, background: C.danger, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", lineHeight: 1 }}>
            {unreadCount}
          </span>
        )}
      </button>
    );
  };

  const ptLinks    = [["dashboard","Home"],["clients","Clients"],["fitness-plans","Plans"],["exercises","Exercises"],["pt-messages","Messages"],["contact-me","Enquiries"],["testimonials","Testimonials"],["my-profile","My Profile"]];
  const clientLinks = [["client-dash","Dashboard"],["client-sessions","Sessions"],["client-messages","Messages"]];
  const mobileLinks = !user || user.role === "admin" ? [] : user.role === "pt" ? ptLinks : clientLinks;

  // Log out of BOTH session kinds. supabase signOut() only ends a Supabase
  // session; the admin "session" is the signed token in localStorage, so it must
  // be cleared explicitly — otherwise the restore effect on mount reads the
  // surviving token and logs the admin straight back in on refresh.
  const handleLogout = () => {
    localStorage.removeItem("mypt_admin_token");
    localStorage.removeItem("mypt_admin_authed");
    signOut();
    setUser(null);
    navigate("home");
  };

  return (
    <>
      <header className="app-header">
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px", display: "flex", alignItems: "center", gap: 12, height: 64 }}>
          {/* Logo */}
          <button onClick={() => navigate(user?.role === "admin" ? "admin" : "home")} style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.5px", color: C.text }}>
              my<span style={{ color: C.primary }}>PT</span>
              <span style={{ color: C.textSub, fontWeight: 400 }}>Online</span>
            </span>
          </button>

          <div style={{ flex: 1 }} />

          {/* Desktop nav */}
          {!user ? (
            <div className="nav-desktop" style={{ gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => navigate("find-pt")} style={{ letterSpacing: "-0.5px" }}><span>Find <span style={{ color: C.text, fontWeight: 700 }}>my</span><span style={{ color: C.primary, fontWeight: 700, textTransform: "uppercase" }}>PT</span></span></button>
              <button className="btn btn-outline btn-sm" onClick={() => navigate("login")}>Login</button>
              <button className="btn btn-primary btn-sm" onClick={() => navigate("signup")}>Sign Up</button>
            </div>
          ) : user.role === "admin" ? (
            null /* unreachable — Header returns null for admins (panel has its own header) */
          ) : user.role === "pt" ? (
            <div className="nav-desktop" style={{ alignItems: "center", gap: 4 }}>
              {user.status !== "suspended" && (
                <>
                  <nav style={{ display: "flex", gap: 2 }}>
                    {ptLinks.map(([v,label]) => <NavBtn key={v} v={v} label={label} />)}
                  </nav>
                  <Avatar name={user.name} size={34} />
                </>
              )}
              <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Log out</button>
            </div>
          ) : (
            <div className="nav-desktop" style={{ alignItems: "center", gap: 4 }}>
              <nav style={{ display: "flex", gap: 2 }}>
                {clientLinks.map(([v,label]) => <NavBtn key={v} v={v} label={label} />)}
              </nav>
              <Avatar name={user.name} size={34} color={C.blue} />
              <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Log out</button>
            </div>
          )}

          {/* Hamburger — mobile only */}
          <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            {menuOpen ? "✕" : "☰"}
          </button>
        </div>
      </header>

      {/* Mobile nav drawer */}
      <div className={`mobile-nav${menuOpen ? " open" : ""}`}>
        {!user ? (
          <>
            <button className="mobile-nav-btn" onClick={() => navigate("find-pt")} style={{ letterSpacing: "-0.5px" }}><span>Find <span style={{ color: C.text, fontWeight: 700 }}>my</span><span style={{ color: C.primary, fontWeight: 700, textTransform: "uppercase" }}>PT</span></span></button>
            <button className="mobile-nav-btn" onClick={() => navigate("login")}>Login</button>
            <button className="mobile-nav-btn" onClick={() => navigate("signup")}>Sign Up</button>
          </>
        ) : (
          <>
            {user.status !== "suspended" && mobileLinks.map(([v, label]) => (
              <button key={v} className={`mobile-nav-btn${view === v ? " active" : ""}`} onClick={() => navigate(v)}>
                {label}
                {(v === "pt-messages" || v === "client-messages") && unreadCount > 0 && (
                  <span style={{ marginLeft: 8, background: C.danger, color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 11, fontWeight: 700 }}>{unreadCount}</span>
                )}
              </button>
            ))}
            <hr className="mobile-nav-divider" />
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px" }}>
              <Avatar name={user.name} size={36} color={user.role === "pt" ? C.primary : C.blue} />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: C.text }}>{user.name}</span>
              <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Log out</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── HOME PAGE ─────────────────────────────────────────────────────────────────
function fmtStat(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "m+";
  if (n >= 1000)    return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k+";
  return n > 0 ? n + "+" : "0";
}

// ── HERO STRAPLINE — rotates every 4 s (NNG/Baymard recommended interval) ────
const HERO_LINES = [
  { line1: "Train smarter.",   line2: "Grow faster."  },
  { line1: "Work out.",          line2: "Get fit."       },
];

function HeroStrapline() {
  const [idx, setIdx]       = useState(0);
  const [visible, setVisible] = useState(true);   // drives opacity

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);                          // fade out (400 ms)
      setTimeout(() => {
        setIdx(i => (i + 1) % HERO_LINES.length);
        setVisible(true);                         // fade in
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const { line1, line2 } = HERO_LINES[idx];
  return (
    <h1
      style={{
        fontSize: "clamp(40px,6vw,72px)", fontWeight: 700, lineHeight: 1.05,
        letterSpacing: "-1.5px", marginBottom: 24,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.4s ease",
      }}
    >
      {line2 ? <>{line1}<br /><span className="glow-text">{line2}</span></> : <span className="glow-text">{line1}</span>}
    </h1>
  );
}

function HomePage({ setView }) {
  const ptCount      = getAll("pts").length;
  const clientCount  = getAll("clients").length;
  const sessionCount = getAll("sessions").length;
  // Average rating: derive from sessions that have a rating field, else show "—"
  const rated   = getAll("sessions").filter(s => typeof s.rating === "number");
  const avgRating = rated.length
    ? (rated.reduce((a, s) => a + s.rating, 0) / rated.length).toFixed(1) + "★"
    : "4.9★";

  const stats = [
    [fmtStat(ptCount),      "Certified PTs"],
    [fmtStat(clientCount),  "Active Clients"],
    [fmtStat(sessionCount), "Sessions Logged"],
    [avgRating,             "Average Rating"],
  ];

  return (
    <div>
      {/* Hero */}
      <div className="hero-section" style={{ position: "relative", padding: "80px 20px 70px", textAlign: "center", overflow: "hidden" }}>
        {/* Glow orb */}
        <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 500, height: 500, background: "radial-gradient(circle, rgba(200,255,87,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 740, margin: "0 auto", position: "relative" }}>
          <div style={{ display: "inline-block", background: C.primaryGlow, border: `1px solid ${C.primary}30`, color: C.primary, padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 28 }}>
            The UK's Personal Training Platform
          </div>
          <HeroStrapline />
          <p style={{ fontSize: 18, color: C.textSub, marginBottom: 44, lineHeight: 1.7, maxWidth: 520, margin: "0 auto 44px" }}>
            myPTOnline connects personal trainers with clients — manage sessions, track progress, and build your business.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn-primary btn-lg" onClick={() => setView("find-pt")}>Find a Personal Trainer</button>
            <button className="btn btn-outline btn-lg" onClick={() => setView("signup")}>Register as a PT</button>
          </div>
        </div>
      </div>

      {/* Stats bar — these counts/rating are still seed/demo data, so show them on
          staging (demo completeness) but hide on production until they're real. */}
      {!IS_PRODUCTION && (
        <div style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface }}>
          <div className="grid-stats" style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 28px", gap: 0 }}>
            {stats.map(([n,l]) => (
              <div key={l} style={{ textAlign: "center", padding: "8px 16px", borderRight: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.primary }}>{n}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, fontWeight: 500, letterSpacing: "0.5px", textTransform: "uppercase" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      <div className="page-content" style={{ paddingTop: 60, paddingBottom: 60 }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div className="accent-line" style={{ margin: "0 auto 16px" }} />
          <h2 style={{ fontSize: 36, fontWeight: 700, letterSpacing: "-0.8px" }}>Everything you need to run your PT business</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }}>
          {[
            { icon: "◎", title: "Online Presence", desc: "Create a professional PT profile in minutes. Be discoverable to clients across the UK.", accent: C.primary },
            { icon: "◈", title: "Contact Me Service", desc: "Receive client enquiries and follow up by phone, email or SMS. Never miss a lead.", accent: C.blue },
            { icon: "▣", title: "Session Management", desc: "Plan sessions, record performance, and track your clients' progress with detailed logs.", accent: C.amber },
            { icon: "◇", title: "Progress Charting", desc: "Visualise client results with appraisal and session charts. Keep clients motivated.", accent: C.green },
            { icon: "⬡", title: "Fitness Plan Library", desc: "Build and share personalised fitness plans across muscle groups and training types.", accent: C.primary },
            { icon: "◉", title: "Client Portal", desc: "Clients get secure access to view plans, leave session feedback, and track progress.", accent: C.blue },
          ].map(f => (
            <div key={f.title} className="card card-hover" style={{ cursor: "default" }}>
              <div style={{ fontSize: 24, color: f.accent, marginBottom: 14 }}>{f.icon}</div>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>{f.title}</div>
              <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.7 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: "64px 28px", textAlign: "center" }}>
        <h2 style={{ fontSize: 36, fontWeight: 700, letterSpacing: "-0.8px", marginBottom: 12 }}>Ready to level up?</h2>
        <p style={{ color: C.textSub, marginBottom: 32, fontSize: 15 }}>Join hundreds of personal trainers already using myPTOnline</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button className="btn btn-primary btn-lg" onClick={() => setView("signup")}>Start Free Profile</button>
          <button className="btn btn-outline btn-lg" onClick={() => setView("login")}>Sign In</button>
        </div>
      </div>
    </div>
  );
}

// ── LOGIN ─────────────────────────────────────────────────────────────────────
function LoginPage({ setView, setUser }) {
  const [email, setEmail]         = useState("");
  const [showPassword, setShowPassword] = useState(true);
  const [password, setPassword]   = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [linkSent, setLinkSent]   = useState(false);
  const [cooldown, setCooldown]   = useState(0);
  // 2FA state — only shown after admin credentials pass on production
  const [show2FA, setShow2FA]     = useState(false);
  const [totpCode, setTotpCode]   = useState("");
  const [totpError, setTotpError] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const isValidEmail = /\S+@\S+\.\S+/.test(email);

  // ── Request a sign-in link ────────────────────────────────────────────────
  // The server decides what to send (sign-in link if the account exists, or a
  // "sign up" invite if not) and always responds the same way — so the UI never
  // reveals whether an email is registered. We show the same confirmation screen
  // regardless.
  const requestLink = async () => {
    try {
      await fetch("/api/request-signin-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch (_) {}
  };

  const sendLink = async () => {
    if (!isValidEmail) { setError("Please enter a valid email address."); return; }
    setError(""); setLoading(true);
    await requestLink();
    setLoading(false);
    setLinkSent(true);
    setCooldown(60);
  };

  const resendLink = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    await requestLink();
    setLoading(false);
    setCooldown(60);
  };

  // ── Password sign-in (demo + Supabase) ──────────────────────────────────
  const handlePasswordLogin = async () => {
    setError("");
    // Demo shortcuts
    if (email === "percy" && password === "password") {
      setUser({ role: "pt", name: "Percy Trena", id: "P.Trena", contactMeCount: 3, msgCount: 2 });
      setView("dashboard"); return;
    }
    if (email === "clint" && password === "password") {
      setUser({ role: "client", name: "Clint Fitz", id: 1, ptName: "Percy Trena" });
      setView("client-dash"); return;
    }
    if (email === "cloe" && password === "password") {
      setUser({ role: "client", name: "Cloe Fitz", id: 4, ptName: "Percy Trena" });
      setView("client-dash"); return;
    }
    // Demo admin — dev/staging ONLY. Disabled on production: the passphrase and
    // the client-side TOTP secret are both inlined into the public JS bundle, so
    // this was never real authentication. Proper admin auth must be server-side.
    if (email === "admin" && password === "mypt-admin-2026" && !IS_PRODUCTION) {
      setUser({ role: "admin", name: "Admin", id: "admin" });
      setView("dashboard");
      return;
    }
    if (!isValidEmail) { setError("Please enter a valid email address."); return; }
    setLoading(true);
    const { error: sbErr } = await signIn(email.trim(), password);
    setLoading(false);
    if (sbErr) { setError("Incorrect email or password."); return; }
    // session handled by onAuthStateChange in App
  };

  // ── 2FA verify handler ───────────────────────────────────────────────────
  const handle2FA = async () => {
    setTotpError("");
    if (!totpCode.trim()) { setTotpError("Enter the 6-digit code from your authenticator."); return; }
    const ok = await verifyTOTP(TOTP_SECRET, totpCode);
    if (ok) {
      setUser({ role: "admin", name: "Admin", id: "admin" });
      setView("dashboard");
    } else {
      setTotpError("Invalid code — try again. Codes refresh every 30 seconds.");
      setTotpCode("");
    }
  };

  // ── 2FA screen ───────────────────────────────────────────────────────────
  if (show2FA) return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:380 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(200,255,87,0.1)", border:`2px solid ${C.primary}50`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 20px" }}>🔐</div>
          <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:"-0.5px", marginBottom:8 }}>Two-factor authentication</h1>
          <p style={{ fontSize:13, color:C.textSub, lineHeight:1.7 }}>
            Open your authenticator app and enter<br />the 6-digit code for <strong style={{ color:C.text }}>myPTOnline Admin</strong>.
          </p>
        </div>

        {/* TOTP code input */}
        <div style={{ marginBottom:16 }}>
          <input
            className="input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={totpCode}
            onChange={e => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={e => e.key === "Enter" && handle2FA()}
            autoFocus
            style={{ textAlign:"center", fontSize:28, letterSpacing:"0.3em", fontWeight:700, padding:"14px 20px" }}
          />
        </div>
        {totpError && <div style={{ padding:"10px 14px", background:"rgba(255,77,77,0.1)", border:"1px solid rgba(255,77,77,0.3)", borderRadius:8, fontSize:13, color:C.danger, marginBottom:12, textAlign:"center" }}>{totpError}</div>}

        <button className="btn btn-primary" style={{ width:"100%", marginBottom:12 }} onClick={handle2FA}>
          Verify →
        </button>
        <button className="btn btn-ghost btn-sm" style={{ width:"100%", fontSize:12, color:C.textMuted }} onClick={() => { setShow2FA(false); setTotpCode(""); setTotpError(""); }}>
          ← Back to login
        </button>

        <div style={{ marginTop:28, padding:"14px 16px", background:C.surfaceAlt, borderRadius:10, border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:11, color:C.textMuted, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8 }}>First time setup?</div>
          <div style={{ fontSize:12, color:C.textSub, lineHeight:1.7, marginBottom:10 }}>
            Scan this QR code in Google Authenticator, Authy, or any TOTP app:
          </div>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:10 }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(`otpauth://totp/myPTOnline:admin?secret=${TOTP_SECRET}&issuer=myPTOnline`)}`}
              alt="2FA QR Code"
              style={{ borderRadius:8, border:`1px solid ${C.border}` }}
            />
          </div>
          <div style={{ fontSize:11, color:C.textMuted, textAlign:"center" }}>
            Manual entry: <code style={{ color:C.primary, letterSpacing:"0.08em" }}>{TOTP_SECRET}</code>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Sent confirmation screen ─────────────────────────────────────────────
  if (linkSent) return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:400 }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ position:"relative", width:72, height:72, margin:"0 auto 24px" }}>
            <div style={{ width:72, height:72, borderRadius:"50%", background:C.primaryGlow, border:`2px solid ${C.primary}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:32 }}>✉️</div>
            <div style={{ position:"absolute", top:-4, right:-4, width:22, height:22, borderRadius:"50%", background:C.primary, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#0c0c0e" }}>✓</div>
          </div>
          <h1 style={{fontSize:24,fontWeight:700,letterSpacing:"-0.5px",marginBottom:10}}>Check your inbox</h1>
          <p style={{fontSize:14,color:C.textSub,lineHeight:1.7}}>
            We sent a link to<br /><strong style={{color:C.text}}>{email}</strong>
            <br /><span style={{fontSize:13,color:C.textMuted}}>Open it to sign in — or to get started if you're new.</span>
          </p>
        </div>
        <div className="card">
          {[["📩","Open the email from myPTOnline"],["🔗","Click the secure link inside"],["✅","You're signed in — or guided to sign up"]].map(([icon,text])=>(
            <div key={text} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:18, width:28, textAlign:"center" }}>{icon}</span>
              <span style={{ fontSize:13, color:C.textSub }}>{text}</span>
            </div>
          ))}
          <div style={{ background:C.amberLight, border:`1px solid ${C.amber}30`, borderRadius:8, padding:"10px 14px", fontSize:12, color:C.amber, margin:"16px 0", lineHeight:1.6 }}>
            Can't find it? Check your <strong>spam or junk folder</strong>. The link expires in 1 hour.
          </div>
          <div style={{ textAlign:"center", marginBottom:14 }}>
            <span style={{ fontSize:13, color:C.textMuted }}>Didn't receive it? </span>
            <button className="btn btn-ghost" onClick={resendLink} disabled={cooldown>0||loading}
              style={{ fontSize:13, color:cooldown>0?C.textMuted:C.primary, fontWeight:600, cursor:cooldown>0?"default":"pointer" }}>
              {cooldown>0?`Resend in ${cooldown}s`:"Resend link"}
            </button>
          </div>
          <button className="btn btn-outline" style={{width:"100%"}} onClick={()=>{setLinkSent(false);setCooldown(0);}}>
            ← Use a different email
          </button>
        </div>
      </div>
    </div>
  );

  // ── Main login screen ────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:420 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div className="accent-line" style={{ margin:"0 auto 20px" }} />
          <h1 style={{ fontSize:32, fontWeight:700, letterSpacing:"-0.8px" }}>Welcome back</h1>
          <p style={{ color:C.textSub, marginTop:8, fontSize:14 }}>
            {showPassword ? "Sign in to your myPTOnline account" : "Enter your email and we'll send a secure sign-in link"}
          </p>
        </div>

        <div className="card">
          {/* Email field */}
          <div style={{ marginBottom:14 }}>
            <label className="label">Email address</label>
            <input
              className="input"
              type="email"
              placeholder="you@email.com"
              value={email}
              autoFocus
              autoComplete="username"
              onChange={e => { setEmail(e.target.value); setError(""); }}
              onKeyDown={e => e.key==="Enter" && (showPassword ? handlePasswordLogin() : sendLink())}
            />
          </div>

          {/* Password field — revealed on toggle */}
          {showPassword && (
            <div style={{ marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                <label className="label" style={{margin:0}}>Password</label>
                <button className="btn btn-ghost" onClick={()=>setView("forgot")} style={{fontSize:12,color:C.textMuted,padding:0}}>Forgot password?</button>
              </div>
              <PasswordInput
                value={password}
                placeholder="Enter your password"
                autoFocus
                autoComplete="current-password"
                onChange={e => { setPassword(e.target.value); setError(""); }}
                onKeyDown={e => e.key==="Enter" && handlePasswordLogin()}
              />
            </div>
          )}

          {error && (
            <div style={{ background:C.dangerLight, color:C.danger, border:`1px solid ${C.danger}30`, borderRadius:8, padding:"10px 14px", fontSize:13, marginBottom:14 }}>
              {error}
            </div>
          )}

          {showPassword ? (
            <>
              {/* Primary CTA — password sign in */}
              <button className="btn btn-primary" style={{width:"100%",padding:12,opacity:loading?0.7:1}} onClick={handlePasswordLogin} disabled={loading}>
                {loading ? "Signing in…" : "Sign in →"}
              </button>

              {/* Divider */}
              <div style={{ display:"flex", alignItems:"center", gap:10, margin:"16px 0" }}>
                <div style={{ flex:1, height:1, background:C.border }} />
                <span style={{ fontSize:12, color:C.textMuted }}>or</span>
                <div style={{ flex:1, height:1, background:C.border }} />
              </div>

              {/* Switch to passwordless link mode */}
              <button className="btn btn-outline" style={{width:"100%"}} onClick={()=>{ setError(""); setShowPassword(false); }} disabled={loading}>
                ✉️  Email me a login link instead
              </button>
            </>
          ) : (
            <>
              {/* Primary CTA — send login link */}
              <button className="btn btn-primary" style={{width:"100%",padding:12,opacity:loading?0.7:1}} onClick={sendLink} disabled={loading}>
                {loading ? "Sending…" : "✉️  Send me a login link"}
              </button>
              <p style={{ textAlign:"center", fontSize:12, color:C.textMuted, margin:"12px 4px 0", lineHeight:1.6 }}>
                No password needed — click the link in the email to sign in.
              </p>

              {/* Divider */}
              <div style={{ display:"flex", alignItems:"center", gap:10, margin:"16px 0" }}>
                <div style={{ flex:1, height:1, background:C.border }} />
                <span style={{ fontSize:12, color:C.textMuted }}>or</span>
                <div style={{ flex:1, height:1, background:C.border }} />
              </div>

              {/* Back to password mode */}
              <button className="btn btn-outline" style={{width:"100%"}} onClick={()=>{ setError(""); setShowPassword(true); }} disabled={loading}>
                🔑  Sign in with a password instead
              </button>
            </>
          )}
        </div>

        <div style={{ textAlign:"center", marginTop:20, fontSize:13, color:C.textMuted }}>
          New here?{" "}
          <button className="btn btn-ghost btn-sm" onClick={()=>setView("signup")} style={{color:C.primary,fontWeight:600}}>Sign up free</button>
        </div>

        {/* Demo accounts */}
        <div style={{ marginTop:12, background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, padding:"12px 14px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.6px", color:C.primary, background:C.primaryGlow, border:`1px solid ${C.primary}30`, borderRadius:4, padding:"2px 6px" }}>DEMO</span>
            <span style={{ fontSize:12, color:C.textSub }}>Explore instantly — no signup. Tap to fill:</span>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {[
              { label:"Trainer", who:"percy" },
              { label:"Client · Clint", who:"clint" },
              { label:"Client · Cloe", who:"cloe" },
            ].map(d => (
              <button key={d.who} type="button" className="btn btn-sm"
                onClick={() => { setEmail(d.who); setPassword("password"); setError(""); setShowPassword(true); }}
                style={{ background:C.surface, border:`1px solid ${C.border}`, color:C.textSub, fontWeight:600, fontSize:12 }}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── BRANDED CLIENT LOGIN (per-PT URL) ─────────────────────────────────────────
function ClientPTLogin({ pt, setView, setUser }) {
  const [form, setForm] = useState({ id: "", password: "" });
  const [error, setError] = useState("");

  const handleLogin = () => {
    const demoNames = { clint: "Clint Fitz", cloe: "Cloe Fitz" };
    const fallbackName = demoNames[form.id.toLowerCase()];
    if (fallbackName && form.password === "password") {
      // Find the matching client for this PT (or by name) and activate them
      const allClients = getAll("clients");
      const client =
        allClients.find(c => c.ptId === pt.id && c.name.toLowerCase().startsWith(form.id.toLowerCase())) ||
        allClients.find(c => c.name === fallbackName);
      if (client && client.status !== "active") {
        update("clients", client.id, { status: "active" });
      }
      // Remember this client's PT so the app root always opens their portal
      localStorage.setItem("mypt_client_pt_slug", slugify(pt.name));
      setUser({ role: "client", name: client?.name || fallbackName, id: client?.id ?? form.id, ptName: pt.name });
      window.location.hash = "";
      setView("client-dash");
    } else {
      setError("Invalid credentials. Demo: clint / password or cloe / password");
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Branded mini header */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: "16px 28px", display: "flex", alignItems: "center", gap: 12 }}>
        <Avatar name={pt.name} size={36} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{pt.name}</div>
          <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.4px" }}>{pt.specialism} · {pt.location}</div>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: C.textMuted }}>Powered by <span style={{ fontWeight: 700 }}>my<span style={{ color: C.primary }}>PT</span>Online</span></span>
      </div>

      {/* Login form */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 440 }}>
          {/* Glow orb */}
          <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 400, height: 400, background: "radial-gradient(circle, rgba(200,255,87,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

          <div style={{ textAlign: "center", marginBottom: 36, position: "relative" }}>
            <Avatar name={pt.name} size={72} />
            <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.5px", marginTop: 20 }}>Welcome to {pt.name.split(" ")[0]}'s training portal</h1>
            <p style={{ color: C.textSub, marginTop: 8, fontSize: 14, lineHeight: 1.6 }}>
              Sign in to view your sessions, plans, and progress
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
              <span className="badge badge-lime">{pt.specialism}</span>
              <span className="badge badge-blue">{pt.membership} Level {pt.level}</span>
              <span style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, padding: "3px 10px", borderRadius: 20, fontSize: 11, color: C.textSub }}>{pt.location}</span>
            </div>
          </div>

          <div className="card" style={{ position: "relative" }}>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 20 }}>Client Sign In</div>
            <div style={{ marginBottom: 16 }}>
              <label className="label">Client ID</label>
              <input className="input" placeholder="e.g. clint" value={form.id} onChange={e => setForm(f => ({ ...f, id: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleLogin()} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label className="label">Password</label>
              <PasswordInput value={form.password} placeholder="Enter password" onChange={e => setForm(f => ({ ...f, password: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleLogin()} />
            </div>
            {error && <div style={{ background: C.dangerLight, color: C.danger, padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, border: `1px solid ${C.danger}30` }}>{error}</div>}
            <button className="btn btn-primary" style={{ width: "100%", padding: 12 }} onClick={handleLogin}>Sign In</button>
          </div>

          <div style={{ marginTop: 16, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.6px", color:C.primary, background:C.primaryGlow, border:`1px solid ${C.primary}30`, borderRadius:4, padding:"2px 6px" }}>DEMO</span>
              <span style={{ fontSize:12, color:C.textSub }}>Try a client account — password <strong style={{color:C.textSub}}>password</strong></span>
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {[["clint","Clint (male)"],["cloe","Cloe (female)"]].map(([id,label]) => (
                <button key={id} type="button" className="btn btn-sm"
                  onClick={() => { setForm({ id, password: "password" }); setError(""); }}
                  style={{ background:C.surface, border:`1px solid ${C.border}`, color:C.textSub, fontWeight:600, fontSize:12 }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { window.location.hash = ""; setView("home"); }} style={{ color: C.textSub, fontSize: 13 }}>← Back to myPTOnline</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PHONE FORMAT HELPERS ──────────────────────────────────────────────────────
const PHONE_FORMATS = {
  "+44":  { pattern: [4,6],   sep:" ", placeholder:"7700 900000",   maxLen:11 },
  "+353": { pattern: [2,7],   sep:" ", placeholder:"87 1234567",    maxLen:10 },
  "+1":   { pattern: [3,3,4], sep:"-", placeholder:"201-555-0123",  maxLen:12 },
  "+61":  { pattern: [1,4,4], sep:" ", placeholder:"4 1234 5678",   maxLen:10 },
  "+33":  { pattern: [1,2,2,2,2], sep:" ", placeholder:"6 12 34 56 78", maxLen:11 },
  "+49":  { pattern: [3,7],   sep:" ", placeholder:"151 1234567",   maxLen:11 },
  "+34":  { pattern: [3,3,3], sep:" ", placeholder:"612 345 678",   maxLen:11 },
  "+27":  { pattern: [2,3,4], sep:" ", placeholder:"71 234 5678",   maxLen:10 },
  "+971": { pattern: [1,3,4], sep:" ", placeholder:"5 123 4567",    maxLen:9  },
  "+91":  { pattern: [5,5],   sep:" ", placeholder:"98765 43210",   maxLen:11 },
};

const COUNTRY_CODES = [
  {code:"+44",flag:"🇬🇧",label:"UK"},{code:"+353",flag:"🇮🇪",label:"IE"},
  {code:"+1",flag:"🇺🇸",label:"US"},{code:"+61",flag:"🇦🇺",label:"AU"},
  {code:"+33",flag:"🇫🇷",label:"FR"},{code:"+49",flag:"🇩🇪",label:"DE"},
  {code:"+34",flag:"🇪🇸",label:"ES"},{code:"+27",flag:"🇿🇦",label:"ZA"},
  {code:"+971",flag:"🇦🇪",label:"UAE"},{code:"+91",flag:"🇮🇳",label:"IN"},
];

// Messaging apps: type "phone" uses PhoneInput, type "handle" uses text handle
const MSG_APPS = [
  { id:"whatsapp",  label:"WhatsApp",  icon:"💬", type:"phone"  },
  { id:"signal",    label:"Signal",    icon:"🔒", type:"phone"  },
  { id:"imessage",  label:"iMessage",  icon:"🍏", type:"phone"  },
  { id:"viber",     label:"Viber",     icon:"📲", type:"phone"  },
  { id:"telegram",  label:"Telegram",  icon:"✈️", type:"handle" },
  { id:"skype",     label:"Skype",     icon:"🎥", type:"handle" },
  { id:"instagram", label:"Instagram", icon:"📸", type:"handle" },
  { id:"messenger", label:"Messenger", icon:"💙", type:"handle" },
];

function formatPhone(raw, code) {
  const digits = raw.replace(/\D/g, "");
  const fmt = PHONE_FORMATS[code] || { pattern:[4,6], sep:" ", maxLen:10 };
  const capped = digits.slice(0, fmt.maxLen - (fmt.pattern.length - 1));
  let result = "", pos = 0;
  for (let i = 0; i < fmt.pattern.length; i++) {
    const chunk = capped.slice(pos, pos + fmt.pattern[i]);
    if (!chunk) break;
    if (i > 0 && result) result += fmt.sep;
    result += chunk;
    pos += fmt.pattern[i];
  }
  return result;
}

// ── PASSWORD INPUT WITH SHOW/HIDE TOGGLE ─────────────────────────────────────
function PasswordInput({ value, onChange, onKeyDown, placeholder, autoFocus, style, className, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position:"relative" }}>
      <input
        className={className || "input"}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "Password"}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        style={{ paddingRight:40, ...(style||{}) }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
        style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:C.textMuted, fontSize:16, padding:2, lineHeight:1 }}
      >
        {show ? "🙈" : "👁"}
      </button>
    </div>
  );
}

function PhoneInput({ label, codeKey, numKey, form, upd, COUNTRY_CODES, optional, icon }) {
  const fmt = PHONE_FORMATS[form[codeKey]] || {};
  return (
    <div>
      <label className="label">{label}{optional && <span style={{color:"#8e8e98",fontWeight:400,textTransform:"none",letterSpacing:0}}> (optional)</span>}</label>
      <div style={{ display:"flex", border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden", background:C.surfaceAlt }}>
        <select aria-label={`${label} country code`} value={form[codeKey]} onChange={e => upd(codeKey, e.target.value)}
          style={{ background:"transparent", border:"none", borderRight:`1px solid ${C.border}`, color:C.text, padding:"0 8px", fontSize:13, cursor:"pointer", outline:"none", flexShrink:0 }}>
          {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
        </select>
        {icon && <span style={{ display:"flex", alignItems:"center", padding:"0 10px", fontSize:16, borderRight:`1px solid ${C.border}` }}>{icon}</span>}
        <input className="input" type="tel" value={form[numKey]}
          onChange={e => upd(numKey, formatPhone(e.target.value, form[codeKey]))}
          placeholder={fmt.placeholder || "Phone number"}
          style={{ border:"none", borderRadius:0, background:"transparent", flex:1, letterSpacing:"0.05em" }} />
      </div>
    </div>
  );
}

// ── SIGN UP ───────────────────────────────────────────────────────────────────
// completeMode: reuse this form to let an already-authenticated account with no
// profile finish their profile (email/password hidden, submits to
// /api/complete-profile instead of creating a new account).
function SignUpPage({ setView, completeMode = false, sessionEmail = "", onProfileCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ firstName:"", lastName:"", email: completeMode ? sessionEmail : "", password:"", confirmPassword:"", landlineCode:"+44", landline:"", mobileCode:"+44", mobile:"", whatsappCode:"+44", whatsapp:"", membership:"REPS", membershipNo:"", level:"3", contactMe:true, gender:"male", experience:"", specialism:"", location:"", rate:"", instagram:"", youtube:"", tiktok:"", facebook:"", linkedin:"", twitter:"" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showContactPreview, setShowContactPreview] = useState(false);
  const upd = (k,v) => setForm(f => ({...f,[k]:v}));

  const profilePayload = () => ({
    gender:     form.gender === "male" ? "M" : "F",
    membership: form.membership,
    level:      parseInt(form.level) || 3,
    experience: form.experience.trim(),
    specialism: form.specialism,
    location:   form.location.trim(),
    rate:       parseFloat(form.rate) || 0,
    instagram:  form.instagram.trim(),
    youtube:    form.youtube.trim(),
    tiktok:     form.tiktok.trim(),
    facebook:   form.facebook.trim(),
    linkedin:   form.linkedin.trim(),
    twitter:    form.twitter.trim(),
  });

  const handleCreate = async () => {
    setError("");
    if (!form.firstName.trim() || !form.lastName.trim()) { setError("First and last name are required."); return; }
    if (form.mobile && !isMobile((form.mobileCode || "") + form.mobile)) { setError("Enter a valid mobile number."); return; }
    if (form.landline && !isLandline((form.landlineCode || "") + form.landline)) { setError("Enter a valid landline number."); return; }

    // ── Complete-profile mode: already authenticated, just create the pts row ──
    if (completeMode) {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) { setError("Your session has expired. Please sign in again."); setLoading(false); return; }
        const res = await fetch("/api/complete-profile", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: `${form.firstName.trim()} ${form.lastName.trim()}`, profile: profilePayload() }),
        });
        const data = await res.json();
        if (!res.ok) { setError(data.error || "Something went wrong. Please try again."); setLoading(false); return; }
        onProfileCreated?.(data, `${form.firstName.trim()} ${form.lastName.trim()}`);
      } catch (e) { setError("Could not connect to the server. Please try again."); setLoading(false); }
      return;
    }

    if (!form.email.trim()) { setError("Email address is required."); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (form.password !== form.confirmPassword) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/send-confirmation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
          name: `${form.firstName.trim()} ${form.lastName.trim()}`,
          role: "pt",
          profile: profilePayload(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }
      setStep(3);
    } catch (e) { setError("Could not connect to the server. Please try again."); }
    setLoading(false);
  };

  if (step === 3) return (
    <div style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 480 }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(200,255,87,0.08)", border: "2px solid rgba(200,255,87,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 24px" }}>📧</div>
        <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Check your email</h2>
        <p style={{ color: C.textSub, marginBottom: 8, lineHeight: 1.7 }}>
          We've sent a confirmation link to <strong style={{color:C.text}}>{form.email}</strong>.
        </p>
        <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 32, lineHeight: 1.6 }}>
          Click the link in the email to activate your account. Check your spam folder if you don't see it within a few minutes.
        </p>
        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px", marginBottom: 28, fontSize: 13, color: C.textSub, textAlign: "left" }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: C.text }}>What happens next?</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span>✓ &nbsp;Click the link in your email</span>
            <span>✓ &nbsp;You'll be signed in automatically</span>
            <span>✓ &nbsp;Complete your PT profile to go live</span>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setView("login")} style={{ fontSize: 13, color: C.textMuted }}>Back to login</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "85vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 540 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div className="accent-line" style={{ margin: "0 auto 20px" }} />
          <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px" }}>{completeMode ? "Complete your profile" : "Create your PT profile"}</h1>
          <p style={{ color: C.textSub, marginTop: 8, fontSize: 14 }}>Step {step} of 2 — {step===1?"Personal details":"Profile settings"}</p>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:14, background:"rgba(200,255,87,0.07)", border:"1px solid rgba(200,255,87,0.2)", borderRadius:20, padding:"5px 14px" }}>
            <span style={{ fontSize:13 }}>🌐</span>
            <span style={{ fontSize:12, color:C.primary, fontWeight:500 }}>
              {step===1 ? "Add your details so clients can find and contact you directly" : "Great profile details = more client enquiries"}
            </span>
          </div>
        </div>
        {step === 1 && completeMode && (
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20, padding:"12px 16px", background:C.amberLight, border:`1px solid ${C.amber}30`, borderRadius:10 }}>
            <span style={{ fontSize:18 }}>👋</span>
            <span style={{ fontSize:13, color:C.amber, lineHeight:1.6 }}>You're signed in{sessionEmail ? ` as ${sessionEmail}` : ""} — just add your details below to finish setting up your account.</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
          {[1,2].map(s => <div key={s} style={{ flex:1, height:3, borderRadius:2, background: s<=step ? C.primary : C.border, transition:"background 0.3s" }} />)}
        </div>
        <div className="card">
          {step === 1 ? (
            <>
              <div className="grid-2-form" style={{ gap:14, marginBottom:14 }}>
                <div><label className="label">First name</label><input className="input" value={form.firstName} onChange={e=>upd("firstName",e.target.value)} placeholder="First name" /></div>
                <div><label className="label">Last name</label><input className="input" value={form.lastName} onChange={e=>upd("lastName",e.target.value)} placeholder="Last name" /></div>
              </div>
              {!completeMode && <>
                <div style={{marginBottom:14}}><label className="label">Email</label><input className="input" type="email" value={form.email} onChange={e=>upd("email",e.target.value)} placeholder="your@email.com" /></div>
                <div className="grid-2-form" style={{ gap:14, marginBottom:14 }}>
                  <div><label className="label">Password</label><PasswordInput value={form.password} onChange={e=>upd("password",e.target.value)} placeholder="At least 8 characters" /></div>
                  <div><label className="label">Confirm password</label><PasswordInput value={form.confirmPassword} onChange={e=>upd("confirmPassword",e.target.value)} placeholder="Repeat password" /></div>
                </div>
              </>}
              <div className="grid-2-form" style={{ gap:14, marginBottom:14 }}>
                <PhoneInput label="Landline" codeKey="landlineCode" numKey="landline" form={form} upd={upd} COUNTRY_CODES={COUNTRY_CODES} />
                <PhoneInput label="Mobile"   codeKey="mobileCode"   numKey="mobile"   form={form} upd={upd} COUNTRY_CODES={COUNTRY_CODES} />
              </div>
              <div style={{ marginBottom:14 }}>
                <PhoneInput label="WhatsApp" codeKey="whatsappCode" numKey="whatsapp" form={form} upd={upd} COUNTRY_CODES={COUNTRY_CODES} optional icon="💬" />
              </div>
              <div className="grid-2-form" style={{ gap:14, marginBottom:14 }}>
                <div><label className="label">Gender</label><select aria-label="Gender" className="input" value={form.gender} onChange={e=>upd("gender",e.target.value)}><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></div>
                <div><label className="label">Professional body</label><select aria-label="Professional body" className="input" value={form.membership} onChange={e=>upd("membership",e.target.value)}><option>REPS</option><option>NRPT</option><option>YMCA</option><option>Other</option></select></div>
              </div>
              <div className="grid-2-form" style={{ gap:14, marginBottom:14 }}>
                <div><label className="label">Membership No.</label><input className="input" value={form.membershipNo} onChange={e=>upd("membershipNo",e.target.value)} placeholder="12345" /></div>
                <div><label className="label">Level</label><select aria-label="Level" className="input" value={form.level} onChange={e=>upd("level",e.target.value)}>{["1","2","3","4"].map(l=><option key={l}>Level {l}</option>)}</select></div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:24, padding:"12px 14px", background:C.primaryGlow, border:`1px solid ${C.primary}20`, borderRadius:8 }}>
                <label style={{ display:"flex", alignItems:"center", gap:10, flex:1, cursor:"pointer" }}>
                  <input type="checkbox" checked={form.contactMe} onChange={e=>upd("contactMe",e.target.checked)} />
                  <span style={{ fontSize:13, color:C.primary, fontWeight:500 }}>Enable Contact Me service (recommended)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowContactPreview(true)}
                  style={{ background:"none", border:`1px solid ${C.primary}40`, borderRadius:6, color:C.primary, fontSize:11, fontWeight:600, padding:"4px 10px", cursor:"pointer", whiteSpace:"nowrap", letterSpacing:"0.3px" }}
                >
                  👁 Preview
                </button>
              </div>

              {/* ── Contact Me preview modal ── */}
              {showContactPreview && (
                <div className="modal-backdrop">
                  <div className="modal-card" style={{ maxWidth:460 }}>

                    {/* Header */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                      <span style={{ fontWeight:700, fontSize:15 }}>
                        Contact Me — {[form.firstName, form.lastName].filter(Boolean).join(" ") || "Your Name"}
                      </span>
                      <CloseBtn onClick={() => setShowContactPreview(false)} />
                    </div>
                    <p style={{ fontSize:12, color:C.textMuted, marginBottom:18, lineHeight:1.5 }}>
                      This is exactly what a potential client sees when they click <strong style={{color:C.text}}>"Contact Me"</strong> on your profile in Find a PT.
                    </p>

                    {/* Amber tip */}
                    <div style={{ padding:"10px 14px", background:C.amberLight, border:`1px solid ${C.amber}30`, borderRadius:8, fontSize:13, color:C.amber, marginBottom:18 }}>
                      Mention myPTOnline when the PT calls to receive your discount!
                    </div>

                    {/* Simulated client form — read-only labels to illustrate layout */}
                    {[
                      ["First name", "Jane"],
                      ["Last name", "Smith"],
                      ["Email", "jane.smith@email.com"],
                    ].map(([lbl, val]) => (
                      <div key={lbl} style={{ marginBottom:10 }}>
                        <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 }}>{lbl}</div>
                        <div style={{ padding:"9px 12px", background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, color:C.textSub }}>{val}</div>
                      </div>
                    ))}

                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                      {[["Landline","020 7946 0920"],["Mobile","07700 900123"]].map(([lbl,val])=>(
                        <div key={lbl}>
                          <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 }}>{lbl}</div>
                          <div style={{ padding:"9px 12px", background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, color:C.textSub }}>🇬🇧 {val}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 }}>Contact preference</div>
                      <div style={{ padding:"9px 12px", background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, color:C.textSub }}>SMS</div>
                    </div>

                    <div style={{ marginBottom:18 }}>
                      <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>Messaging app (optional)</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                        {MSG_APPS.map(app => (
                          <span key={app.id} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:600, background:app.id==="whatsapp"?C.primary:C.surfaceAlt, color:app.id==="whatsapp"?"#0c0c0e":C.textSub, border:`1px solid ${app.id==="whatsapp"?C.primary:C.border}` }}>
                            {app.icon} {app.label}
                          </span>
                        ))}
                      </div>
                      <div style={{ padding:"9px 12px", background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, color:C.textSub }}>💬 🇬🇧 +44 &nbsp;7700 900456</div>
                    </div>

                    {/* CTA */}
                    <div style={{ padding:"9px 12px", background:C.primary, borderRadius:8, textAlign:"center", fontSize:14, fontWeight:700, color:"#0c0c0e" }}>
                      Submit Request
                    </div>

                    <p style={{ marginTop:14, fontSize:11, color:C.textMuted, textAlign:"center", lineHeight:1.6 }}>
                      You receive the client's details by email and can follow up at your convenience.
                    </p>
                  </div>
                </div>
              )}
              <button className="btn btn-primary" style={{ width:"100%", padding:12 }} onClick={()=>setStep(2)}>Continue →</button>
            </>
          ) : (
            <>
              <div style={{marginBottom:14}}><label className="label">About Me</label><textarea className="input" rows={3} placeholder="Describe your experience and approach..." style={{resize:"vertical"}} value={form.experience} onChange={e=>upd("experience",e.target.value)} /></div>
              <div style={{marginBottom:14}}>
                <label className="label">Specialism</label>
                <select className="input" value={form.specialism} onChange={e=>upd("specialism",e.target.value)}>
                  <option value="">Select your main specialism…</option>
                  {["Weight Loss","Muscle Building","Muscular Sculpting","Sports Recovery","Post-natal Fitness","Cardio Fitness","Strength & Conditioning","Nutrition","Rehabilitation","Boxing & Combat"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid-2-form" style={{ gap:14, marginBottom:14 }}>
                <div><label className="label">Location / Area</label><input className="input" placeholder="e.g. Clapham, London" value={form.location} onChange={e=>upd("location",e.target.value)} /></div>
                <div><label className="label">Rate (£/hour)</label><input className="input" type="number" placeholder="e.g. 40" value={form.rate} onChange={e=>upd("rate",e.target.value)} /></div>
              </div>
              {/* Social media */}
              <div style={{ marginBottom:8, fontSize:12, fontWeight:600, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px" }}>Social media <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional — shown on your profile)</span></div>
              <div className="grid-2-form" style={{ gap:10, marginBottom:10 }}>
                <div><label className="label">📸 Instagram</label><input className="input" placeholder="@yourhandle or post URL" value={form.instagram} onChange={e=>upd("instagram",e.target.value)} /></div>
                <div><label className="label">▶️ YouTube</label><input className="input" placeholder="Channel or video URL" value={form.youtube} onChange={e=>upd("youtube",e.target.value)} /></div>
              </div>
              <div className="grid-2-form" style={{ gap:10, marginBottom:10 }}>
                <div><label className="label">🎵 TikTok</label><input className="input" placeholder="@yourhandle" value={form.tiktok} onChange={e=>upd("tiktok",e.target.value)} /></div>
                <div><label className="label">📘 Facebook</label><input className="input" placeholder="Page URL" value={form.facebook} onChange={e=>upd("facebook",e.target.value)} /></div>
              </div>
              <div className="grid-2-form" style={{ gap:10, marginBottom:24 }}>
                <div><label className="label">💼 LinkedIn</label><input className="input" placeholder="Profile URL" value={form.linkedin} onChange={e=>upd("linkedin",e.target.value)} /></div>
                <div><label className="label">𝕏 Twitter / X</label><input className="input" placeholder="@yourhandle" value={form.twitter} onChange={e=>upd("twitter",e.target.value)} /></div>
              </div>
              {error && <div style={{ background:"rgba(255,80,80,0.08)", color:"#ff5050", border:"1px solid rgba(255,80,80,0.2)", borderRadius:8, padding:"10px 14px", fontSize:13, marginBottom:16 }}>{error}</div>}
              <div style={{ display:"flex", gap:10 }}>
                <button className="btn btn-outline" style={{flex:1,padding:12}} onClick={()=>setStep(1)}>Back</button>
                <button className="btn btn-primary" style={{flex:2,padding:12}} onClick={handleCreate} disabled={loading}>{loading ? (completeMode ? "Saving…" : "Creating account…") : (completeMode ? "Finish setup →" : "Create Profile")}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PT DASHBOARD ──────────────────────────────────────────────────────────────
// ── PRODUCT RATING PROMPT (shown to established PTs on their dashboard) ────────
function RatingPrompt({ ptId, ptName, reason, onDone }) {
  const [rating, setRating]     = useState(0);
  const [comment, setComment]   = useState("");
  const [expanded, setExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = () => {
    if (!rating) return;
    saveProductRating(ptId, ptName, rating, comment.trim());
    setSubmitted(true);
    setTimeout(() => onDone?.(), 1800);
  };
  const later = () => { snoozeRatingPrompt(ptId); onDone?.(); };

  if (submitted) {
    return (
      <div className="card" style={{ marginBottom: 24, background: C.primaryGlow, border: `1px solid ${C.primary}30`, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 22 }}>🎉</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Thanks for the feedback!</div>
          <div style={{ fontSize: 13, color: C.textSub }}>Your {rating}-star rating helps us improve myPTOnline.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 24, background: C.primaryGlow, border: `1px solid ${C.primary}30` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Enjoying myPTOnline?</div>
          <div style={{ fontSize: 13, color: C.textSub, marginBottom: 14 }}>{reason} — we'd love a quick rating.</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <StarRating value={rating} onChange={(n) => { setRating(n); setExpanded(true); }} />
            {rating > 0 && <span style={{ fontSize: 12, color: C.textMuted }}>{["", "Poor", "Fair", "Good", "Great", "Excellent"][rating]}</span>}
          </div>
          {expanded && (
            <div style={{ marginTop: 14 }}>
              <textarea className="input" rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Anything you'd like to share? (optional)" />
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button className="btn btn-primary btn-sm" disabled={!rating} onClick={submit}>Submit rating</button>
                <button className="btn btn-ghost btn-sm" style={{ color: C.textMuted }} onClick={later}>Maybe later</button>
              </div>
            </div>
          )}
        </div>
        {!expanded && (
          <button className="btn btn-ghost btn-sm" style={{ color: C.textMuted, flexShrink: 0 }} onClick={later} title="Dismiss">Maybe later</button>
        )}
      </div>
    </div>
  );
}

// ── Client roster (shared by Dashboard + My Clients) ─────────────────────────
// One source so the two screens can't drift. The demo PT (Percy, non-numeric id)
// uses the local seed clients; a real PT reads their own clients from Supabase
// (RLS scopes the query to the signed-in PT). Seed data never leaks into a real
// PT's view — and local lookups by id would collide anyway (seed Clint is id 1,
// so is the first real client on staging).
const ROSTER_COLUMNS = "id,name,username,email,status,objectives,user_id,mobile,landline,gender,dob,weight,height,occupation,lifestyle";

function useRosterClients(user) {
  const isDemoPT = typeof user?.id !== "number";
  const [demoClients] = useState(() => isDemoPT ? getAll("clients") : []);
  const [serverClients, setServerClients] = useState([]);
  const [nextSessions, setNextSessions] = useState({}); // client_id -> earliest upcoming session
  const [loading, setLoading] = useState(!isDemoPT);

  const reload = async () => {
    if (isDemoPT) return;
    try {
      // upcoming_sessions.date is YYYY-MM-DD text; compare against the LOCAL date.
      // Done/cancelled sessions are deleted from the table, so date alone suffices.
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const [{ data: rows }, { data: upcoming }] = await Promise.all([
        supabase.from("clients").select(ROSTER_COLUMNS).order("id", { ascending:false }),
        supabase.from("upcoming_sessions").select("client_id,name,date,time").gte("date", today).order("date").order("time"),
      ]);
      setServerClients(rows || []);
      const next = {};
      for (const s of upcoming || []) if (!next[s.client_id]) next[s.client_id] = s;
      setNextSessions(next);
    } catch { /* leave as-is */ }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { isDemoPT, demoClients, serverClients, nextSessions, loading, reload };
}

const CLIENT_STATUS_BADGES = {
  active:                ["badge-green", "Active"],
  invited:               ["badge-amber", "Invited"],
  "awaiting-activation": ["badge-blue",  "Awaiting activation"],
  "awaiting-setup":      ["badge-blue",  "Setup sent"],
  "awaiting-signup":     ["badge-amber", "Awaiting signup"], // demo seed
  "awaiting-notes":      ["badge-red",   "Awaiting notes"],  // demo seed
};
function ClientStatusBadge({ status }) {
  const [cls, label] = CLIENT_STATUS_BADGES[status] || ["badge-blue", status || "—"];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function Dashboard({ user, setView, setSelectedClient }) {
  const { isDemoPT, demoClients, serverClients, nextSessions, loading: clientsLoading } = useRosterClients(user);
  const [contacts, setContacts] = useState(() => getAll("contacts"));
  const [ratingReason, setRatingReason] = useState(() => ratingRequestReason(user.id, user.name));
  const hideDemo = getHideDemoClients();
  const shownClients = isDemoPT ? (hideDemo ? demoClients.filter(c => !c.demo) : demoClients) : serverClients;
  const activeCount = shownClients.filter(c => c.status === "active").length;
  const nextSessionText = (c) => {
    if (isDemoPT) return getNextSession(c.id) || "No upcoming session";
    const s = nextSessions[c.id];
    return s ? `${s.date.split("-").reverse().join("/")} · ${s.name || "Session"} ${s.time || ""}`.trim() : "No upcoming session";
  };

  return (
    <div className="page-content">
      <div style={{ marginBottom: 32 }}>
        <div className="accent-line" />
        <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.5px" }}>Welcome back, {user.name.split(" ")[0]}</h1>
        {/* Login email — the account this session is signed in as */}
        {user.email && (
          <p style={{ color: C.textMuted, fontSize: 13, marginTop: 6 }}>
            ✉️ Signed in as <strong style={{ color: C.textSub, fontWeight: 600 }}>{user.email}</strong>
          </p>
        )}
        <p style={{ color: C.textSub, fontSize: 14, marginTop: 6 }}>
          Next session: <strong style={{ color: C.primary }}>Clint Fitz — 30/03/2025 Body Build 19:00</strong>
        </p>
      </div>

      {ratingReason && (
        <RatingPrompt ptId={user.id} ptName={user.name} reason={ratingReason} onDone={() => setRatingReason(null)} />
      )}

      {/* Stats */}
      <div className="grid-stats">
        {[
          { num: clientsLoading ? "…" : activeCount, label: "Active clients", view: "clients" },
          { num: user.contactMeCount,  label: "New enquiries",      view: "contact-me"   },
          { num: user.msgCount,        label: "Unread messages",    view: "pt-messages"  },
          { num: 12,                   label: "Sessions this month", view: "monthly-report" },
        ].map(s => (
          <div key={s.label} className="stat-card"
            onClick={() => s.view && setView(s.view)}
            style={{ cursor: s.view ? "pointer" : "default", transition:"all 0.15s" }}
            onMouseEnter={e => { if (s.view) e.currentTarget.style.borderColor = C.primary; }}
            onMouseLeave={e => { if (s.view) e.currentTarget.style.borderColor = ""; }}
            title={s.view ? `Go to ${s.label}` : undefined}
          >
            <div className="stat-num">{s.num}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid-2">
        {/* Clients */}
        <div className="card">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
            <span style={{ fontWeight:600, fontSize:15 }}>My Clients</span>
            <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>setView("clients")}>View all →</button>
          </div>
          {shownClients.map(c => (
            <div key={c.id} onClick={()=>{ setSelectedClient(c); setView(isDemoPT ? "client-detail" : "reg-client-detail"); }}
              style={{ padding:"12px 0", borderBottom:`1px solid ${C.border}`, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <Avatar name={c.name} size={32} />
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontWeight:500, fontSize:14 }}>{c.name}</span>
                    {c.demo && <DemoTag />}
                  </div>
                  <div style={{ fontSize:12, color:C.textMuted, marginTop:2 }}>{nextSessionText(c)}</div>
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {c.msgs > 0 && <span className="badge badge-blue">{c.msgs} msg</span>}
                <ClientStatusBadge status={c.status} />
              </div>
            </div>
          ))}
          {shownClients.length === 0 && (
            <div style={{ padding:"14px 0", color:C.textMuted, fontSize:13 }}>
              {clientsLoading ? "Loading…" : (isDemoPT && hideDemo) ? "Demo clients hidden." : "No clients yet."}
            </div>
          )}
        </div>

        {/* Contact Me */}
        <div className="card">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
            <span style={{ fontWeight:600, fontSize:15 }}>Contact Me Requests</span>
            <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>setView("contact-me")}>View all →</button>
          </div>
          {contacts.slice(0,3).map((c,i) => (
            <div key={i} style={{ padding:"12px 0", borderBottom: i<2?`1px solid ${C.border}`:"none", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontWeight:500, fontSize:14 }}>{c.name}</div>
                <div style={{ fontSize:12, color:C.textMuted, marginTop:2 }}>Prefers {c.preference} · {c.bestTime}</div>
              </div>
              <span className={`badge ${c.contacted?"badge-green":"badge-amber"}`}>{c.contacted?"Contacted":"Pending"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Client Login Link */}
      <div className="card" style={{ marginTop: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Client Login Link</span>
          <span className="badge badge-lime">Share with clients</span>
        </div>
        <p style={{ fontSize: 13, color: C.textSub, marginBottom: 14, lineHeight: 1.6 }}>
          Share this personalised URL with your clients so they can log in directly to your training portal.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, padding: "10px 14px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.primary, fontFamily: "monospace", letterSpacing: "0.3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {window.location.origin + window.location.pathname + "#/pt/" + slugify(user.name)}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => {
            const url = window.location.origin + window.location.pathname + "#/pt/" + slugify(user.name);
            navigator.clipboard.writeText(url).catch(() => {});
          }}>Copy</button>
        </div>
      </div>
    </div>
  );
}

// ── CLIENTS LIST ──────────────────────────────────────────────────────────────
// PAR-Q screening questions (shared by the wizard and the client's own view).
const PARQ_QUESTIONS = [
  "Has your doctor ever said you have a heart condition requiring supervised exercise?",
  "Do you feel pain in your chest when you do physical activity?",
  "In the past month, have you had chest pain when not active?",
  "Do you lose your balance due to dizziness or lose consciousness?",
  "Do you have a bone or joint problem that could be worsened by physical activity?",
  "Is your doctor currently prescribing medication for blood pressure or a heart condition?",
  "Do you know of any other reason you should not do physical activity?",
];

async function ptAuthHeader() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

// ── Phone validation (UK-first; lenient for non-UK international numbers) ──────
const cleanPhone = v => (v || "").replace(/[\s()\-.]/g, "");
const isPhoneish = v => /^\+?\d{7,15}$/.test(cleanPhone(v));
const looksUK    = v => /^(?:\+?44|0)/.test(cleanPhone(v));
const nsnUK      = v => cleanPhone(v).replace(/^\+?44/, "").replace(/^0/, "");
const isMobile   = v => isPhoneish(v) && (looksUK(v) ? /^7\d{9}$/.test(nsnUK(v)) : true);
const isLandline = v => isPhoneish(v) && (looksUK(v) ? /^[1-9]\d{8,9}$/.test(nsnUK(v)) : true);

// Space UK numbers as they're typed (keeps the leading 0): 07911 123456 / 020 1234 5678.
function formatUKPhone(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.startsWith("07")) return d.length <= 5 ? d : `${d.slice(0,5)} ${d.slice(5)}`;          // mobile 5+6
  if (d.startsWith("02")) return [d.slice(0,3), d.slice(3,7), d.slice(7,11)].filter(Boolean).join(" "); // 020 area 3+4+4
  return d.length <= 5 ? d : `${d.slice(0,5)} ${d.slice(5)}`;                                    // other landline 5+6
}

// ── REGISTER CLIENT WIZARD ────────────────────────────────────────────────────
function RegisterClientWizard({ onClose, onDone }) {
  const [step, setStep] = useState(1);           // 1 details · 2 PAR-Q · 3 invite · 4 done
  const [f, setF] = useState({ firstName:"", lastName:"", email:"", landline:"", mobile:"", dob:"", gender:"Male", weight:"", height:"", occupation:"", lifestyle:"", objectives:"" });
  const [parq, setParq] = useState(Array(7).fill(false));
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim());
  const mobileOk   = !f.mobile   || isMobile(f.mobile);
  const landlineOk = !f.landline || isLandline(f.landline);
  const step1Ok = f.firstName.trim() && emailOk && mobileOk && landlineOk;
  const parqFlagged = parq.some(Boolean);
  const suggestedUser = `${f.firstName}.${f.lastName}`.toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/(^\.|\.$)/g, "");

  const goInvite = () => { if (!username) setUsername(suggestedUser); setStep(3); };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const res = await fetch("/api/client", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await ptAuthHeader()) },
        body: JSON.stringify({
          action: "register", firstName: f.firstName, lastName: f.lastName, email: f.email,
          landline: f.landline, mobile: f.mobile, dob: f.dob, gender: f.gender,
          weight: f.weight, height: f.height, occupation: f.occupation, lifestyle: f.lifestyle,
          objectives: f.objectives, parq, username: username || suggestedUser, notify: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setErr(data.error || "Could not register the client."); setBusy(false); return; }
      setResult(data); setStep(4); onDone?.();
    } catch { setErr("Network error — please try again."); }
    setBusy(false);
  };

  const copy = (text, tag) => { try { navigator.clipboard.writeText(text); setCopied(tag); setTimeout(() => setCopied(""), 1500); } catch {} };

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth:540 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <span style={{ fontWeight:600, fontSize:16 }}>{step===4 ? "Client registered" : `Client Registration — Step ${step} of 3`}</span>
          <CloseBtn onClick={onClose} />
        </div>
        {step<4 && (
          <div style={{ display:"flex", gap:6, marginBottom:20 }}>
            {[1,2,3].map(s => <div key={s} style={{ flex:1, height:4, borderRadius:2, background: s<=step ? C.primary : C.surfaceAlt }} />)}
          </div>
        )}
        {err && <div style={{ padding:"10px 12px", background:C.dangerLight, color:C.danger, borderRadius:8, fontSize:13, marginBottom:14 }}>{err}</div>}

        {step===1 && <>
          <div className="grid-2-form">
            <div><label className="label">First name *</label><input className="input" placeholder="First name" value={f.firstName} onChange={e=>set("firstName", e.target.value)} /></div>
            <div><label className="label">Last name</label><input className="input" placeholder="Last name" value={f.lastName} onChange={e=>set("lastName", e.target.value)} /></div>
          </div>
          <div style={{marginBottom:12}}><label className="label">Email *</label><input className="input" type="email" placeholder="client@email.com" value={f.email} onChange={e=>set("email", e.target.value)} />
            {f.email && !emailOk && <div style={{ fontSize:12, color:C.danger, marginTop:4 }}>Enter a valid email address.</div>}</div>
          <div className="grid-2-form">
            <div><label className="label">Landline</label><input className="input" type="tel" placeholder="020 XXXX XXXX" value={f.landline} onChange={e=>set("landline", formatUKPhone(e.target.value))} />
              {!landlineOk && <div style={{ fontSize:12, color:C.danger, marginTop:4 }}>Enter a valid landline number.</div>}</div>
            <div><label className="label">Mobile</label><input className="input" type="tel" placeholder="07XXX XXXXXX" value={f.mobile} onChange={e=>set("mobile", formatUKPhone(e.target.value))} />
              {!mobileOk && <div style={{ fontSize:12, color:C.danger, marginTop:4 }}>Enter a valid mobile number.</div>}</div>
          </div>
          <div className="grid-2-form">
            <div><label className="label">Date of Birth</label><input className="input" type="date" value={f.dob} onChange={e=>set("dob", e.target.value)} /></div>
            <div><label className="label">Gender</label><select className="input" value={f.gender} onChange={e=>set("gender", e.target.value)}><option>Male</option><option>Female</option><option>Other</option></select></div>
          </div>
          <div className="grid-2-form">
            <div><label className="label">Weight (kg)</label><input className="input" type="number" placeholder="75" value={f.weight} onChange={e=>set("weight", e.target.value)} /></div>
            <div><label className="label">Height (cm)</label><input className="input" type="number" placeholder="175" value={f.height} onChange={e=>set("height", e.target.value)} /></div>
          </div>
          <div style={{marginBottom:12}}><label className="label">Occupation</label><input className="input" placeholder="e.g. Consultant" value={f.occupation} onChange={e=>set("occupation", e.target.value)} /></div>
          <div style={{marginBottom:12}}><label className="label">Lifestyle</label><textarea className="input" rows={2} placeholder="Diet, activity level, habits..." value={f.lifestyle} onChange={e=>set("lifestyle", e.target.value)} /></div>
          <div style={{marginBottom:20}}><label className="label">Health & Fitness Objectives</label><textarea className="input" rows={2} placeholder="What does this client want to achieve?" value={f.objectives} onChange={e=>set("objectives", e.target.value)} /></div>
          <button className="btn btn-primary" style={{width:"100%", opacity: step1Ok?1:0.5, cursor: step1Ok?"pointer":"not-allowed"}} disabled={!step1Ok} onClick={()=>setStep(2)}>Continue to PAR-Q →</button>
        </>}

        {step===2 && <>
          <p style={{ fontSize:13, color:C.textSub, marginBottom:16, lineHeight:1.6 }}>Physical Activity Readiness Questionnaire. Ensure the client answers all questions before continuing.</p>
          {PARQ_QUESTIONS.map((q,i) => (
            <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:10, padding:"10px 12px", background:C.surfaceAlt, borderRadius:8 }}>
              <div style={{ fontSize:13, flex:1, lineHeight:1.5, color:C.textSub }}>{i+1}. {q}</div>
              <div style={{ display:"flex", gap:14 }}>
                <label style={{ fontSize:13, color:C.text, cursor:"pointer" }}><input type="radio" name={`q${i}`} checked={parq[i]===true} onChange={()=>setParq(p=>p.map((v,j)=>j===i?true:v))} /> Yes</label>
                <label style={{ fontSize:13, color:C.text, cursor:"pointer" }}><input type="radio" name={`q${i}`} checked={parq[i]===false} onChange={()=>setParq(p=>p.map((v,j)=>j===i?false:v))} /> No</label>
              </div>
            </div>
          ))}
          {parqFlagged && (
            <div style={{ padding:"11px 13px", background:C.amberLight, border:`1px solid ${C.amber}`, borderRadius:8, fontSize:13, color:C.amber, margin:"6px 0 4px", lineHeight:1.5 }}>
              ⚠ One or more answers are "Yes". Medical clearance is recommended before this client starts physical activity.
            </div>
          )}
          <div style={{ display:"flex", gap:10, marginTop:20 }}>
            <button className="btn btn-outline" style={{flex:1}} onClick={()=>setStep(1)}>Back</button>
            <button className="btn btn-primary" style={{flex:2}} onClick={goInvite}>Continue →</button>
          </div>
        </>}

        {step===3 && <>
          <p style={{ fontSize:13, color:C.textSub, marginBottom:16, lineHeight:1.6 }}>Set the username your client will use to start sign-up. This is a two-stage process: first, a one-time 6-digit PIN is <strong style={{color:C.text}}>emailed straight to the client</strong>; after they confirm it, you activate them and they receive a second email to set their password.</p>
          <div style={{marginBottom:16}}><label className="label">Client username</label><input className="input" value={username} onChange={e=>setUsername(e.target.value)} placeholder={suggestedUser || "client.name"} /></div>
          <div style={{ padding:"12px 14px", background:C.primaryGlow, border:`1px solid ${C.primary}20`, borderRadius:8, fontSize:13, color:C.primary, marginBottom:22, lineHeight:1.55 }}>
            📧 Stage 1: on registration we email the PIN + sign-up link to <strong>{f.email || "the client's email"}</strong> (valid 28 days, and 24 hours after first use). Stage 2: once they confirm the PIN, activate them from the client list so they receive their password setup email.
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button className="btn btn-outline" style={{flex:1}} onClick={()=>setStep(2)}>Back</button>
            <button className="btn btn-primary" style={{flex:2, opacity: busy?0.6:1}} disabled={busy} onClick={submit}>{busy ? "Registering…" : "Complete Registration"}</button>
          </div>
        </>}

        {step===4 && result && <>
          <div style={{ textAlign:"center", marginBottom:16 }}>
            <div style={{ width:52, height:52, borderRadius:"50%", background: result.emailed ? C.greenLight : C.amberLight, border:`2px solid ${result.emailed ? C.green : C.amber}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, margin:"0 auto 12px" }}>{result.emailed ? "✓" : "!"}</div>
            <h3 style={{ fontSize:18, fontWeight:700 }}>{result.name} is registered</h3>
          </div>
          {result.emailed ? (
            <div style={{ background:C.surfaceAlt, borderRadius:10, padding:"16px 18px", marginBottom:14 }}>
              <p style={{ fontSize:13.5, color:C.textSub, marginBottom:6 }}>Their sign-up PIN has been emailed to:</p>
              <p style={{ fontSize:15, fontWeight:700, color:C.primary, marginBottom:14, wordBreak:"break-all" }}>{f.email}</p>
              <p style={{ fontSize:13, color:C.textSub, lineHeight:1.65 }}>Tell your client:</p>
              <p style={{ fontSize:14, color:C.text, fontWeight:600, lineHeight:1.6, margin:"4px 0 10px" }}>“Check your email for your myPTOnline PIN and confirm it. After I activate your account, you'll get another email to set your password.”</p>
              <p style={{ fontSize:12, color:C.textMuted, lineHeight:1.6 }}>You won't see the PIN — it stays private to the client. After they confirm it, use <strong style={{color:C.text}}>Activate</strong> on their client row to send the password setup email. If they didn't get the first email, check the address is right (Edit) and Resend.</p>
            </div>
          ) : (
            <div style={{ background:C.amberLight, border:`1px solid ${C.amber}`, borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
              <p style={{ fontSize:13.5, color:C.amber, lineHeight:1.6 }}>⚠ We couldn't email the invite to <strong>{f.email}</strong>. The PIN is only sent by email, so check the address is correct — use <strong>Edit</strong> on their row to fix it, then <strong>Resend invite</strong>.</p>
            </div>
          )}
          <div style={{ fontSize:12.5, color:C.textSub, marginBottom:12 }}>Their username is <strong style={{color:C.text}}>{result.username}</strong> (also included in the email).</div>
          <button className="btn btn-outline btn-sm" style={{ width:"100%", marginBottom:12 }} onClick={()=>copy(`You're invited to myPTOnline! First, check your email (${f.email}) for your 6-digit PIN, then go to ${result.signupUrl} and enter your username "${result.username}" and that PIN. After you confirm it, I'll activate your account and you'll receive a second email to set your password.`, "msg")}>{copied==="msg" ? "Copied ✓" : "Copy a reminder message"}</button>
          {result.parqFlagged && <div style={{ fontSize:12.5, color:C.amber, marginBottom:8 }}>⚠ PAR-Q flagged — recommend medical clearance before starting activity.</div>}
          <button className="btn btn-primary" style={{width:"100%", marginTop:4}} onClick={onClose}>Done</button>
        </>}
      </div>
    </div>
  );
}

// ── EDIT CLIENT (PT edits their client · client edits their own personal details) ─
// Loads the full record by id (RLS lets the PT read their clients and a client
// read their own row) and saves via the server `update` action. Email/username/
// status are shown read-only — they aren't editable here.
function ClientEditModal({ clientId, onClose, onSaved }) {
  const [c, setC]         = useState(null);
  const [parq, setParq]   = useState(Array(7).fill(false));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      const { data } = await supabase.from("clients").select("*").eq("id", clientId).maybeSingle();
      if (!live) return;
      if (data) {
        setC(data);
        const a = Array.isArray(data.parq?.answers) ? data.parq.answers.map(Boolean) : [];
        setParq(Array.from({ length: 7 }, (_, i) => a[i] === true));
      }
      setLoading(false);
    })();
    return () => { live = false; };
  }, [clientId]);

  const set = (k, v) => setC(p => ({ ...p, [k]: v }));
  const parqFlagged = parq.some(Boolean);
  const signedUp = !!c?.user_id; // email locks once they have a login

  const save = async () => {
    if (!c?.name?.trim()) { setErr("Name is required."); return; }
    if (c.mobile && !isMobile(c.mobile)) { setErr("Enter a valid mobile number."); return; }
    if (c.landline && !isLandline(c.landline)) { setErr("Enter a valid landline number."); return; }
    setBusy(true); setErr("");
    try {
      const res = await fetch("/api/client", {
        method: "POST", headers: { "Content-Type": "application/json", ...(await ptAuthHeader()) },
        body: JSON.stringify({ action: "update", clientId, fields: {
          name: c.name, email: c.email, mobile: c.mobile, landline: c.landline, gender: c.gender, dob: c.dob,
          weight: c.weight, height: c.height, occupation: c.occupation, lifestyle: c.lifestyle, objectives: c.objectives, parq,
        } }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setErr(data.error || "Could not save changes."); setBusy(false); return; }
      onSaved?.(data.client); onClose();
    } catch { setErr("Network error — please try again."); }
    setBusy(false);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth:540, maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <span style={{ fontWeight:600, fontSize:16 }}>Edit client</span>
          <CloseBtn onClick={onClose} />
        </div>
        {loading && <div style={{ padding:"24px 0", textAlign:"center", color:C.textMuted, fontSize:13 }}>Loading…</div>}
        {!loading && !c && <div style={{ padding:"24px 0", textAlign:"center", color:C.danger, fontSize:13 }}>Could not load this client.</div>}
        {!loading && c && <>
          {err && <div style={{ padding:"10px 12px", background:C.dangerLight, color:C.danger, borderRadius:8, fontSize:13, marginBottom:14 }}>{err}</div>}
          <div style={{ marginBottom:12 }}><label className="label">Name *</label><input className="input" value={c.name||""} onChange={e=>set("name", e.target.value)} /></div>
          <div style={{ marginBottom:12 }}>
            <label className="label">Email {signedUp && <span style={{ color:C.textMuted, fontWeight:400, textTransform:"none", letterSpacing:0 }}>· login</span>}</label>
            <input className="input" type="email" value={c.email||""} onChange={e=>set("email", e.target.value)} disabled={signedUp} style={signedUp ? { opacity:0.55, cursor:"not-allowed" } : undefined} />
            {signedUp ? (
              <div style={{ display:"flex", gap:8, alignItems:"flex-start", marginTop:6, padding:"9px 11px", background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, fontSize:12, color:C.textSub, lineHeight:1.55 }}>
                <span style={{ fontSize:13 }}>🔒</span>
                <span><strong style={{ color:C.text }}>Locked.</strong> This is the client's login — the email <strong style={{ color:C.text }}>can't be changed once they've signed up.</strong></span>
              </div>
            ) : (
              <div style={{ fontSize:12, color:C.primary, marginTop:6, lineHeight:1.55 }}>
                ✎ You can change this <strong>now, while they're invited</strong>. After the client signs up it becomes their login and <strong>can't be changed here.</strong>
              </div>
            )}
          </div>
          <div className="grid-2-form">
            <div><label className="label">Landline</label><input className="input" type="tel" placeholder="020 XXXX XXXX" value={c.landline||""} onChange={e=>set("landline", formatUKPhone(e.target.value))} /></div>
            <div><label className="label">Mobile</label><input className="input" type="tel" placeholder="07XXX XXXXXX" value={c.mobile||""} onChange={e=>set("mobile", formatUKPhone(e.target.value))} /></div>
          </div>
          <div className="grid-2-form">
            <div><label className="label">Date of Birth</label><input className="input" type="date" value={c.dob||""} onChange={e=>set("dob", e.target.value)} /></div>
            <div><label className="label">Gender</label><select className="input" value={c.gender||"Male"} onChange={e=>set("gender", e.target.value)}><option>Male</option><option>Female</option><option>Other</option></select></div>
          </div>
          <div className="grid-2-form">
            <div><label className="label">Weight (kg)</label><input className="input" type="number" value={c.weight??""} onChange={e=>set("weight", e.target.value)} /></div>
            <div><label className="label">Height (cm)</label><input className="input" type="number" value={c.height??""} onChange={e=>set("height", e.target.value)} /></div>
          </div>
          <div style={{ marginBottom:12 }}><label className="label">Occupation</label><input className="input" value={c.occupation||""} onChange={e=>set("occupation", e.target.value)} /></div>
          <div style={{ marginBottom:12 }}><label className="label">Lifestyle</label><textarea className="input" rows={2} value={c.lifestyle||""} onChange={e=>set("lifestyle", e.target.value)} /></div>
          <div style={{ marginBottom:16 }}><label className="label">Health & Fitness Objectives</label><textarea className="input" rows={2} value={c.objectives||""} onChange={e=>set("objectives", e.target.value)} /></div>
          <div style={{ marginBottom:8 }}><label className="label">PAR-Q health screening</label></div>
          {PARQ_QUESTIONS.map((q,i) => (
            <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:8, padding:"8px 10px", background:C.surfaceAlt, borderRadius:8 }}>
              <div style={{ fontSize:12.5, flex:1, lineHeight:1.5, color:C.textSub }}>{i+1}. {q}</div>
              <div style={{ display:"flex", gap:12 }}>
                <label style={{ fontSize:13, color:C.text, cursor:"pointer" }}><input type="radio" name={`eq${i}`} checked={parq[i]===true} onChange={()=>setParq(p=>p.map((v,j)=>j===i?true:v))} /> Yes</label>
                <label style={{ fontSize:13, color:C.text, cursor:"pointer" }}><input type="radio" name={`eq${i}`} checked={parq[i]===false} onChange={()=>setParq(p=>p.map((v,j)=>j===i?false:v))} /> No</label>
              </div>
            </div>
          ))}
          {parqFlagged && <div style={{ padding:"10px 12px", background:C.amberLight, border:`1px solid ${C.amber}`, borderRadius:8, fontSize:12.5, color:C.amber, margin:"6px 0 12px" }}>⚠ One or more "Yes" answers — medical clearance recommended before activity.</div>}
          <div style={{ display:"flex", gap:10, marginTop:16 }}>
            <button className="btn btn-outline" style={{ flex:1 }} onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" style={{ flex:2, opacity: busy?0.6:1 }} disabled={busy} onClick={save}>{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </>}
      </div>
    </div>
  );
}

function ClientsList({ setView, setSelectedClient, onMessageClient, user }) {
  // Demo PT (Percy, non-numeric id) shows its local seed clients; real PTs show only their Supabase clients.
  const { demoClients, serverClients, loading, reload: load } = useRosterClients(user);
  const [showReg, setShowReg] = useState(false);
  const [hideDemo, setHideDemo] = useState(getHideDemoClients);
  const [resendState, setResendState] = useState({}); // clientId -> {busy|pin}
  const [editId, setEditId] = useState(null);         // client id being edited

  const resend = async (c) => {
    setResendState(s => ({ ...s, [c.id]: { busy:true } }));
    try {
      const res = await fetch("/api/client", {
        method:"POST", headers:{ "Content-Type":"application/json", ...(await ptAuthHeader()) },
        body: JSON.stringify({ action:"resend", clientId: c.id, notify:true }),
      });
      const data = await res.json();
      setResendState(s => ({ ...s, [c.id]: res.ok && data.ok ? { done:true, emailed:data.emailed } : { error: data.error || "Failed" } }));
    } catch { setResendState(s => ({ ...s, [c.id]: { error:"Network error" } })); }
  };

  // Activate a client who has confirmed their PIN → emails them a set-password link.
  const activate = async (c) => {
    setResendState(s => ({ ...s, [c.id]: { busy:true } }));
    try {
      const res = await fetch("/api/client", {
        method:"POST", headers:{ "Content-Type":"application/json", ...(await ptAuthHeader()) },
        body: JSON.stringify({ action:"activate", clientId: c.id }),
      });
      const data = await res.json();
      if (res.ok && data.ok) { setResendState(s => ({ ...s, [c.id]: { activated:true, emailed:data.emailed } })); load(); }
      else setResendState(s => ({ ...s, [c.id]: { error: data.error || "Failed" } }));
    } catch { setResendState(s => ({ ...s, [c.id]: { error:"Network error" } })); }
  };

  const hasDemo = demoClients.length > 0;
  const demoRows = (hideDemo ? [] : demoClients);

  const statusBadge = (c) => <ClientStatusBadge status={c.status} />;

  return (
    <div className="page-content">
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:28 }}>
        <div>
          <div className="accent-line" />
          <h1 style={{ fontSize:28, fontWeight:700, letterSpacing:"-0.5px" }}>My Clients</h1>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          {hasDemo && (
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:C.textSub, cursor:"pointer", userSelect:"none" }}>
              <input type="checkbox" checked={hideDemo} onChange={e=>{ setHideDemo(e.target.checked); setHideDemoClients(e.target.checked); }} />
              Hide demo clients
            </label>
          )}
          <button className="btn btn-primary" onClick={()=>setShowReg(true)}>+ Register Client</button>
        </div>
      </div>

      {showReg && <RegisterClientWizard onClose={()=>setShowReg(false)} onDone={load} />}
      {editId && <ClientEditModal clientId={editId} onClose={()=>setEditId(null)} onSaved={load} />}

      <div className="card table-wrap" style={{ padding:0, overflow:"hidden" }}>
        <table className="dark-table">
          <thead>
            <tr>{["Client","Status","Objectives","Actions"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {serverClients.map(c => {
              const rs = resendState[c.id];
              return (
                <tr key={`s${c.id}`}>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <Avatar name={c.name} size={32} />
                      {/* Email under the name (not its own column). The username only
                          matters for the one-time invite step and is shown on the
                          client's detail page. */}
                      <div><div style={{ fontWeight:500 }}>{c.name}</div><div style={{ fontSize:11, color:C.textMuted, maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={c.email || undefined}>{c.email || `@${c.username}`}</div></div>
                    </div>
                  </td>
                  <td>{statusBadge(c)}
                    {rs?.done && (rs.emailed ? <div style={{ fontSize:11, color:C.green, marginTop:4 }}>Invite re-sent ✓</div> : <div style={{ fontSize:11, color:C.amber, marginTop:4 }}>Email failed — check the address</div>)}
                    {rs?.activated && (rs.emailed ? <div style={{ fontSize:11, color:C.green, marginTop:4 }}>Setup email sent ✓</div> : <div style={{ fontSize:11, color:C.amber, marginTop:4 }}>Activated, but email failed</div>)}
                    {rs?.error && <div style={{ fontSize:11, color:C.danger, marginTop:4 }}>{rs.error}</div>}
                  </td>
                  <td style={{ color:C.textSub, fontSize:13, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.objectives || "—"}</td>
                  <td>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>{ setSelectedClient(c); setView("reg-client-detail"); }}>View</button>
                      <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>onMessageClient?.(c)}>Message</button>
                      <button className="btn btn-ghost btn-sm" style={{color:C.textSub}} onClick={()=>setEditId(c.id)}>Edit</button>
                      {c.status === "awaiting-activation" && <button className="btn btn-ghost btn-sm" style={{color:C.green, fontWeight:600}} disabled={rs?.busy} onClick={()=>activate(c)}>{rs?.busy?"…":"Activate"}</button>}
                      {c.status === "invited" && <button className="btn btn-ghost btn-sm" style={{color:C.textSub}} disabled={rs?.busy} onClick={()=>resend(c)}>{rs?.busy?"…":"Resend invite"}</button>}
                      {c.status === "awaiting-setup" && <button className="btn btn-ghost btn-sm" style={{color:C.textSub}} disabled={rs?.busy} onClick={()=>activate(c)}>{rs?.busy?"…":"Resend setup email"}</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {demoRows.map(c => (
              <tr key={`d${c.id}`}>
                <td><div style={{ display:"flex", alignItems:"center", gap:10 }}><Avatar name={c.name} size={32} /><span style={{ fontWeight:500 }}>{c.name}</span> <DemoTag /></div></td>
                <td><span className={`badge ${c.status==="active"?"badge-green":c.status==="awaiting-signup"?"badge-amber":"badge-red"}`}>{c.status==="active"?"Active":c.status==="awaiting-signup"?"Awaiting signup":"Awaiting notes"}</span></td>
                <td style={{ color:C.textSub, fontSize:13, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.objectives}</td>
                <td><button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>{setSelectedClient(c);setView("client-detail");}}>View →</button></td>
              </tr>
            ))}
            {!loading && serverClients.length === 0 && demoRows.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign:"center", color:C.textMuted, padding:"32px 16px", fontSize:13 }}>
                {hideDemo ? "Demo clients are hidden. Register a client to build your roster." : "No clients yet — register your first client to get started."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── CLIENT DETAIL ─────────────────────────────────────────────────────────────
// ── Session feedback model ────────────────────────────────────────────────────
// A session carries two feedback slots — PT feedback (coachNotes) and client
// feedback (clientRating + clientComment). Each side can see the other's. The
// status is DERIVED from which slot is still empty, and the same label is shown
// in both the PT's and the client's views.
function sessionFeedbackState(s) {
  const hasPT     = !!(s.coachNotes && String(s.coachNotes).trim());
  const hasClient = !!s.clientRating || !!(s.clientComment && String(s.clientComment).trim());
  if (!hasPT)     return "awaiting-pt";       // PT still to leave feedback
  if (!hasClient) return "awaiting-client";   // client still to leave feedback
  return "complete";
}
function sessionBadge(s) {
  const st = sessionFeedbackState(s);
  if (st === "complete")    return { label: "Complete", cls: "badge-green" };
  if (st === "awaiting-pt") return { label: "Awaiting PT feedback", cls: "badge-amber" };
  return { label: "Awaiting client feedback", cls: "badge-blue" };
}

// ── REGISTERED CLIENT WORKSPACE (Supabase-persisted sessions & bookings) ──────
// ── PER-SET PERFORMANCE CAPTURE (PT logs actual reps/kg vs the plan) ──────────
// Stored as JSON in sessions.performance: { [exerciseName]: [{reps, kg}, ...] }.
function parsePerf(raw) { if (!raw) return {}; if (typeof raw === "object") return raw; try { return JSON.parse(raw); } catch { return {}; } }
function PerformanceEditor({ session, exercises, demo, onClose, onSaved }) {
  const [actuals, setActuals] = useState(() => {
    const stored = parsePerf(session.performance);
    const out = {};
    (exercises || []).forEach(e => {
      const n = Math.max(1, Number(e.sets) || 3);
      out[e.name] = Array.from({ length: n }, (_, i) => stored[e.name]?.[i] || { reps: e.reps ?? "", kg: e.kg ?? "" });
    });
    return out;
  });
  const [busy, setBusy] = useState(false);
  const setCell = (name, i, field, v) => setActuals(a => ({ ...a, [name]: a[name].map((s, j) => j === i ? { ...s, [field]: v } : s) }));

  const save = async () => {
    setBusy(true);
    if (demo) update("sessions", session.id, { performance: JSON.stringify(actuals) });
    else await supabase.from("sessions").update({ performance: JSON.stringify(actuals) }).eq("id", session.id);
    setBusy(false); onSaved();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 620, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <span style={{ fontWeight:700, fontSize:16 }}>Log performance</span>
          <CloseBtn onClick={onClose} />
        </div>
        <div style={{ fontSize:13, color:C.textMuted, marginBottom:16 }}>{session.date} — {session.name}. Enter actual reps/kg done each set (pre-filled from the plan).</div>
        {(!exercises || exercises.length === 0)
          ? <div style={{ color:C.textMuted, fontSize:13, padding:"12px 0" }}>No plan attached to this session — book with a fitness plan to log per-set performance.</div>
          : exercises.map(e => (
            <div key={e.name} style={{ marginBottom:16 }}>
              <div style={{ fontWeight:600, marginBottom:6 }}>{e.name} <span style={{ fontSize:12, color:C.textMuted, fontWeight:400 }}>· plan: {e.sets}×{e.reps} @ {e.kg||0}kg</span></div>
              <div style={{ overflowX:"auto" }}>
                <table className="dark-table" style={{ fontSize:13 }}>
                  <thead><tr><th>Set</th><th>Actual reps</th><th>Actual kg</th></tr></thead>
                  <tbody>
                    {(actuals[e.name] || []).map((s, i) => (
                      <tr key={i}>
                        <td style={{ color:C.textSub, width:60 }}>{i+1}</td>
                        <td><input className="input" type="number" style={{ padding:"6px 8px", width:100 }} value={s.reps} onChange={ev=>setCell(e.name, i, "reps", ev.target.value)} /></td>
                        <td><input className="input" type="number" style={{ padding:"6px 8px", width:100 }} value={s.kg} onChange={ev=>setCell(e.name, i, "kg", ev.target.value)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:12 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={busy || !exercises?.length} onClick={save}>{busy ? "Saving…" : "Save performance"}</button>
        </div>
      </div>
    </div>
  );
}

function RegisteredClientDetail({ client, setView }) {
  const [ptId, setPtId]       = useState(client.pt_id || null);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans]     = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [apprModal, setApprModal] = useState(null); // null=closed · {}=new · row=edit
  const [showBook, setShowBook] = useState(false);
  const [book, setBook]       = useState({ date:"", time:"19:00", name:"", planId:"" });
  const [err, setErr]         = useState("");
  const [noteDraft, setNoteDraft] = useState({}); // sessionId -> coach_notes draft
  const [perfSession, setPerfSession] = useState(null); // session whose per-set performance is being logged
  const exercisesForSession = (s) => (plans.find(p => p.id === s.plan_id)?.exercises) || [];

  const load = async () => {
    let pid = ptId;
    if (!pid) { const { data } = await supabase.from("clients").select("pt_id").eq("id", client.id).maybeSingle(); pid = data?.pt_id; setPtId(pid); }
    const [{ data: up }, { data: pa }, { data: pl }, { data: ap }] = await Promise.all([
      supabase.from("upcoming_sessions").select("*").eq("client_id", client.id).order("date"),
      supabase.from("sessions").select("*").eq("client_id", client.id).order("id", { ascending:false }),
      supabase.from("plans").select("id,name,exercises").eq("pt_id", pid).order("name"),
      supabase.from("appraisals").select("*").eq("client_id", client.id).order("id", { ascending:false }),
    ]);
    setUpcoming(up || []); setPast(pa || []); setPlans(pl || []); setAppraisals(ap || []); setLoading(false);
  };

  const saveAppraisal = async (draft) => {
    const row = { date: draft.date, data: draft };
    if (apprModal && apprModal.id) await supabase.from("appraisals").update(row).eq("id", apprModal.id);
    else await supabase.from("appraisals").insert({ pt_id: ptId, client_id: client.id, ...row });
    setApprModal(null); load();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createBooking = async () => {
    setErr("");
    if (!book.date) { setErr("Pick a date."); return; }
    if (!ptId) { setErr("Couldn't resolve this client's trainer."); return; }
    const plan = plans.find(p => String(p.id) === String(book.planId));
    const { error } = await supabase.from("upcoming_sessions").insert({ pt_id: ptId, client_id: client.id, name: plan?.name || book.name.trim() || "Session", date: book.date, time: book.time, status: "upcoming", plan_id: plan ? plan.id : null });
    if (error) { setErr(error.message); return; }
    setShowBook(false); setBook({ date:"", time:"19:00", name:"", planId:"" }); load();
  };

  const markDone = async (u) => {
    await supabase.from("sessions").insert({ pt_id: u.pt_id, client_id: u.client_id, name: u.name, date: u.date, time: u.time, status: "completed", plan_id: u.plan_id });
    await supabase.from("upcoming_sessions").delete().eq("id", u.id);
    load();
  };
  const cancelUpcoming = async (u) => { await supabase.from("upcoming_sessions").delete().eq("id", u.id); load(); };
  const saveNote = async (s) => {
    await supabase.from("sessions").update({ coach_notes: (noteDraft[s.id] ?? s.coach_notes ?? "").trim() }).eq("id", s.id);
    setNoteDraft(d => { const n = { ...d }; delete n[s.id]; return n; });
    load();
  };

  return (
    <div className="page-content" style={{ maxWidth: 900 }}>
      <button className="btn btn-ghost btn-sm" style={{ color:C.primary, marginBottom:20 }} onClick={()=>setView("clients")}>← Back to clients</button>
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:24 }}>
        <Avatar name={client.name} size={52} />
        <div>
          <h1 style={{ fontSize:26, fontWeight:700, letterSpacing:"-0.5px" }}>{client.name}</h1>
          <div style={{ fontSize:13, color:C.textMuted }}>@{client.username} · {client.email}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontWeight:700 }}>Session Overview</div>
          <button className="btn btn-primary btn-sm" onClick={()=>{ setShowBook(s=>!s); setErr(""); }}>+ Book Session</button>
        </div>
        {showBook && (
          <div className="card" style={{ background:C.primaryGlow, border:`1px solid ${C.primary}20`, marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:14 }}>Create Booking</div>
            <div className="grid-2-form">
              <div><label className="label">Date</label><input className="input" type="date" value={book.date} onChange={e=>setBook(b=>({...b,date:e.target.value}))} /></div>
              <div><label className="label">Time</label><input className="input" type="time" value={book.time} onChange={e=>setBook(b=>({...b,time:e.target.value}))} /></div>
            </div>
            <div style={{ marginBottom:14 }}><label className="label">Fitness plan (optional)</label>
              <select className="input" value={book.planId} onChange={e=>setBook(b=>({...b,planId:e.target.value}))}>
                <option value="">— none —</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {plans.length === 0 && <div style={{ fontSize:11.5, color:C.textMuted, marginTop:4 }}>No plans yet — create one under Plans.</div>}</div>
            <div style={{ marginBottom:14 }}><label className="label">Session focus (optional)</label><input className="input" placeholder="e.g. Upper body strength" value={book.name} onChange={e=>setBook(b=>({...b,name:e.target.value}))} /></div>
            {err && <div style={{ fontSize:12, color:C.danger, marginBottom:10 }}>{err}</div>}
            <div style={{ display:"flex", gap:10 }}>
              <button className="btn btn-outline btn-sm" onClick={()=>setShowBook(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={createBooking}>Create Booking</button>
            </div>
          </div>
        )}

        <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, margin:"4px 0 10px", textTransform:"uppercase", letterSpacing:"0.8px" }}>Upcoming</div>
        {loading ? <div style={{ color:C.textMuted, fontSize:13 }}>Loading…</div>
          : upcoming.length === 0 ? <div style={{ color:C.textMuted, fontSize:13, marginBottom:8 }}>No upcoming sessions.</div>
          : upcoming.map(u => (
            <div key={u.id} className="card" style={{ marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px" }}>
              <div><span style={{ fontWeight:500 }}>{u.date} — {u.name}</span><span style={{ fontSize:13, color:C.textMuted, marginLeft:8 }}>{u.time}</span></div>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-primary btn-sm" onClick={()=>markDone(u)}>Mark done</button>
                <button className="btn btn-ghost btn-sm" style={{ color:C.danger }} onClick={()=>cancelUpcoming(u)}>Cancel</button>
              </div>
            </div>
          ))}

        <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, margin:"18px 0 10px", textTransform:"uppercase", letterSpacing:"0.8px" }}>Completed sessions</div>
        {past.length === 0 ? <div style={{ color:C.textMuted, fontSize:13 }}>No completed sessions yet.</div>
          : past.map(s => (
            <div key={s.id} className="card" style={{ marginBottom:8, padding:"12px 16px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ fontWeight:500 }}>{s.date} — {s.name}</span>
                {s.client_rating ? <span className="badge badge-green">Client rated {s.client_rating}/5</span> : <span className="badge badge-amber">Awaiting client feedback</span>}
              </div>
              {s.client_comment && <div style={{ fontSize:13, color:C.textSub, marginBottom:8 }}>Client: “{s.client_comment}”</div>}
              <label className="label">Coach notes</label>
              <textarea className="input" rows={2} value={noteDraft[s.id] ?? s.coach_notes ?? ""} onChange={e=>setNoteDraft(d=>({...d,[s.id]:e.target.value}))} />
              <div style={{ display:"flex", justifyContent:"flex-end", marginTop:8 }}>
                {s.plan_id && <button className="btn btn-outline btn-sm" onClick={()=>setPerfSession(s)}>{Object.keys(parsePerf(s.performance)).length ? "Performance ✓" : "Log performance"}</button>}
                <button className="btn btn-outline btn-sm" onClick={()=>saveNote(s)}>Save notes</button>
              </div>
            </div>
          ))}
      </div>

      <div className="card">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontWeight:700 }}>Appraisals</div>
          <button className="btn btn-primary btn-sm" onClick={()=>setApprModal({})}>+ Record appraisal</button>
        </div>
        {appraisals.length === 0 ? <div style={{ color:C.textMuted, fontSize:13 }}>No appraisals recorded yet.</div>
          : appraisals.map(a => (
            <div key={a.id} className="card" style={{ marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px" }}>
              <span style={{ fontWeight:500 }}>{a.date || (a.created_at && new Date(a.created_at).toLocaleDateString("en-GB"))}</span>
              <button className="btn btn-ghost btn-sm" style={{ color:C.primary }} onClick={()=>setApprModal(a)}>Edit</button>
            </div>
          ))}
      </div>

      {apprModal && <AppraisalModal initial={apprModal.data || { date: new Date().toISOString().slice(0,10) }} onSave={saveAppraisal} onClose={()=>setApprModal(null)} />}
      {perfSession && <PerformanceEditor session={perfSession} exercises={exercisesForSession(perfSession)} onClose={()=>setPerfSession(null)} onSaved={()=>{ setPerfSession(null); load(); }} />}
    </div>
  );
}

function ClientDetail({ client, setView }) {
  const [tab, setTab] = useState("sessions");
  const [clientData, setClientData] = useState(client);
  const [sessions, setSessions] = useState(() => query("sessions", s => s.clientId === (client?.id)));
  const [upcoming, setUpcoming] = useState(() => query("upcomingSessions", s => s.clientId === (client?.id)));
  const [showBooking, setShowBooking] = useState(false);
  const [book, setBook] = useState({ date:"", time:"19:00", planType:"Muscular Development", plan:"Body Build — Upper Body Focus" });

  const createBooking = () => {
    if (!book.date) { setRegError(""); return; }
    const disp = book.date.split("-").reverse().join("/");
    add("upcomingSessions", { clientId: client.id, date: disp, time: book.time, name: book.plan, planType: book.planType });
    setUpcoming(query("upcomingSessions", s => s.clientId === client?.id));
    setBook({ date:"", time:"19:00", planType:"Muscular Development", plan:"Body Build — Upper Body Focus" });
    setShowBooking(false);
  };
  const [showRecord, setShowRecord] = useState(null);
  const [perfSession, setPerfSession] = useState(null); // demo per-set performance editor
  const [recordNotes, setRecordNotes] = useState("");  // PT feedback draft for the open record
  const [showPlan, setShowPlan] = useState(null);
  const [showRegEdit, setShowRegEdit] = useState(false);
  const [regDraft, setRegDraft] = useState({});
  const [regError, setRegError] = useState("");

  const openRegEdit = () => {
    setRegDraft({
      name:       clientData.name       || "",
      email:      clientData.email      || "",
      phone:      clientData.phone      || "",
      mobile:     clientData.mobile     || "",
      objectives: clientData.objectives || "",
      weight:     clientData.weight     || "",
      height:     clientData.height     || "",
    });
    setRegError("");
    setShowRegEdit(true);
  };

  const saveRegDetails = () => {
    setRegError("");
    if (!regDraft.name?.trim()) { setRegError("Name is required."); return; }
    const updated = update("clients", clientData.id, {
      name:       regDraft.name.trim(),
      email:      regDraft.email.trim(),
      phone:      regDraft.phone.trim(),
      mobile:     regDraft.mobile.trim(),
      objectives: regDraft.objectives.trim(),
      weight:     Number(regDraft.weight) || 0,
      height:     Number(regDraft.height) || 0,
    });
    setClientData(updated);
    setShowRegEdit(false);
  };

  const openRecord = (s) => { setRecordNotes(s.coachNotes || ""); setShowRecord(s); };
  const savePTFeedback = (sessionId) => {
    update("sessions", sessionId, { coachNotes: recordNotes.trim() });
    const refreshed = query("sessions", s => s.clientId === client?.id);
    setSessions(refreshed);
    setShowRecord(refreshed.find(s => s.id === sessionId) || null);
  };

  if (!client) return <div style={{ padding:40, color:C.textSub }}>No client selected.</div>;

  return (
    <div className="page-content">
      <button className="btn btn-ghost btn-sm" style={{color:C.primary, marginBottom:20}} onClick={()=>setView("clients")}>← Back to clients</button>

      <div className="grid-sidebar">
        {/* Sidebar */}
        <div>
          <div className="card" style={{marginBottom:16}}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
              <Avatar name={client.name} size={52} />
              <div>
                <div style={{ fontWeight:600, fontSize:16 }}>{client.name}</div>
                <span className={`badge ${client.status==="active"?"badge-green":"badge-amber"}`} style={{fontSize:11,marginTop:4}}>
                  {client.status==="active"?"Active":"Pending"}
                </span>
              </div>
            </div>
            <hr className="divider" />
            <div style={{ fontSize:13, lineHeight:2.2 }}>
              {[["Weight",`${client.weight} kg`],["Height",`${client.height} cm`]].map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{color:C.textMuted}}>{k}</span>
                  <strong style={{color:C.text}}>{v}</strong>
                </div>
              ))}
            </div>
          </div>
          <div className="card" style={{ background:C.primaryGlow, border:`1px solid ${C.primary}20` }}>
            <div style={{ fontWeight:600, fontSize:12, marginBottom:8, color:C.primary, letterSpacing:"0.5px", textTransform:"uppercase" }}>Objectives</div>
            <p style={{ fontSize:13, color:C.textSub, lineHeight:1.6 }}>{client.objectives}</p>
          </div>
          {getNextSession(client.id) && (
            <div className="card" style={{marginTop:16}}>
              <div style={{ fontSize:11, color:C.textMuted, marginBottom:4, textTransform:"uppercase", letterSpacing:"0.5px" }}>Next session</div>
              <div style={{ fontWeight:600, fontSize:14, color:C.primary }}>{getNextSession(client.id)}</div>
            </div>
          )}
        </div>

        {/* Main */}
        <div>
          <div className="tab-bar" style={{marginBottom:20}}>
            {[["sessions","Sessions"],["charts","Charts"],["account","Account"]].map(([t,l])=>(
              <button key={t} className={`tab-btn ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{l}</button>
            ))}
          </div>

          {tab==="sessions" && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                <span style={{fontWeight:600,fontSize:15}}>Session Overview</span>
                <button className="btn btn-primary btn-sm" onClick={()=>setShowBooking(true)}>+ Book Session</button>
              </div>

              {showBooking && (
                <div className="card" style={{ marginBottom:20, background:C.primaryGlow, border:`1px solid ${C.primary}20` }}>
                  <div style={{fontWeight:600,marginBottom:16}}>Create Booking</div>
                  <div className="grid-2-form">
                    <div><label className="label">Date</label><input className="input" type="date" value={book.date} onChange={e=>setBook(b=>({...b,date:e.target.value}))} /></div>
                    <div><label className="label">Time</label><input className="input" type="time" value={book.time} onChange={e=>setBook(b=>({...b,time:e.target.value}))} /></div>
                  </div>
                  <div style={{marginBottom:12}}><label className="label">Fitness Plan Type</label>
                    <select className="input" value={book.planType} onChange={e=>setBook(b=>({...b,planType:e.target.value}))}><option>Muscular Development</option><option>Cardio Fitness</option><option>Full Body Workout</option><option>Post-natal Recovery</option></select>
                  </div>
                  <div style={{marginBottom:18}}><label className="label">Fitness Plan</label>
                    <select className="input" value={book.plan} onChange={e=>setBook(b=>({...b,plan:e.target.value}))}><option>Body Build — Upper Body Focus</option><option>Cardio Burn — Interval Training</option></select>
                  </div>
                  {!book.date && <div style={{ fontSize:12, color:C.textMuted, marginBottom:10 }}>Pick a date to book.</div>}
                  <div style={{display:"flex",gap:10}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowBooking(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" style={{opacity: book.date?1:0.5, cursor: book.date?"pointer":"not-allowed"}} disabled={!book.date} onClick={createBooking}>Create Booking</button>
                  </div>
                </div>
              )}

              <div style={{marginBottom:18}}>
                <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.8px" }}>Upcoming</div>
                {upcoming.map((s,i)=>(
                  <div key={i} className="card" style={{ marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px" }}>
                    <div><span style={{fontWeight:500}}>{s.date} — {s.name}</span><span style={{fontSize:13,color:C.textMuted,marginLeft:8}}>{s.time}</span></div>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <span className="badge badge-blue">Upcoming</span>
                      {findPlanForSession(s.name) && (
                        <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>setShowPlan(findPlanForSession(s.name))}>Session Plan</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div style={{ fontSize:11, fontWeight:600, color:C.textMuted, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.8px" }}>Recent sessions</div>
                {sessions.map(s=>(
                  <div key={s.id} className="card" style={{ marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px" }}>
                    <span style={{fontWeight:500}}>Session {s.id} — {s.date} — {s.name}</span>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      {(() => { const b = sessionBadge(s); return <span className={`badge ${b.cls}`}>{b.label}</span>; })()}
                      <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>openRecord(s)}>View</button>
                      {findPlanForSession(s.name) && (
                        <button className="btn btn-ghost btn-sm" style={{color:C.textSub}} onClick={()=>setShowPlan(findPlanForSession(s.name))}>Session Plan</button>
                      )}
                      {findPlanForSession(s.name) && (
                        <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>setPerfSession(s)}>{Object.keys(parsePerf(s.performance)).length ? "Performance ✓" : "Log performance"}</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab==="charts" && <ClientChartsView client={client} />}

          {tab==="account" && (
            <div className="card">
              <div style={{fontWeight:600,marginBottom:18}}>Client Account Management</div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", background:C.surfaceAlt, borderRadius:10, marginBottom:16 }}>
                <div>
                  <div style={{fontWeight:500,fontSize:14}}>Account Status</div>
                  <div style={{fontSize:13,color:C.textSub,marginTop:2}}>Controls client access to myPTOnline</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <span className="badge badge-green">Active</span>
                  <button className="btn btn-outline btn-sm">Disable</button>
                </div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={openRegEdit}>Update registration details</button>
            </div>
          )}
        </div>
      </div>

      {showRecord && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{maxWidth:620}}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
              <span style={{fontWeight:600,fontSize:15}}>Session {showRecord.id} — {showRecord.date} — {showRecord.name}</span>
              <CloseBtn onClick={()=>setShowRecord(null)} />
            </div>
            {(() => { const plan = findPlanForSession(showRecord.name); return plan ? (
              <>
                <div style={{ marginBottom:12, padding:"10px 14px", background:C.primaryGlow, border:`1px solid ${C.primary}20`, borderRadius:8, fontSize:13, color:C.primary }}>
                  <div style={{fontSize:11,color:C.textMuted,marginBottom:3,textTransform:"uppercase",letterSpacing:"0.5px"}}>Plan: {plan.name}</div>
                  {plan.objective}
                </div>
                <div style={{overflowX:"auto"}}>
                  <table className="dark-table" style={{fontSize:13}}>
                    <thead><tr><th>Exercise</th><th>Sets</th><th>Reps</th><th>Kg</th><th>Rest (min)</th></tr></thead>
                    <tbody>
                      {(plan.exercises || []).map((e, i) => { const ex = normalizeEx(e); return (
                        <tr key={i}>
                          <td style={{fontWeight:500}}>{ex.name}</td>
                          <td style={{textAlign:"center",color:C.textSub}}>{ex.sets}</td>
                          <td style={{textAlign:"center",color:C.textSub}}>{ex.reps}</td>
                          <td style={{textAlign:"center",color:C.textSub}}>{ex.kg ?? "—"}</td>
                          <td style={{textAlign:"center",color:C.textSub}}>{ex.rest}</td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{fontSize:13,color:C.textMuted,marginBottom:12}}>No linked plan found for this session.</div>
            ); })()}
            <div className="grid-2-form" style={{ marginTop:16, marginBottom:0 }}>
              <div><label className="label">Your feedback (PT)</label>
                <textarea className="input" rows={3} value={recordNotes} onChange={e=>setRecordNotes(e.target.value)} placeholder="Leave feedback on this session for your client…" /></div>
              <div><label className="label">Client feedback</label>
                <div style={{ padding:"10px 14px", background:C.surfaceAlt, borderRadius:8, fontSize:13, color:C.textSub, minHeight:60 }}>
                  {showRecord.clientRating ? <div style={{ color:C.primary, fontSize:15, marginBottom:6 }}>{"★".repeat(showRecord.clientRating)}{"☆".repeat(5 - showRecord.clientRating)}</div> : null}
                  {showRecord.clientComment || <span style={{ color:C.textMuted }}>Your client hasn't left feedback yet.</span>}
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:16,alignItems:"center"}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setShowRecord(null)}>Close</button>
              <button className="btn btn-primary btn-sm" onClick={() => savePTFeedback(showRecord.id)}>Save feedback</button>
              {(() => { const b = sessionBadge(showRecord); return <span className={`badge ${b.cls}`} style={{ marginLeft:"auto" }}>{b.label}</span>; })()}
            </div>
          </div>
        </div>
      )}

      {showPlan && <PlanDetailModal plan={showPlan} onClose={()=>setShowPlan(null)} editable={true} />}
      {perfSession && <PerformanceEditor session={perfSession} demo exercises={(findPlanForSession(perfSession.name)?.exercises || []).map(normalizeEx)} onClose={()=>setPerfSession(null)} onSaved={()=>{ setPerfSession(null); setSessions(query("sessions", s => s.clientId === client?.id)); }} />}

      {showRegEdit && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 520 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 6 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>Update Registration Details</span>
              <CloseBtn onClick={() => setShowRegEdit(false)} />
            </div>
            <div style={{ fontSize:12, color:C.textMuted, marginBottom:18 }}>Changes are saved to the client's record immediately.</div>
            <div style={{ marginBottom:12 }}><label className="label">Full name</label>
              <input className="input" value={regDraft.name} onChange={e => setRegDraft(d=>({...d,name:e.target.value}))} /></div>
            <div style={{ marginBottom:12 }}><label className="label">Email</label>
              <input className="input" type="email" value={regDraft.email} onChange={e => setRegDraft(d=>({...d,email:e.target.value}))} /></div>
            <div className="grid-2-form">
              <div><label className="label">Phone</label>
                <input className="input" type="tel" placeholder="020  XXXX  XXXX" style={{letterSpacing:"0.12em"}} value={regDraft.phone} onChange={e => setRegDraft(d=>({...d,phone:e.target.value}))} /></div>
              <div><label className="label">Mobile</label>
                <input className="input" type="tel" placeholder="07XXX  XXXXXX" style={{letterSpacing:"0.12em"}} value={regDraft.mobile} onChange={e => setRegDraft(d=>({...d,mobile:e.target.value}))} /></div>
            </div>
            <div style={{ marginBottom:12 }}><label className="label">Objectives</label>
              <textarea className="input" rows={3} value={regDraft.objectives} onChange={e => setRegDraft(d=>({...d,objectives:e.target.value}))} /></div>
            <div className="grid-2-form" style={{ marginBottom:16 }}>
              <div><label className="label">Weight (kg)</label>
                <input className="input" type="number" value={regDraft.weight} onChange={e => setRegDraft(d=>({...d,weight:e.target.value}))} /></div>
              <div><label className="label">Height (cm)</label>
                <input className="input" type="number" value={regDraft.height} onChange={e => setRegDraft(d=>({...d,height:e.target.value}))} /></div>
            </div>
            {regError && <div style={{ fontSize:12, color:C.danger, marginBottom:12 }}>{regError}</div>}
            <div style={{ display:"flex", gap:10 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowRegEdit(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveRegDetails}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CLIENT APPRAISALS & CHARTS ────────────────────────────────────────────────
// Categories commonly used on fitness-appraisal norm tables (free-text allowed).
const APPRAISAL_CATEGORIES = ["Poor", "Below average", "Average", "Fair/good", "Good", "Above/ average above", "Excellent"];

// Section schema drives BOTH the capture form and the read-only detail view.
const APPRAISAL_SECTIONS = [
  { title: "Anthropometric Measurements", fields: [
    { key: "weight",         label: "Weight (kg)",            type: "number",   step: "0.1" },
    { key: "height",         label: "Height (m)",             type: "number",   step: "0.0001" },
    { key: "bmi",            label: "BMI",                    type: "computed", compute: bmiOf },
    { key: "anthroCategory", label: "Category",               type: "category" },
  ]},
  { title: "Resting Blood Pressure", fields: [
    { key: "systolic1",  label: "Systolic BP (mmHg) (1)",  type: "number" },
    { key: "diastolic1", label: "Diastolic BP (mmHg) (1)", type: "number" },
    { key: "systolic2",  label: "Systolic BP (mmHg) (2)",  type: "number" },
    { key: "diastolic2", label: "Diastolic BP (mmHg) (2)", type: "number" },
    { key: "systolic3",  label: "Systolic BP (mmHg) (3)",  type: "number" },
    { key: "diastolic3", label: "Diastolic BP (mmHg) (3)", type: "number" },
    { key: "bpCategory", label: "Category",                type: "category" },
  ]},
  { title: "Body composition", fields: [
    { key: "bodyFat",         label: "% Body fat", type: "number", step: "0.1" },
    { key: "bodyFatCategory", label: "Category",   type: "category" },
  ]},
  { title: "Range of Movement", fields: [
    { key: "sitReach1",  label: "Sit and Reach (1) cm", type: "number" },
    { key: "sitReach2",  label: "Sit and Reach (2) cm", type: "number" },
    { key: "sitReach3",  label: "Sit and Reach (3) cm", type: "number" },
    { key: "romCategory", label: "Category",            type: "category" },
  ]},
  { title: "Cardiovascular fitness", fields: [
    { key: "vo2",         label: "Fit test VO₂ (ml/kg/min)", type: "number", step: "0.1" },
    { key: "vo2Category", label: "Category",                      type: "category" },
  ]},
  { title: "Muscular fitness", fields: [
    { key: "pressUps",         label: "60 secs (no. of press ups)", type: "number" },
    { key: "muscularCategory", label: "Category",                   type: "category" },
  ]},
];

function bmiOf(a) {
  const w = parseFloat(a.weight), h = parseFloat(a.height);
  if (!w || !h) return "";
  return (w / (h * h)).toFixed(2);
}
function bmiBand(bmi) {
  const v = parseFloat(bmi);
  if (!v) return "";
  if (v < 18.5) return "Underweight";
  if (v < 25)   return "Healthy";
  if (v < 30)   return "Overweight";
  return "Obese";
}
const _parseDM   = d   => { const [dd, mm, yy] = (d || "").split("/");   return new Date(+yy || 0, (+mm || 1) - 1, +dd || 1); };
const _isoFromDM = d   => { const [dd, mm, yy] = (d || "").split("/");   return (yy && mm && dd) ? `${yy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}` : ""; };
const _dmFromISO = iso => { const [yy, mm, dd] = (iso || "").split("-"); return (yy && mm && dd) ? `${dd}/${mm}/${yy}` : ""; };

function emptyAppraisal() {
  return {
    date: new Date().toLocaleDateString("en-GB"),
    weight: "", height: "", anthroCategory: "",
    systolic1: "", diastolic1: "", systolic2: "", diastolic2: "", systolic3: "", diastolic3: "", bpCategory: "",
    bodyFat: "", bodyFatCategory: "",
    sitReach1: "", sitReach2: "", sitReach3: "", romCategory: "",
    vo2: "", vo2Category: "",
    pressUps: "", muscularCategory: "",
  };
}

// ── Chart primitives & metric config (shared by detail, card & explorer) ──────
const CHART_COLORS = { blue: "#60a5fa", green: "#34d399", purple: "#a78bfa", amber: "#f59e0b" };

const _num = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const _avg = (...xs) => { const n = xs.map(parseFloat).filter(v => !isNaN(v)); return n.length ? n.reduce((a, b) => a + b, 0) / n.length : 0; };
const _fmt = v => { const n = Math.round(v * 10) / 10; return Number.isInteger(n) ? String(n) : n.toFixed(1); };

// Metrics that can be trended across appraisals.
const APPRAISAL_METRICS = [
  { key: "bmi",      label: "BMI",             unit: "",   color: CHART_COLORS.blue,   goal: "down", base: 30, get: a => _num(bmiOf(a)) },
  { key: "bodyFat",  label: "Body fat",        unit: "%",  color: C.danger,            goal: "down", base: 40, get: a => _num(a.bodyFat) },
  { key: "vo2",      label: "VO₂ max",         unit: "",   color: CHART_COLORS.green,  goal: "up",   base: 50, get: a => _num(a.vo2) },
  { key: "pressUps", label: "Press-ups (60s)", unit: "",   color: C.primary,           goal: "up",   base: 60, get: a => _num(a.pressUps) },
  { key: "weight",   label: "Weight",          unit: "kg", color: CHART_COLORS.purple, goal: "down", base: 0,  get: a => _num(a.weight) },
  { key: "sitReach", label: "Sit & reach",     unit: "cm", color: CHART_COLORS.amber,  goal: "up",   base: 35, get: a => _avg(a.sitReach1, a.sitReach2, a.sitReach3) },
];

// Single-series vertical bar chart. bars: [{ label, value, color }]
function BarChart({ bars, height = 120, unit = "", showValues = true }) {
  const max = Math.max(1, ...bars.map(b => _num(b.value))) * 1.18;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-end", justifyContent: "space-around" }}>
      {bars.map((b, i) => {
        const v = _num(b.value);
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 22 }}>
            <div style={{ height, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center" }}>
              {showValues && <div style={{ fontSize: 10, fontWeight: 600, color: C.textSub, marginBottom: 3 }}>{v ? _fmt(v) + unit : "—"}</div>}
              <div style={{ width: 26, height: `${(v / max) * height}px`, minHeight: v ? 3 : 0, background: b.color || C.primary, borderRadius: "4px 4px 0 0", transition: "height .4s" }} title={`${b.label}: ${v}${unit}`} />
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6, textAlign: "center" }}>{b.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// Grouped vertical bars (e.g. blood-pressure readings). series: [{ name, color, values: [] }]
function GroupedBarChart({ groups, series, height = 120, unit = "", barW = 14 }) {
  const max = Math.max(1, ...series.flatMap(s => s.values.map(_num))) * 1.18;
  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", justifyContent: "space-around" }}>
        {groups.map((g, gi) => (
          <div key={gi} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ height, display: "flex", gap: 4, alignItems: "flex-end" }}>
              {series.map((s, si) => {
                const v = _num(s.values[gi]);
                return (
                  <div key={si} style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center" }}>
                    <div style={{ fontSize: 9, color: C.textSub, marginBottom: 2 }}>{v || "—"}</div>
                    <div style={{ width: barW, height: `${(v / max) * height}px`, minHeight: v ? 3 : 0, background: s.color, borderRadius: "3px 3px 0 0" }} title={`${s.name} ${g}: ${v}${unit}`} />
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6 }}>{g}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 12, justifyContent: "center", flexWrap: "wrap" }}>
        {series.map(s => (
          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textSub }}>
            <div style={{ width: 11, height: 11, background: s.color, borderRadius: 2 }} /> {s.name}
          </div>
        ))}
      </div>
    </div>
  );
}

// Trend of one metric across appraisals (chronological). Latest bar is highlighted.
function MetricTrendChart({ chrono, metric, height = 120 }) {
  const pts = chrono.map(a => ({ date: a.date, value: metric.get(a) }));
  const max = Math.max(metric.base || 1, ...pts.map(p => p.value)) * 1.18;
  const first = pts[0]?.value, last = pts[pts.length - 1]?.value;
  const delta = last - first;
  const improved = metric.goal === "down" ? delta < 0 : delta > 0;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{metric.label}</div>
        {pts.length > 1 && Math.abs(delta) > 0.01 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: improved ? C.green : C.danger }}>
            {delta > 0 ? "+" : ""}{_fmt(delta)}{metric.unit} {improved ? "▲" : "▼"}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", justifyContent: "space-around" }}>
        {pts.map((p, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 20 }}>
            <div style={{ height, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: i === pts.length - 1 ? metric.color : C.textSub, marginBottom: 3 }}>{p.value ? _fmt(p.value) : "—"}</div>
              <div style={{ width: 22, height: `${(p.value / max) * height}px`, minHeight: p.value ? 3 : 0, background: metric.color, opacity: i === pts.length - 1 ? 1 : 0.5, borderRadius: "4px 4px 0 0", transition: "height .4s" }} title={`${p.date}: ${p.value}${metric.unit}`} />
            </div>
            <div style={{ fontSize: 9, color: C.textMuted, marginTop: 6 }}>{p.date.slice(0, 5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// All charts for a SINGLE appraisal — used in the detail modal & on the card preview.
function AppraisalCharts({ appraisal, compact = false }) {
  const a = appraisal;
  const h = compact ? 90 : 120;
  const keyMetrics = APPRAISAL_METRICS.filter(m => ["bmi", "bodyFat", "vo2", "pressUps"].includes(m.key));
  const hasBP = [a.systolic1, a.systolic2, a.systolic3].some(v => _num(v) > 0);
  const hasROM = [a.sitReach1, a.sitReach2, a.sitReach3].some(v => _num(v) > 0);
  const panel = { padding: "14px 16px", margin: 0 };
  const head = { fontSize: 12, fontWeight: 600, color: C.primary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 };
  return (
    <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: 14 }}>
      <div className="card" style={panel}>
        <div style={head}>Key metrics</div>
        {keyMetrics.map(m => {
          const v = m.get(a); const max = m.base || Math.max(v, 1);
          return (
            <div key={m.key} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: C.textSub }}>{m.label}</span>
                <span style={{ color: m.color, fontWeight: 700 }}>{v ? _fmt(v) + m.unit : "—"}</span>
              </div>
              <div style={{ height: 8, background: C.surfaceAlt, borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(100, (v / max) * 100)}%`, height: "100%", background: m.color, borderRadius: 4, transition: "width .4s" }} />
              </div>
            </div>
          );
        })}
      </div>
      {hasBP && (
        <div className="card" style={panel}>
          <div style={head}>Resting blood pressure</div>
          <GroupedBarChart groups={["1", "2", "3"]} height={h} unit=" mmHg"
            series={[
              { name: "Systolic",  color: C.danger,          values: [a.systolic1, a.systolic2, a.systolic3] },
              { name: "Diastolic", color: CHART_COLORS.blue, values: [a.diastolic1, a.diastolic2, a.diastolic3] },
            ]} />
        </div>
      )}
      {hasROM && (
        <div className="card" style={panel}>
          <div style={head}>Range of movement — sit &amp; reach</div>
          <BarChart height={h} unit=" cm"
            bars={[
              { label: "Reach 1", value: a.sitReach1, color: CHART_COLORS.amber },
              { label: "Reach 2", value: a.sitReach2, color: CHART_COLORS.amber },
              { label: "Reach 3", value: a.sitReach3, color: CHART_COLORS.amber },
            ]} />
        </div>
      )}
    </div>
  );
}

// Full charts explorer — opened by clicking the progress chart. Select any metric
// trend, view all trends at once, or drill into a single appraisal's charts.
function ChartsExplorerModal({ appraisals, onClose, onOpenAppraisal }) {
  const chrono = [...appraisals].sort((a, b) => _parseDM(a.date) - _parseDM(b.date));
  const [tab, setTab] = useState("trends");           // "trends" | "appraisal"
  const [metricKey, setMetricKey] = useState("all");  // "all" | metric.key | "bp"
  const [apprId, setApprId] = useState(chrono[chrono.length - 1]?.id);

  const bpSeries = [
    { name: "Systolic (avg)",  color: C.danger,          values: chrono.map(a => Math.round(_avg(a.systolic1, a.systolic2, a.systolic3))) },
    { name: "Diastolic (avg)", color: CHART_COLORS.blue, values: chrono.map(a => Math.round(_avg(a.diastolic1, a.diastolic2, a.diastolic3))) },
  ];
  const selected = chrono.find(a => a.id === apprId) || chrono[chrono.length - 1];
  const trendPills = [{ key: "all", label: "All charts" }, ...APPRAISAL_METRICS.map(m => ({ key: m.key, label: m.label })), { key: "bp", label: "Blood pressure" }];
  const pill = (active) => ({ background: active ? C.primary : C.surfaceAlt, color: active ? "#0c0c0e" : C.textSub, border: "none", fontWeight: 600 });

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 820, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontWeight: 700, fontSize: 17 }}>Progress charts</span>
          <CloseBtn onClick={onClose} />
        </div>

        <div className="tab-bar" style={{ marginBottom: 16 }}>
          {[["trends", "Trends over time"], ["appraisal", "By appraisal"]].map(([t, l]) => (
            <button key={t} className={`tab-btn ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{l}</button>
          ))}
        </div>

        {tab === "trends" && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {trendPills.map(p => (
                <button key={p.key} className="btn btn-sm" style={pill(metricKey === p.key)} onClick={() => setMetricKey(p.key)}>{p.label}</button>
              ))}
            </div>

            {metricKey === "all" ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {APPRAISAL_METRICS.map(m => (
                  <div key={m.key} className="card" style={{ padding: "16px 18px", margin: 0 }}>
                    <MetricTrendChart chrono={chrono} metric={m} height={110} />
                  </div>
                ))}
                <div className="card" style={{ padding: "16px 18px", margin: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Resting blood pressure</div>
                  <GroupedBarChart groups={chrono.map(a => a.date.slice(0, 5))} series={bpSeries} height={110} unit=" mmHg" barW={10} />
                </div>
              </div>
            ) : metricKey === "bp" ? (
              <div className="card" style={{ padding: "18px 20px", margin: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Resting blood pressure — average per appraisal</div>
                <GroupedBarChart groups={chrono.map(a => a.date.slice(0, 5))} series={bpSeries} height={160} unit=" mmHg" />
              </div>
            ) : (
              <div className="card" style={{ padding: "18px 20px", margin: 0 }}>
                <MetricTrendChart chrono={chrono} metric={APPRAISAL_METRICS.find(m => m.key === metricKey)} height={180} />
              </div>
            )}
          </>
        )}

        {tab === "appraisal" && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {chrono.map(a => (
                <button key={a.id} className="btn btn-sm" style={pill(apprId === a.id)} onClick={() => setApprId(a.id)}>{a.date}</button>
              ))}
            </div>
            {selected && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontWeight: 600 }}>Appraisal — {selected.date}</div>
                  {onOpenAppraisal && <button className="btn btn-ghost btn-sm" style={{ color: C.primary }} onClick={() => onOpenAppraisal(selected)}>Full breakdown →</button>}
                </div>
                <AppraisalCharts appraisal={selected} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Appraisal capture flow (modal form) ───────────────────────────────────────
function AppraisalModal({ initial, onSave, onClose }) {
  const [draft, setDraft] = useState(initial);
  const upd = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 700, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{draft.id ? "Edit Appraisal" : "New Fitness Appraisal"}</span>
          <CloseBtn onClick={onClose} />
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 18 }}>Capture the client's physical assessment results. BMI is calculated automatically from weight and height.</div>

        <div style={{ marginBottom: 16 }}>
          <label className="label">Appraisal date</label>
          <input className="input" type="date" style={{ maxWidth: 220 }}
            value={_isoFromDM(draft.date)}
            onChange={e => upd("date", _dmFromISO(e.target.value))} />
        </div>

        {APPRAISAL_SECTIONS.map(sec => (
          <div key={sec.title} className="card" style={{ marginBottom: 14, padding: "16px 18px" }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 14, color: C.primary, textTransform: "uppercase", letterSpacing: "0.6px" }}>{sec.title}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              {sec.fields.map(f => {
                if (f.type === "computed") {
                  const val = f.compute(draft);
                  return (
                    <div key={f.key}>
                      <label className="label">{f.label}</label>
                      <div className="input" style={{ background: C.surfaceAlt, color: val ? C.primary : C.textMuted, fontWeight: 600, display: "flex", alignItems: "center" }}>
                        {val || "—"}{f.key === "bmi" && val ? <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.textSub }}>{bmiBand(val)}</span> : null}
                      </div>
                    </div>
                  );
                }
                if (f.type === "category") {
                  return (
                    <div key={f.key} style={{ gridColumn: "1 / -1" }}>
                      <label className="label">{f.label}</label>
                      <input className="input" list="appraisal-cats" placeholder="e.g. Average" value={draft[f.key] ?? ""} onChange={e => upd(f.key, e.target.value)} />
                    </div>
                  );
                }
                return (
                  <div key={f.key}>
                    <label className="label">{f.label}</label>
                    <input className="input" type="number" step={f.step || "any"} value={draft[f.key] ?? ""} onChange={e => upd(f.key, e.target.value)} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <datalist id="appraisal-cats">{APPRAISAL_CATEGORIES.map(o => <option key={o} value={o} />)}</datalist>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={() => onSave(draft)}>{draft.id ? "Save Changes" : "Save Appraisal"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Appraisal read-only detail (matches the printed assessment layout) ────────
function AppraisalDetailModal({ appraisal, onClose, onEdit }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Appraisal — {appraisal.date}</span>
          <CloseBtn onClick={onClose} />
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 18 }}>Standard fitness assessment</div>

        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Visual summary</div>
        <div style={{ marginBottom: 22 }}>
          <AppraisalCharts appraisal={appraisal} />
        </div>

        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Detailed readings</div>
        {APPRAISAL_SECTIONS.map(sec => (
          <div key={sec.title} style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{sec.title}</div>
            <table className="dark-table" style={{ fontSize: 13 }}>
              <tbody>
                {sec.fields.map(f => {
                  const val = f.type === "computed" ? f.compute(appraisal) : appraisal[f.key];
                  const has = !(val === "" || val == null);
                  return (
                    <tr key={f.key}>
                      <td style={{ color: C.textMuted, width: "58%" }}>{f.label}</td>
                      <td style={{ fontWeight: f.type === "category" ? 500 : 600, color: !has ? C.textMuted : (f.type === "category" ? C.textSub : C.primary) }}>{has ? val : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
          {onEdit && <button className="btn btn-primary btn-sm" onClick={onEdit}>✏️ Edit</button>}
        </div>
      </div>
    </div>
  );
}

function ClientChartsView({ client }) {
  // Match by id, or by name as a fallback (covers Supabase-synced clients whose
  // id may differ from the seed but whose name still resolves to the same person).
  const belongs = (a) => a.clientId === client?.id || (a.clientName && client?.name && a.clientName === client.name);
  const load = () => query("appraisals", belongs).sort((a, b) => _parseDM(b.date) - _parseDM(a.date));
  const [appraisals, setAppraisals] = useState(load);
  const [formInitial, setFormInitial] = useState(null);  // non-null → capture modal open
  const [detail, setDetail] = useState(null);            // non-null → detail modal open
  const [explorer, setExplorer] = useState(false);       // true → full charts explorer

  const refresh = () => setAppraisals(load());
  const handleSave = (draft) => {
    if (draft.id) update("appraisals", draft.id, draft);
    else          add("appraisals", { ...draft, clientId: client.id, clientName: client.name });
    refresh();
    setFormInitial(null);
    setDetail(null);
  };

  const latest = appraisals[0];
  const chrono = [...appraisals].sort((a, b) => _parseDM(a.date) - _parseDM(b.date));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>Appraisals & Progress</span>
        <button className="btn btn-primary btn-sm" onClick={() => setFormInitial(emptyAppraisal())}>+ New Appraisal</button>
      </div>

      {latest && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <div style={{ fontWeight: 600 }}>Latest appraisal</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>{latest.date}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {[
              { label: "BMI",        value: bmiOf(latest) || "—",                       sub: bmiBand(bmiOf(latest)) },
              { label: "Body fat",   value: latest.bodyFat ? `${latest.bodyFat}%` : "—", sub: latest.bodyFatCategory },
              { label: "VO₂ max", value: latest.vo2 || "—",                         sub: latest.vo2Category },
              { label: "Press-ups",  value: latest.pressUps || "—",                      sub: latest.muscularCategory },
            ].map(k => (
              <div key={k.label} style={{ background: C.surfaceAlt, borderRadius: 10, padding: "14px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.primary }}>{k.value}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{k.label}</div>
                {k.sub ? <div style={{ fontSize: 10, color: C.textSub, marginTop: 4 }}>{k.sub}</div> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Appraisal history</div>
        {appraisals.length === 0 ? (
          <div style={{ textAlign: "center", padding: "26px 12px" }}>
            <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 14 }}>No appraisals recorded yet for {client.name.split(" ")[0]}.</div>
            <button className="btn btn-outline btn-sm" onClick={() => setFormInitial(emptyAppraisal())}>Record first appraisal</button>
          </div>
        ) : (
          appraisals.map(a => (
            <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: C.surfaceAlt, borderRadius: 10, marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.date}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                  BMI {bmiOf(a) || "—"} · Body fat {a.bodyFat ? a.bodyFat + "%" : "—"} · VO{"₂"} {a.vo2 || "—"}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" style={{ color: C.primary }} onClick={() => setDetail(a)}>View →</button>
            </div>
          ))
        )}
      </div>

      {chrono.length > 0 && latest && (
        <div className="card" onClick={() => setExplorer(true)}
          style={{ cursor: "pointer", transition: "border-color .15s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = ""; }}
          title="Open progress charts">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <div style={{ fontWeight: 600 }}>Appraisal Progress Chart</div>
            <span className="btn btn-ghost btn-sm" style={{ color: C.primary, pointerEvents: "none" }}>View all charts →</span>
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 18 }}>
            Latest appraisal · {latest.date}{chrono.length > 1 ? ` — tap to explore ${chrono.length} appraisals & all metric trends` : " — tap to explore all charts"}
          </div>
          <AppraisalCharts appraisal={latest} />
        </div>
      )}

      {formInitial && <AppraisalModal initial={formInitial} onSave={handleSave} onClose={() => setFormInitial(null)} />}
      {detail && <AppraisalDetailModal appraisal={detail} onClose={() => setDetail(null)} onEdit={() => { setFormInitial(detail); setDetail(null); }} />}
      {explorer && <ChartsExplorerModal appraisals={appraisals} onClose={() => setExplorer(false)} onOpenAppraisal={(a) => { setExplorer(false); setDetail(a); }} />}
    </div>
  );
}

// ── PLAN DETAIL MODAL ─────────────────────────────────────────────────────────
function PlanDetailModal({ plan: initialPlan, onClose, editable = false }) {
  const [editing, setEditing] = useState(false);
  const [livePlan, setLivePlan] = useState(initialPlan);
  const [draft, setDraft] = useState(null);
  const [exSearch, setExSearch] = useState("");
  const [editError, setEditError] = useState("");
  const [viewExercise, setViewExercise] = useState(null); // exercise detail drill-down

  if (!livePlan) return null;

  const allExercises = getAll("exercises");
  const libraryExercises = allExercises.filter(e => e.inLibrary === true);

  const openExercise = (name) => {
    const found = allExercises.find(e => e.name.toLowerCase() === name.toLowerCase());
    setViewExercise(found || { name, _notFound: true });
  };

  const openEdit = () => {
    setDraft({ ...livePlan, exercises: (livePlan.exercises || []).map(normalizeEx) });
    setExSearch(""); setEditError(""); setEditing(true);
  };

  const addToEdit = (name) => {
    if (draft.exercises.some(e => e.name === name)) return;
    setDraft(d => ({ ...d, exercises: [...d.exercises, { name, sets:3, reps:12, kg:defaultKgForExercise(name), rest:1 }] }));
  };

  const removeFromEdit = (name) => {
    setDraft(d => ({ ...d, exercises: d.exercises.filter(e => e.name !== name) }));
  };

  const updateExField = (name, field, value) => {
    setDraft(d => ({ ...d, exercises: d.exercises.map(e => e.name === name ? { ...e, [field]: Number(value) } : e) }));
  };

  const saveEdit = () => {
    if (!draft.name?.trim()) { setEditError("Plan name is required."); return; }
    const saved = update("plans", draft.id, {
      name: draft.name.trim(), objective: draft.objective,
      type: draft.type, exercises: draft.exercises,
    });
    setLivePlan(saved || { ...draft, name: draft.name.trim() });
    setEditing(false);
  };

  const availableToAdd = libraryExercises
    .filter(e => !draft?.exercises.some(d => d.name === e.name))
    .filter(e => !exSearch || e.name.toLowerCase().includes(exSearch.toLowerCase()));

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: editing ? 800 : 580, transition:"max-width 0.2s" }}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <span style={{ fontWeight:700, fontSize:16 }}>
            {editing ? "Edit Plan" : livePlan.name}
          </span>
          <CloseBtn onClick={onClose} />
        </div>

        {/* ── EXERCISE DETAIL DRILL-DOWN ── */}
        {!editing && viewExercise && (<>
          <button onClick={()=>setViewExercise(null)} style={{ background:"none", border:"none", color:C.primary, fontSize:13, cursor:"pointer", padding:0, marginBottom:16, display:"flex", alignItems:"center", gap:6 }}>← Back to plan</button>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
            <div>
              <div style={{ fontWeight:700, fontSize:18, marginBottom:4 }}>{viewExercise.name}</div>
              {viewExercise.muscle && <div style={{ fontSize:13, color:C.textMuted }}>{viewExercise.bodyArea} · {viewExercise.muscle}</div>}
            </div>
            {viewExercise.muscle && <span className="badge badge-lime">{viewExercise.bodyArea}</span>}
          </div>
          {viewExercise._notFound ? (
            <div style={{ fontSize:13, color:C.textMuted, padding:"16px 0" }}>No detailed information available for this exercise.</div>
          ) : (<>
            {viewExercise.description && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:C.textMuted, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.5px" }}>Description</div>
                <p style={{ fontSize:13, color:C.textSub, lineHeight:1.7 }}>{viewExercise.description}</p>
              </div>
            )}
            {viewExercise.teachingPoints && (
              <div style={{ marginBottom:14, padding:"10px 14px", background:C.primaryGlow, border:`1px solid ${C.primary}20`, borderRadius:8 }}>
                <div style={{ fontSize:11, color:C.textMuted, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.5px" }}>Teaching Points</div>
                <p style={{ fontSize:13, color:C.primary, lineHeight:1.7 }}>{viewExercise.teachingPoints}</p>
              </div>
            )}
            {viewExercise.adaptations && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:C.textMuted, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.5px" }}>Adaptations</div>
                <p style={{ fontSize:13, color:C.textSub, lineHeight:1.7 }}>{viewExercise.adaptations}</p>
              </div>
            )}
            {viewExercise.alternatives?.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:C.textMuted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Alternatives</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {viewExercise.alternatives.map(a => <span key={a} style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, padding:"3px 10px", borderRadius:6, fontSize:12, color:C.textSub }}>{a}</span>)}
                </div>
              </div>
            )}
            {viewExercise.videoUrl && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:C.textMuted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Video</div>
                <VideoFrame url={viewExercise.videoUrl} title={viewExercise.name} />
              </div>
            )}
          </>)}
          <div style={{ marginTop:18 }}>
            <button className="btn btn-outline btn-sm" onClick={()=>setViewExercise(null)}>← Back to plan</button>
          </div>
        </>)}

        {/* ── VIEW MODE ── */}
        {!editing && !viewExercise && (<>
          <span className="badge badge-lime" style={{ marginBottom:14, display:"inline-block" }}>{livePlan.type}</span>
          <div style={{ marginBottom:16, padding:"10px 14px", background:C.primaryGlow, border:`1px solid ${C.primary}20`, borderRadius:8, fontSize:13, color:C.primary }}>
            <div style={{ fontSize:11, color:C.textMuted, marginBottom:4, textTransform:"uppercase", letterSpacing:"0.5px" }}>Objective</div>
            {livePlan.objective}
          </div>
          <div style={{ fontWeight:600, marginBottom:10 }}>Session Plan</div>
          <table className="dark-table" style={{ fontSize:13 }}>
            <thead><tr><th>Exercise</th><th>Sets</th><th>Reps</th><th>Kg</th><th>Rest (min)</th></tr></thead>
            <tbody>
              {(livePlan.exercises || []).map((e, i) => {
                const ex = normalizeEx(e);
                return (
                  <tr key={i} style={{ cursor:"pointer" }}
                    onMouseEnter={el=>el.currentTarget.style.background=C.surfaceAlt}
                    onMouseLeave={el=>el.currentTarget.style.background=""}
                    onClick={()=>openExercise(ex.name)}>
                    <td style={{ fontWeight:500, color:C.primary }}>{ex.name} <span style={{fontSize:11,color:C.textMuted}}>→</span></td>
                    <td style={{ color:C.textSub, textAlign:"center" }}>{ex.sets}</td>
                    <td style={{ color:C.textSub, textAlign:"center" }}>{ex.reps}</td>
                    <td style={{ color:C.textSub, textAlign:"center" }}>{ex.kg ?? "—"}</td>
                    <td style={{ color:C.textSub, textAlign:"center" }}>{ex.rest}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize:11, color:C.textMuted, marginTop:8 }}>Click an exercise to view details</div>
          <div style={{ display:"flex", gap:10, marginTop:14 }}>
            <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
            {editable && <button className="btn btn-primary btn-sm" onClick={openEdit}>Edit Plan</button>}
          </div>
        </>)}

        {/* ── EDIT MODE ── */}
        {editing && draft && (<>
          <div style={{ marginBottom:12 }}><label className="label">Plan name</label>
            <input className="input" value={draft.name} onChange={e=>setDraft(d=>({...d,name:e.target.value}))} /></div>
          <div className="grid-2-form">
            <div><label className="label">Type</label>
              <select className="input" value={draft.type} onChange={e=>setDraft(d=>({...d,type:e.target.value}))}>
                {["Muscular Development","Cardio Fitness","Full Body Workout","Flexibility","Custom"].map(t=><option key={t}>{t}</option>)}
              </select></div>
          </div>
          <div style={{ marginBottom:18 }}><label className="label">Objective</label>
            <textarea className="input" rows={2} value={draft.objective} onChange={e=>setDraft(d=>({...d,objective:e.target.value}))} /></div>

          <div className="grid-plan-edit" style={{ display:"grid", gridTemplateColumns:"3fr 2fr", gap:16 }}>
            {/* Current exercises */}
            <div>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>In this plan <span style={{color:C.textMuted,fontWeight:400}}>({draft.exercises.length})</span></div>
              {draft.exercises.length > 0 && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 44px 44px 44px 44px 28px", gap:4, padding:"0 10px", marginBottom:4 }}>
                  {["Exercise","Sets","Reps","Kg","Rest",""].map((h,i)=>(
                    <div key={i} style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:i>0?"center":"left"}}>{h}</div>
                  ))}
                </div>
              )}
              <div style={{ maxHeight:260, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }}>
                {draft.exercises.length === 0 && <div style={{fontSize:13,color:C.textMuted}}>No exercises added.</div>}
                {draft.exercises.map(ex => (
                  <div key={ex.name} style={{ display:"grid", gridTemplateColumns:"1fr 44px 44px 44px 44px 28px", gap:4, alignItems:"center", padding:"6px 10px", background:C.surfaceAlt, borderRadius:8, border:`1px solid ${C.border}` }}>
                    <span style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={ex.name}>{ex.name}</span>
                    {["sets","reps","kg","rest"].map(field => (
                      <input key={field} type="number" min={0} value={ex[field]} onChange={e=>updateExField(ex.name,field,e.target.value)}
                        style={{width:"100%",padding:"3px 4px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,color:C.text,textAlign:"center"}} />
                    ))}
                    <button onClick={()=>removeFromEdit(ex.name)} style={{background:"none",border:"none",color:C.danger,fontSize:16,cursor:"pointer",lineHeight:1,textAlign:"center"}}>×</button>
                  </div>
                ))}
              </div>
            </div>
            {/* Library picker */}
            <div>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>Add from library</div>
              <input className="input" placeholder="Search exercises…" value={exSearch} onChange={e=>setExSearch(e.target.value)} style={{marginBottom:8}} />
              <div style={{ maxHeight:240, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }}>
                {availableToAdd.length === 0 && <div style={{fontSize:13,color:C.textMuted}}>All library exercises added.</div>}
                {availableToAdd.map(ex => (
                  <div key={ex.id} onClick={()=>addToEdit(ex.name)}
                    style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", background:C.surface, borderRadius:8, border:`1px solid ${C.border}`, cursor:"pointer" }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=C.primary}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                    <div>
                      <div style={{fontSize:13,fontWeight:500}}>{ex.name}</div>
                      {ex.muscle && <div style={{fontSize:11,color:C.textMuted}}>{ex.muscle}</div>}
                    </div>
                    <span style={{color:C.primary,fontSize:18,fontWeight:700,lineHeight:1}}>+</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {editError && <div style={{fontSize:12,color:C.danger,marginTop:10}}>{editError}</div>}
          <div style={{ display:"flex", gap:10, marginTop:20 }}>
            <button className="btn btn-outline btn-sm" onClick={()=>setEditing(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveEdit}>Save Plan</button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ── FITNESS PLANS ─────────────────────────────────────────────────────────────
// Normalise an exercise entry — handles legacy string entries and new objects
const DEFAULT_KG_BY_EXERCISE = {
  "treadmill warm up": 0,
  "tricep stretch": 0,
  "recumbent cycle warm up": 0,
  "treadmill intervals": 0,
  "rowing machine": 0,
  "cross trainer": 0,
  "cool down stretch": 0,
  "warm up walk": 0,
  "planks": 0,
  "chest press": 30,
  "tricep pulley": 15,
  "bicep curl": 8,
  "shoulder press": 12,
  "squats": 20,
  "deadlifts": 40,
  "bench press": 30,
  "lat pulldown": 25,
};

function defaultKgForExercise(name) {
  const key = (name || "").trim().toLowerCase();
  if (key in DEFAULT_KG_BY_EXERCISE) return DEFAULT_KG_BY_EXERCISE[key];
  if (/warm|stretch|treadmill|cycle|bike|row|cross trainer|plank|cardio|cool down/.test(key)) return 0;
  return 10;
}

function normalizeEx(e) {
  if (typeof e === "string") return { name: e, sets: 3, reps: 12, kg: defaultKgForExercise(e), rest: 1 };
  const name = e?.name || "";
  return {
    ...e,
    sets: e?.sets ?? 3,
    reps: e?.reps ?? 12,
    kg: e?.kg ?? defaultKgForExercise(name),
    rest: e?.rest ?? 1,
  };
}

function FitnessPlans({ user }) {
  const PT_ID = 1;
  const PT_NAME = "Percy Trena";
  const isDemo = typeof user?.id !== "number";
  const ptId = isDemo ? PT_ID : user.id;
  const [plans, setPlans] = useState(() => isDemo ? getAll("plans") : []);
  const [activeTab, setActiveTab] = useState("mine"); // "mine" | "community"
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [exercises, setExercises] = useState([]);  // exercises in the new plan being created
  const [createDraft, setCreateDraft] = useState({ name: "", type: "Muscular Development", objective: "" });
  const [createExSearch, setCreateExSearch] = useState("");
  const [createError, setCreateError] = useState("");
  const [planToast, setPlanToast] = useState("");

  // Community state
  const [communityPlans, setCommunityPlans] = useState(() => getCommunityPlans());
  const [communitySearch, setCommunitySearch] = useState("");
  const [sharedPlanIds, setSharedPlanIds] = useState(() => new Set(getCommunityPlans().filter(p => p.sharedBy === PT_NAME).map(p => p.communityPlanId)));

  // Edit state
  const [showEdit, setShowEdit] = useState(false);
  const [editDraft, setEditDraft] = useState(null);   // { ...plan, exercises: [{name,sets,reps,kg,rest}] }
  const [exSearch, setExSearch] = useState("");
  const [editError, setEditError] = useState("");

  // All library exercises for this PT
  const [libraryExercises, setLibraryExercises] = useState(() => isDemo ? getAll("exercises").filter(e => e.ptId === PT_ID && e.inLibrary === true) : []);

  useEffect(() => {
    if (isDemo) return;
    (async () => {
      const [{ data: pl }, { data: ex }, { data: refs }] = await Promise.all([
        supabase.from("plans").select("*").eq("pt_id", ptId).order("id"),
        fetchAllRows(() => supabase.from("exercises").select("id,name,muscle,body_area,platform_exercise_id").eq("pt_id", ptId).eq("in_library", true).order("name").order("id")),
        // Linked platform exercises are pickable too — a plan stores a name snapshot either way.
        fetchAllRows(() => supabase.from("pt_exercise_refs").select("exercise_id, exercises(id,name,muscle,body_area)").eq("pt_id", ptId).order("exercise_id")),
      ]);
      setPlans(pl || []);
      const toPick = e => ({ id:e.id, name:e.name, muscle:e.muscle, bodyArea:e.body_area, inLibrary:true });
      const own = (ex || []).map(e => ({ ...toPick(e), platformExerciseId: e.platform_exercise_id }));
      setLibraryExercises([...own, ...linkedExercises(own, refs, toPick)].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const types = ["All","Muscular Development","Cardio Fitness","Full Body"];
  const filtered = filter==="All" ? plans : plans.filter(p=>p.type===filter);

  const refreshPlans = async () => {
    if (isDemo) { setPlans(getAll("plans")); return; }
    const { data } = await supabase.from("plans").select("*").eq("pt_id", ptId).order("id");
    setPlans(data || []);
  };

  const showToast = (msg) => { setPlanToast(msg); setTimeout(() => setPlanToast(""), 2500); };

  const handleShare = (plan) => {
    const ok = sharePlanToCommunity(plan, PT_NAME);
    if (ok) {
      setSharedPlanIds(prev => new Set([...prev, plan.id]));
      setCommunityPlans(getCommunityPlans());
      showToast(`"${plan.name}" shared to community!`);
    } else {
      showToast("This plan is already shared.");
    }
  };

  const handleUnshare = (plan) => {
    unsharePlan(plan.id);
    setSharedPlanIds(prev => { const s = new Set(prev); s.delete(plan.id); return s; });
    setCommunityPlans(getCommunityPlans());
    showToast(`"${plan.name}" removed from community.`);
  };

  const handleCommunityAdd = (communityPlan) => {
    try {
      const plan = createFitnessPlan(PT_ID, communityPlan.name + " (copy)", communityPlan.objective || "", communityPlan.type || "Muscular Development");
      update("plans", plan.id, { exercises: communityPlan.exercises || [] });
      refreshPlans();
      showToast(`"${communityPlan.name}" added to your plans!`);
    } catch (e) { showToast(e.message); }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === "community") setCommunityPlans(getCommunityPlans());
  };

  const filteredCommunity = communityPlans.filter(p =>
    (p.name || "").toLowerCase().includes(communitySearch.toLowerCase())
  );

  const openEdit = (plan) => {
    setEditDraft({ ...plan, exercises: (plan.exercises || []).map(normalizeEx) });
    setExSearch("");
    setEditError("");
    setSelected(null);
    setShowEdit(true);
  };

  const addToEdit = (name) => {
    if (editDraft.exercises.some(e => e.name === name)) return;
    setEditDraft(d => ({ ...d, exercises: [...d.exercises, { name, sets: 3, reps: 12, kg: defaultKgForExercise(name), rest: 1 }] }));
  };

  const removeFromEdit = (name) => {
    setEditDraft(d => ({ ...d, exercises: d.exercises.filter(e => e.name !== name) }));
  };

  const updateExField = (name, field, value) => {
    setEditDraft(d => ({
      ...d,
      exercises: d.exercises.map(e => e.name === name ? { ...e, [field]: value } : e),
    }));
  };

  const saveEdit = async () => {
    setEditError("");
    if (!editDraft.name?.trim()) { setEditError("Plan name is required."); return; }
    const patch = { name: editDraft.name.trim(), objective: editDraft.objective, type: editDraft.type, exercises: editDraft.exercises };
    try {
      if (isDemo) update("plans", editDraft.id, patch);
      else { const { error } = await supabase.from("plans").update(patch).eq("id", editDraft.id); if (error) throw error; }
      await refreshPlans();
      setShowEdit(false);
    } catch (e) { setEditError(e.message); }
  };

  const resetCreate = () => {
    setExercises([]);
    setCreateDraft({ name: "", type: "Muscular Development", objective: "" });
    setCreateExSearch("");
    setCreateError("");
  };

  const addToCreate = (name) => {
    if (exercises.some(e => e.name === name)) return;
    setExercises(ex => [...ex, { name, sets: 3, reps: 12, kg: defaultKgForExercise(name), rest: 1 }]);
  };
  const removeFromCreate = (name) => setExercises(ex => ex.filter(e => e.name !== name));
  const updateCreateExField = (name, field, value) =>
    setExercises(ex => ex.map(e => e.name === name ? { ...e, [field]: value } : e));

  const savePlan = async () => {
    setCreateError("");
    if (!createDraft.name.trim()) { setCreateError("Plan name is required."); return; }
    try {
      if (isDemo) {
        const plan = createFitnessPlan(PT_ID, createDraft.name.trim(), createDraft.objective, createDraft.type);
        update("plans", plan.id, { exercises });
      } else {
        const { error } = await supabase.from("plans").insert({ pt_id: ptId, name: createDraft.name.trim(), type: createDraft.type, objective: createDraft.objective, exercises });
        if (error) throw error;
      }
      setShowCreate(false);
      resetCreate();
      await refreshPlans();
    } catch (e) { setCreateError(e.message); }
  };

  // Library exercises not yet in the draft, filtered by search
  const availableToAdd = libraryExercises
    .filter(e => !editDraft?.exercises.some(d => d.name === e.name))
    .filter(e => !exSearch || e.name.toLowerCase().includes(exSearch.toLowerCase()));

  // Library exercises available to add to new plan
  const availableToCreate = libraryExercises
    .filter(e => !exercises.some(d => d.name === e.name))
    .filter(e => !createExSearch || e.name.toLowerCase().includes(createExSearch.toLowerCase()));

  return (
    <div className="page-content">
      {/* Toast */}
      {planToast && (
        <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", background:C.primary, color:"#000", padding:"10px 22px", borderRadius:24, fontWeight:600, fontSize:14, zIndex:9999, boxShadow:"0 4px 20px rgba(0,0,0,0.4)", pointerEvents:"none" }}>
          {planToast}
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <div className="accent-line" />
          <h1 style={{fontSize:28,fontWeight:700,letterSpacing:"-0.5px"}}>Fitness Plans</h1>
        </div>
        {activeTab === "mine" && <button className="btn btn-primary" onClick={()=>setShowCreate(true)}>+ Create Plan</button>}
      </div>

      {/* Tab bar */}
      <div style={{ display:"flex", gap:8, marginBottom:24 }}>
        <button onClick={() => switchTab("mine")} style={{ padding:"7px 18px", borderRadius:20, background: activeTab==="mine" ? C.primary : C.surfaceAlt, color: activeTab==="mine" ? "#000" : C.textSub, border:`1px solid ${activeTab==="mine"?C.primary:C.border}`, fontSize:13, fontWeight:activeTab==="mine"?600:500, cursor:"pointer", transition:"all 0.15s" }}>My Plans</button>
        {isDemo && <button onClick={() => switchTab("community")} style={{ padding:"7px 18px", borderRadius:20, background: activeTab==="community" ? C.primary : C.surfaceAlt, color: activeTab==="community" ? "#000" : C.textSub, border:`1px solid ${activeTab==="community"?C.primary:C.border}`, fontSize:13, fontWeight:activeTab==="community"?600:500, cursor:"pointer", transition:"all 0.15s" }}>Community Plans 🌐</button>}
      </div>

      {/* ── MY PLANS TAB ── */}
      {activeTab === "mine" && (
        <>
          <div style={{ display:"flex", gap:8, marginBottom:24 }}>
            {types.map(t=>(
              <button key={t} onClick={()=>setFilter(t)} style={{ padding:"7px 18px", borderRadius:20, background: filter===t ? C.surfaceAlt : "transparent", color: filter===t ? C.text : C.textSub, border:`1px solid ${filter===t?C.border:"transparent"}`, fontSize:13, fontWeight:filter===t?600:500, cursor:"pointer", transition:"all 0.15s" }}>{t}</button>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:20 }}>
            {filtered.map(p => {
              const isShared = sharedPlanIds.has(p.id);
              return (
                <div key={p.id} className="card" style={{ display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                  <div style={{cursor:"pointer"}} onClick={()=>setSelected(p)}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                      <div style={{fontWeight:600,fontSize:15}}>{p.name}</div>
                      <span className="badge badge-lime">{p.type}</span>
                    </div>
                    <p style={{fontSize:13,color:C.textSub,lineHeight:1.7,marginBottom:14}}>{p.objective}</p>
                    <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:14}}>
                      <div style={{fontSize:11,color:C.textMuted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.5px"}}>Exercises ({p.exercises.length})</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {p.exercises.slice(0,4).map(e=>{const n=typeof e==="string"?e:e.name;return<span key={n} style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,padding:"3px 9px",borderRadius:6,fontSize:11,color:C.textSub}}>{n}</span>;})}
                        {p.exercises.length>4 && <span style={{fontSize:11,color:C.textMuted}}>+{p.exercises.length-4} more</span>}
                      </div>
                    </div>
                  </div>
                  {/* Share toggle */}
                  <div style={{ display:"flex", justifyContent:"flex-end" }}>
                    {isShared
                      ? <button className="btn btn-ghost btn-sm" style={{ fontSize:11, color:C.primary, padding:"3px 10px" }} onClick={() => handleUnshare(p)}>Shared ✓ · Remove</button>
                      : <button className="btn btn-outline btn-sm" style={{ fontSize:11, padding:"3px 10px" }} onClick={() => handleShare(p)}>Share 🌐</button>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── COMMUNITY PLANS TAB ── */}
      {activeTab === "community" && (
        <>
          <div style={{ fontSize:14, color:C.textSub, marginBottom:16 }}>Fitness plans shared by PTs on myPTOnline — add any to your library</div>
          <input className="input" placeholder="Search community plans..." value={communitySearch} onChange={e => setCommunitySearch(e.target.value)} style={{ maxWidth:320, marginBottom:24 }} />
          {filteredCommunity.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 20px", color:C.textMuted }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🌐</div>
              <div style={{ fontWeight:600, fontSize:16, marginBottom:8 }}>No community plans yet</div>
              <div style={{ fontSize:13 }}>Share plans from My Plans tab to see them here.</div>
            </div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:20 }}>
              {filteredCommunity.map((p, i) => {
                const alreadyMine = plans.some(mp => mp.name === p.name + " (copy)" || mp.name === p.name);
                return (
                  <div key={p.communityPlanId || i} className="card" style={{ display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                        <div style={{ fontWeight:600, fontSize:15 }}>{p.name}</div>
                        <span className="badge badge-lime">{p.type}</span>
                      </div>
                      <p style={{ fontSize:13, color:C.textSub, lineHeight:1.7, marginBottom:14 }}>{p.objective}</p>
                      <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, marginBottom:14 }}>
                        <div style={{ fontSize:11, color:C.textMuted, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.5px" }}>Exercises ({(p.exercises||[]).length})</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                          {(p.exercises||[]).slice(0,4).map(e=>{const n=typeof e==="string"?e:e.name;return<span key={n} style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,padding:"3px 9px",borderRadius:6,fontSize:11,color:C.textSub}}>{n}</span>;})}
                          {(p.exercises||[]).length>4 && <span style={{fontSize:11,color:C.textMuted}}>+{(p.exercises||[]).length-4} more</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontSize:11, color:C.textMuted }}>by {p.sharedBy}</span>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={alreadyMine}
                        style={{ fontSize:12, ...(alreadyMine ? { opacity:0.5, cursor:"default" } : {}) }}
                        onClick={() => !alreadyMine && handleCommunityAdd(p)}
                      >
                        {alreadyMine ? "Already added" : "Add to My Plans"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {selected && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{maxWidth:580}}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
              <span style={{fontWeight:600,fontSize:16}}>{selected.name}</span>
              <CloseBtn onClick={()=>setSelected(null)} />
            </div>
            <span className="badge badge-lime" style={{marginBottom:14,display:"inline-block"}}>{selected.type}</span>
            <div style={{ marginBottom:18, padding:"12px 14px", background:C.primaryGlow, border:`1px solid ${C.primary}20`, borderRadius:8 }}>
              <div style={{fontSize:11,color:C.textMuted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Objective</div>
              <div style={{fontSize:13,color:C.primary}}>{selected.objective}</div>
            </div>
            <div style={{fontWeight:600,marginBottom:12}}>Exercise List</div>
            <table className="dark-table" style={{fontSize:13}}>
              <thead><tr><th>Exercise</th><th>Sets</th><th>Reps</th><th>Kg</th><th>Rest (min)</th></tr></thead>
              <tbody>
                {selected.exercises.map((e,i)=>{const ex=normalizeEx(e);return(
                  <tr key={i}>
                    <td style={{fontWeight:500}}>{ex.name}</td>
                    <td style={{color:C.textSub}}>{ex.sets}</td>
                    <td style={{color:C.textSub}}>{ex.reps}</td>
                    <td style={{color:C.textSub}}>{ex.kg ?? "—"}</td>
                    <td style={{color:C.textSub}}>{ex.rest}</td>
                  </tr>
                );})}
              </tbody>
            </table>
            <div style={{display:"flex",gap:10,marginTop:18}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setSelected(null)}>Close</button>
              <button className="btn btn-primary btn-sm" onClick={()=>openEdit(selected)}>Edit Plan</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && editDraft && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 800 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>Edit Plan</span>
              <CloseBtn onClick={() => setShowEdit(false)} />
            </div>
            <div style={{ marginBottom:12 }}><label className="label">Plan name</label>
              <input className="input" value={editDraft.name} onChange={e=>setEditDraft(d=>({...d,name:e.target.value}))} /></div>
            <div className="grid-2-form">
              <div><label className="label">Type</label>
                <select className="input" value={editDraft.type} onChange={e=>setEditDraft(d=>({...d,type:e.target.value}))}>
                  {["Muscular Development","Cardio Fitness","Full Body Workout","Flexibility","Custom"].map(t=><option key={t}>{t}</option>)}
                </select></div>
            </div>
            <div style={{ marginBottom:18 }}><label className="label">Objective</label>
              <textarea className="input" rows={2} value={editDraft.objective} onChange={e=>setEditDraft(d=>({...d,objective:e.target.value}))} /></div>

            <div className="grid-plan-edit" style={{ display:"grid", gridTemplateColumns:"3fr 2fr", gap:16 }}>
              {/* Current exercises */}
              <div>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>In this plan <span style={{color:C.textMuted,fontWeight:400}}>({editDraft.exercises.length})</span></div>
                {/* Column headers */}
                {editDraft.exercises.length > 0 && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 44px 44px 44px 44px 28px", gap:4, padding:"0 10px", marginBottom:4 }}>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Exercise</div>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"center"}}>Sets</div>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"center"}}>Reps</div>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"center"}}>Kg</div>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"center"}}>Rest</div>
                    <div />
                  </div>
                )}
                <div style={{ maxHeight:280, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }}>
                  {editDraft.exercises.length === 0 && <div style={{fontSize:13,color:C.textMuted}}>No exercises added.</div>}
                  {editDraft.exercises.map(ex => (
                    <div key={ex.name} style={{ display:"grid", gridTemplateColumns:"1fr 44px 44px 44px 44px 28px", gap:4, alignItems:"center", padding:"6px 10px", background:C.surfaceAlt, borderRadius:8, border:`1px solid ${C.border}` }}>
                      <span style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={ex.name}>{ex.name}</span>
                      {["sets","reps","kg","rest"].map(field => (
                        <input key={field} type="number" min={0} value={ex[field]} onChange={e=>updateExField(ex.name, field, Number(e.target.value))}
                          style={{width:"100%",padding:"3px 4px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,color:C.text,textAlign:"center"}} />
                      ))}
                      <button onClick={()=>removeFromEdit(ex.name)} style={{background:"none",border:"none",color:C.danger,fontSize:16,cursor:"pointer",lineHeight:1,padding:"0 2px",textAlign:"center"}}>×</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Library picker */}
              <div>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Add from library</div>
                <input className="input" placeholder="Search exercises…" value={exSearch} onChange={e=>setExSearch(e.target.value)} style={{marginBottom:8}} />
                <div style={{ maxHeight:220, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }}>
                  {availableToAdd.length === 0 && <div style={{fontSize:13,color:C.textMuted}}>All library exercises added.</div>}
                  {availableToAdd.map(ex => (
                    <div key={ex.id} onClick={()=>addToEdit(ex.name)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", background:C.surface, borderRadius:8, border:`1px solid ${C.border}`, cursor:"pointer", transition:"border-color 0.1s" }}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=C.primary}
                      onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500}}>{ex.name}</div>
                        {ex.muscle && <div style={{fontSize:11,color:C.textMuted}}>{ex.muscle}</div>}
                      </div>
                      <span style={{color:C.primary,fontSize:18,fontWeight:700,lineHeight:1}}>+</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {editError && <div style={{fontSize:12,color:C.danger,marginTop:12}}>{editError}</div>}
            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setShowEdit(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveEdit}>Save Plan</button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{maxWidth:560}}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
              <span style={{fontWeight:600,fontSize:16}}>Create Fitness Plan</span>
              <CloseBtn onClick={()=>{setShowCreate(false);resetCreate();}} />
            </div>
            <div style={{marginBottom:12}}><label className="label">Plan name</label>
              <input className="input" placeholder="e.g. Body Build — Upper Body" value={createDraft.name} onChange={e=>setCreateDraft(d=>({...d,name:e.target.value}))} /></div>
            <div className="grid-2-form" style={{marginBottom:12}}>
              <div><label className="label">Type</label>
                <select aria-label="Plan type" className="input" value={createDraft.type} onChange={e=>setCreateDraft(d=>({...d,type:e.target.value}))}>
                  {["Muscular Development","Cardio Fitness","Full Body Workout","Flexibility","Custom"].map(t=><option key={t}>{t}</option>)}
                </select></div>
            </div>
            <div style={{marginBottom:18}}><label className="label">Objective</label>
              <textarea className="input" rows={2} placeholder="Describe the aim of this plan..." value={createDraft.objective} onChange={e=>setCreateDraft(d=>({...d,objective:e.target.value}))} /></div>

            <div className="grid-plan-edit" style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:16}}>
              {/* Added exercises */}
              <div>
                <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>In this plan <span style={{color:C.textMuted,fontWeight:400}}>({exercises.length})</span></div>
                {exercises.length > 0 && (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 44px 44px 44px 44px 28px",gap:4,padding:"0 10px",marginBottom:4}}>
                    {["Exercise","Sets","Reps","Kg","Rest",""].map((h,i)=>(
                      <div key={i} style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:"0.5px",textAlign:i>0?"center":"left"}}>{h}</div>
                    ))}
                  </div>
                )}
                <div style={{maxHeight:260,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
                  {exercises.length === 0 && <div style={{fontSize:13,color:C.textMuted}}>No exercises added yet — pick from the library →</div>}
                  {exercises.map(ex=>(
                    <div key={ex.name} style={{display:"grid",gridTemplateColumns:"1fr 44px 44px 44px 44px 28px",gap:4,alignItems:"center",padding:"6px 10px",background:C.surfaceAlt,borderRadius:8,border:`1px solid ${C.border}`}}>
                      <span style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={ex.name}>{ex.name}</span>
                      {["sets","reps","kg","rest"].map(field=>(
                        <input key={field} type="number" min={0} value={ex[field]} onChange={e=>updateCreateExField(ex.name,field,Number(e.target.value))}
                          style={{width:"100%",padding:"3px 4px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,color:C.text,textAlign:"center"}} />
                      ))}
                      <button onClick={()=>removeFromCreate(ex.name)} style={{background:"none",border:"none",color:C.danger,fontSize:16,cursor:"pointer",lineHeight:1,padding:"0 2px",textAlign:"center"}}>×</button>
                    </div>
                  ))}
                </div>
              </div>
              {/* Library picker */}
              <div>
                <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>Add from library</div>
                <input className="input" placeholder="Search exercises…" value={createExSearch} onChange={e=>setCreateExSearch(e.target.value)} style={{marginBottom:8}} />
                <div style={{maxHeight:260,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
                  {libraryExercises.length === 0 && <div style={{fontSize:13,color:C.textMuted}}>No exercises in library yet.</div>}
                  {libraryExercises.length > 0 && availableToCreate.length === 0 && <div style={{fontSize:13,color:C.textMuted}}>All library exercises added.</div>}
                  {availableToCreate.map(ex=>(
                    <div key={ex.id} onClick={()=>addToCreate(ex.name)}
                      style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",background:C.surface,borderRadius:8,border:`1px solid ${C.border}`,cursor:"pointer",transition:"border-color 0.1s"}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=C.primary}
                      onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500}}>{ex.name}</div>
                        {ex.muscle && <div style={{fontSize:11,color:C.textMuted}}>{ex.muscle}</div>}
                      </div>
                      <span style={{color:C.primary,fontSize:18,fontWeight:700,lineHeight:1}}>+</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {createError && <div style={{fontSize:12,color:C.danger,marginTop:12}}>{createError}</div>}
            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button className="btn btn-outline btn-sm" onClick={()=>{setShowCreate(false);resetCreate();}}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={savePlan}>Save Plan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TESTIMONIALS STORE ────────────────────────────────────────────────────────
function testimonialsKey(ptId) { return `mypt_testimonials_${ptId}`; }
function getTestimonials(ptId) {
  try { return JSON.parse(localStorage.getItem(testimonialsKey(ptId)) || "[]"); } catch { return []; }
}
function saveTestimonials(ptId, list) {
  localStorage.setItem(testimonialsKey(ptId), JSON.stringify(list));
}
function addTestimonial(ptId, t) {
  const list = getTestimonials(ptId);
  const entry = { ...t, id: Date.now(), date: t.date || new Date().toISOString().slice(0, 10), visible: true };
  saveTestimonials(ptId, [...list, entry]);
  return entry;
}
function updateTestimonial(ptId, id, patch) {
  const list = getTestimonials(ptId).map(t => t.id === id ? { ...t, ...patch } : t);
  saveTestimonials(ptId, list);
}
function deleteTestimonial(ptId, id) {
  saveTestimonials(ptId, getTestimonials(ptId).filter(t => t.id !== id));
}

// ── COMMUNITY PLAN STORE ──────────────────────────────────────────────────────
const COMMUNITY_PLANS_KEY = "mypt_community_plans";
function getCommunityPlans() {
  try { return JSON.parse(localStorage.getItem(COMMUNITY_PLANS_KEY) || "[]"); } catch { return []; }
}
function sharePlanToCommunity(plan, ptName) {
  const community = getCommunityPlans();
  if (community.find(p => p.communityPlanId === plan.id)) return false; // already shared
  community.push({ ...plan, sharedBy: ptName || "Anonymous PT", sharedAt: new Date().toISOString(), communityPlanId: plan.id });
  localStorage.setItem(COMMUNITY_PLANS_KEY, JSON.stringify(community));
  return true;
}
function unsharePlan(planId) {
  const community = getCommunityPlans().filter(p => p.communityPlanId !== planId);
  localStorage.setItem(COMMUNITY_PLANS_KEY, JSON.stringify(community));
}

// ── EXERCISES (UC: Create Exercise) ───────────────────────────────────────────

// ── COMMUNITY EXERCISE STORE ──────────────────────────────────────────────────
const COMMUNITY_KEY = "mypt_community_exercises";
function getCommunityExercises() {
  try { return JSON.parse(localStorage.getItem(COMMUNITY_KEY) || "[]"); } catch { return []; }
}
function shareToCommunity(exercise, ptName) {
  const community = getCommunityExercises();
  if (community.find(e => e.name.trim().toLowerCase() === exercise.name.trim().toLowerCase())) return false;
  community.push({ ...exercise, sharedBy: ptName || "Anonymous PT", sharedAt: new Date().toISOString(), communityId: Date.now() });
  localStorage.setItem(COMMUNITY_KEY, JSON.stringify(community));
  return true;
}
function removeFromCommunity(name) {
  const community = getCommunityExercises().filter(e => e.name.trim().toLowerCase() !== name.trim().toLowerCase());
  localStorage.setItem(COMMUNITY_KEY, JSON.stringify(community));
}

// ── WGER EXERCISE FETCH ───────────────────────────────────────────────────────
// Free public API — no auth required. Language 2 = English.
const WGER_CATEGORIES = [
  { id: 10, label: "Legs" },
  { id: 8,  label: "Arms" },
  { id: 11, label: "Back" },
  { id: 12, label: "Chest" },
  { id: 13, label: "Shoulders" },
  { id: 14, label: "Core" },
  { id: 15, label: "Cardio" },
];

async function fetchWgerExercises(categoryId) {
  const url = `https://wger.de/api/v2/exerciseinfo/?format=json&language=2&category=${categoryId}&limit=80`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch from Wger");
  const data = await res.json();
  return (data.results || []).map(ex => {
    const translation = ex.translations?.find(t => t.language === 2) || ex.translations?.[0] || {};
    const muscle = ex.muscles?.[0]?.name_en || ex.muscles_secondary?.[0]?.name_en || "";
    return {
      name: translation.name || ex.name || "",
      description: translation.description?.replace(/<[^>]+>/g, "").trim() || "",
      teachingPoints: "",
      adaptations: "",
      bodyArea: ex.category?.name || "",
      muscle,
      category: "Strength",
      videoUrl: "",
      source: "wger",
    };
  }).filter(e => e.name);
}

// The Supabase API returns at most 1000 rows per request (a bigger .limit() is
// silently capped). Page through with .range() and resolve to { data, error } like
// a normal query. buildQuery must return a FRESH builder each call, with a stable
// order (include a unique tiebreak such as id).
async function fetchAllRows(buildQuery, pageSize = 1000) {
  const data = [];
  for (let from = 0; ; from += pageSize) {
    const { data: page, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) return { data, error };
    data.push(...(page || []));
    if (!page || page.length < pageSize) return { data, error: null };
  }
}

// Platform exercises a PT has linked (pt_exercise_refs rows with an embedded
// `exercises` object), minus any the PT has customised into their own copy or whose
// name clashes with one of their own exercises — the PT's own version wins.
// `own` items need .name and .platformExerciseId; `map` shapes each platform row.
function linkedExercises(own, refs, map = r => r) {
  const ownNames = new Set(own.map(e => (e.name || "").trim().toLowerCase()));
  const customised = new Set(own.map(e => e.platformExerciseId).filter(Boolean));
  return (refs || [])
    .map(r => r.exercises)
    .filter(ex => ex && !customised.has(ex.id) && !ownNames.has((ex.name || "").trim().toLowerCase()))
    .map(ex => ({ ...map(ex), linked: true }));
}

const EMPTY_EXERCISE = {
  name: "", description: "", teachingPoints: "", adaptations: "",
  bodyArea: "Shoulder", muscle: "", category: "Strength", media: "", videoUrl: "",
};

// Convert a YouTube watch / youtu.be / shorts URL to an embed URL.
function toYouTubeEmbed(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return url;
      if (u.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
  } catch { /* not a URL */ }
  return url;
}

function toYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("?")[0];
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/")[2];
      return u.searchParams.get("v") || null;
    }
  } catch { /* not a URL */ }
  return null;
}

// ── Video links ───────────────────────────────────────────────────────────────
// Turn any exercise video link into something embeddable. TikTok and Instagram
// refuse to be framed by their normal page URLs, so each needs its own embed URL.
// `vertical` marks portrait formats (TikTok, Reels, Shorts) so they aren't
// letterboxed in a 16:9 frame. Returns null when we can't embed it — e.g. a
// vm.tiktok.com short link, which needs a redirect we can't follow in the browser
// — and the caller links out instead.
function videoEmbed(url) {
  const raw = String(url || "").trim();
  if (!raw) return null;
  let u;
  try { u = new URL(raw); } catch { return null; }
  const host = u.hostname.replace(/^www\./, "");
  const path = u.pathname;

  if (/\.(mp4|webm|ogv|ogg|mov)$/i.test(path)) return { provider: "file", src: raw, file: true, vertical: false };
  if (host === "youtu.be") return { provider: "YouTube", src: `https://www.youtube.com/embed/${path.slice(1)}`, vertical: false };
  if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
    if (path.startsWith("/embed/")) return { provider: "YouTube", src: raw, vertical: false };
    if (path.startsWith("/shorts/")) return { provider: "YouTube", src: `https://www.youtube.com/embed/${path.split("/")[2]}`, vertical: true };
    const v = u.searchParams.get("v");
    return v ? { provider: "YouTube", src: `https://www.youtube.com/embed/${v}`, vertical: false } : null;
  }
  if (host.endsWith("tiktok.com")) {
    const m = /\/video\/(\d+)/.exec(path);      // tiktok.com/@user/video/1234567890
    return m ? { provider: "TikTok", src: `https://www.tiktok.com/embed/v2/${m[1]}`, vertical: true } : null;
  }
  if (host.endsWith("instagram.com")) {
    const m = /^\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/.exec(path);
    return m ? { provider: "Instagram", src: `https://www.instagram.com/p/${m[1]}/embed`, vertical: true } : null;
  }
  if (host.endsWith("vimeo.com")) {
    const m = /\/(\d+)/.exec(path);
    return m ? { provider: "Vimeo", src: `https://player.vimeo.com/video/${m[1]}`, vertical: false } : null;
  }
  return null;
}

const videoHost = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return "link"; } };

// Player for any supported link; falls back to a link-out button.
function VideoFrame({ url, title }) {
  const em = videoEmbed(url);
  if (!em) {
    return (
      <a className="btn btn-outline btn-sm" href={url} target="_blank" rel="noopener noreferrer">
        Open video on {videoHost(url)} ↗
      </a>
    );
  }
  if (em.file) {
    return <video src={em.src} controls playsInline style={{ width: "100%", maxHeight: 420, borderRadius: 10, background: "#000", border: `1px solid ${C.border}` }} />;
  }
  const shape = em.vertical
    ? { maxWidth: 340, margin: "0 auto", paddingBottom: "min(177.78%, 600px)" }
    : { paddingBottom: "56.25%" };
  return (
    <div style={{ position: "relative", height: 0, borderRadius: 10, overflow: "hidden", background: "#000", border: `1px solid ${C.border}`, ...shape }}>
      <iframe
        src={em.src} title={title || em.provider} frameBorder="0" allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
    </div>
  );
}

// ── FLIPPABLE EXERCISE CARD ───────────────────────────────────────────────────
// Single click → flip to show media. Click again → flip back.
// Double click → open full detail modal.
// ── Exercise card building blocks ─────────────────────────────────────────────
// Shared by the library grid and the Platform Library picker so both read as one
// system. Cards carry a thumbnail + chips only: the description is step-by-step
// instructions, so a truncated slice of it just crowded the card (it lives in the
// detail popup instead).
const capitalise = s => (s ? s[0].toUpperCase() + s.slice(1) : s);

// Thumbnail: the exercise image, else the YouTube still, else a muted placeholder.
// Accepts either shape — camelCase from mapEx, or a raw snake_case DB row.
function ExerciseThumb({ e, size = 64, radius = 10 }) {
  const img = e.media || e.image_url;
  const yt  = toYouTubeId(e.videoUrl || e.video_url);
  const src = img || (yt ? `https://img.youtube.com/vi/${yt}/mqdefault.jpg` : null);
  const box = { width: size, height: size, borderRadius: radius, border: `1px solid ${C.border}`, flexShrink: 0 };
  return src
    ? <img src={src} alt="" loading="lazy" style={{ ...box, objectFit: "cover", background: "#000" }} />
    : <div style={{ ...box, background: C.surfaceAlt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size / 3), color: C.textMuted }}>🏋️</div>;
}

function MetaChip({ children, tone }) {
  const tones = { media: [C.primary, "rgba(200,255,87,0.12)"], default: [C.textSub, C.surfaceAlt] };
  const [color, background] = tones[tone] || tones.default;
  return <span style={{ fontSize: 10.5, fontWeight: 500, padding: "3px 8px", borderRadius: 6, background, color, whiteSpace: "nowrap" }}>{children}</span>;
}

// ONE provenance chip per card — there used to be up to three competing badges.
const PROVENANCE = {
  custom:             ["MINE",        C.primary, "rgba(200,255,87,0.15)"],
  amended:            ["AMENDED",     C.blue,    "rgba(91,159,255,0.15)"],
  wger:               ["WGER",        C.amber,   "rgba(255,179,71,0.15)"],
  "free-exercise-db": ["OPEN SOURCE", C.amber,   "rgba(255,179,71,0.15)"],
};
function ProvenanceChip({ e }) {
  const [label, color, background] = e.linked
    ? ["PLATFORM", C.blue, "rgba(91,159,255,0.15)"]
    : PROVENANCE[e.source] || ["LIBRARY", C.textMuted, C.surfaceAlt];
  return <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.3px", padding: "2px 6px", borderRadius: 4, background, color, whiteSpace: "nowrap" }}>{label}</span>;
}

function FlippableExerciseCard({ e, isShared, onOpen, onShare }) {
  const [flipped, setFlipped]   = useState(false);
  const clickTimer              = useRef(null);
  const ytId                    = toYouTubeId(e.videoUrl);
  const hasVideo                = !!String(e.videoUrl || "").trim(); // a real video, for the ▶ chip
  const hasMedia                = !!(ytId || e.media || hasVideo);   // image OR video — the flip shows either
  const videoProvider           = hasVideo ? (videoEmbed(e.videoUrl) || {}).provider || videoHost(e.videoUrl) : null;

  const handleClick = () => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      onOpen(e);           // double click → modal
    } else {
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        setFlipped(f => !f); // single click → flip
      }, 220);
    }
  };

  const CARD_H = 200;

  return (
    <div
      style={{ perspective: "1000px", height: CARD_H, cursor: "pointer" }}
      onClick={handleClick}
      title={hasMedia ? "Click to flip • Double-click to open" : "Double-click to open"}
    >
      <div style={{
        position: "relative", width: "100%", height: "100%",
        transformStyle: "preserve-3d",
        transition: "transform 0.55s cubic-bezier(0.4,0,0.2,1)",
        transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
      }}>

        {/* ── FRONT ── */}
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 16px 12px",
          overflow: "hidden", display: "flex", flexDirection: "column",
        }}>
          {e.inLibrary === false && (
            <span className="badge badge-amber" style={{ position: "absolute", top: 10, right: 10, fontSize: 10 }}>Draft</span>
          )}
          {/* Thumbnail + name. Body area / muscle sit under the name as one quiet line. */}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <ExerciseThumb e={e} size={60} />
            <div style={{ flex: 1, minWidth: 0, paddingRight: e.inLibrary === false ? 48 : 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{e.name}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {[e.bodyArea, e.muscle].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
          </div>
          {/* Metadata as chips on one wrapping row, with a single provenance chip. */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start", alignContent: "flex-start", marginTop: 10, flex: 1, overflow: "hidden" }}>
            {e.equipment && <MetaChip>{e.equipment}</MetaChip>}
            {e.level && <MetaChip>{capitalise(e.level)}</MetaChip>}
            {hasVideo && <MetaChip tone="media">▶ Video</MetaChip>}
            <ProvenanceChip e={e} />
          </div>
          <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }} onClick={ev => ev.stopPropagation()}>
            <span style={{ fontSize: 10.5, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {hasMedia ? "Click to preview · double-click for detail" : "Double-click for detail"}
            </span>
            {/* Share only if PT created or renamed the exercise */}
            {(e.source === "custom" || e.source === "amended") && (
              isShared
                ? <span className="badge badge-lime" style={{ fontSize: 10, flexShrink: 0 }}>Shared ✓</span>
                : <button className="btn btn-outline btn-sm" style={{ fontSize: 10, padding: "2px 8px", flexShrink: 0 }} onClick={ev => { ev.stopPropagation(); onShare(e); }}>Share 🌐</button>
            )}
          </div>
        </div>

        {/* ── BACK ── */}
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
          transform: "rotateY(180deg)",
          background: "#000", border: `1px solid ${C.border}`, borderRadius: 12,
          overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {ytId ? (
            <div style={{ position: "relative", width: "100%", height: "100%" }}>
              <img
                src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                alt={e.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }}
              />
              {/* Play button overlay */}
              <div style={{
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(200,255,87,0.9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>▶</div>
                <span style={{ fontSize: 11, color: "#fff", fontWeight: 600, textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>Double-click to open</span>
              </div>
            </div>
          ) : e.media ? (
            <img src={e.media} alt={e.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : hasVideo ? (
            /* A video we have no thumbnail for (TikTok, Instagram, a file…) */
            <div style={{ textAlign: "center", color: "#fff" }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(200,255,87,0.9)", color: "#000", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, margin: "0 auto 8px" }}>▶</div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{videoProvider} video</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>Double-click to play</div>
            </div>
          ) : (
            <div style={{ textAlign: "center", color: C.textMuted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
              <div style={{ fontSize: 12 }}>No media added</div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function Exercises({ user }) {
  const isAdmin = user?.role === "admin";
  const PT_ID = 1;
  const PT_NAME = "Percy Trena";
  // Real PTs (numeric pts.id) get their own Supabase library; demo/admin keep hardcoded data.
  const isDemo = isAdmin || typeof user?.id !== "number";
  const ptId = isDemo ? PT_ID : user.id;
  // `media` (the card/detail image) is stored as exercises.image_url — previously
  // dropped on save for real PTs. equipment/level/source come from platform imports.
  const mapEx = e => ({ id:e.id, ptId:e.pt_id, name:e.name, bodyArea:e.body_area, muscle:e.muscle, category:e.category, description:e.description, teachingPoints:e.teaching_points, adaptations:e.adaptations, alternatives:e.alternatives, videoUrl:e.video_url, inLibrary:e.in_library, media:e.image_url||"", equipment:e.equipment, level:e.level, source:e.source||undefined, sourceId:e.source_id||null, platformExerciseId:e.platform_exercise_id||null });
  const toRow = (d, extra={}) => ({ name:(d.name||"").trim(), body_area:d.bodyArea||null, muscle:d.muscle||null, category:d.category||null, description:d.description||null, teaching_points:d.teachingPoints||null, adaptations:d.adaptations||null, video_url:d.videoUrl||null, image_url:d.media||null, ...extra });

  // ── core state ───────────────────────────────────────────────────────────────
  const [exercises, setExercises] = useState(() => isDemo ? getAll("exercises") : []);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(EMPTY_EXERCISE);
  const [stagedId, setStagedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [importMsg, setImportMsg] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [muscleFilter, setMuscleFilter] = useState("All");
  const [mediaFilter, setMediaFilter] = useState(false); // true = show only exercises with media
  // source filter: set of active sources — empty = show all
  const [sourcesOn, setSourcesOn] = useState(new Set());

  // ── tab state ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("library"); // "library" | "community" | "import"

  // ── community state ──────────────────────────────────────────────────────────
  const [community, setCommunity] = useState([]);
  const [communitySearch, setCommunitySearch] = useState("");
  const [sharedNames, setSharedNames] = useState(() => {
    const c = getCommunityExercises();
    return new Set(c.filter(e => e.sharedBy === PT_NAME).map(e => e.name.trim().toLowerCase()));
  });
  const [toastMsg, setToastMsg] = useState("");

  // ── wger state ───────────────────────────────────────────────────────────────
  const [wgerCategory, setWgerCategory] = useState(WGER_CATEGORIES[0]);
  const [wgerResults, setWgerResults] = useState([]);
  const [wgerLoading, setWgerLoading] = useState(false);
  const [wgerError, setWgerError] = useState("");
  const [wgerImporting, setWgerImporting] = useState(new Set());

  // ── platform library state (real PTs) ───────────────────────────────────────
  const [platform, setPlatform] = useState(null);            // null = not loaded yet
  const [platformErr, setPlatformErr] = useState("");
  const [platformSearch, setPlatformSearch] = useState("");
  const [platformArea, setPlatformArea] = useState("All");
  const [platformCat, setPlatformCat] = useState("All");
  const [platformAdding, setPlatformAdding] = useState(new Set()); // platform ids being added

  // ── derived ──────────────────────────────────────────────────────────────────
  const muscles = ["All", ...Array.from(new Set(exercises.map(e => e.muscle).filter(Boolean)))];
  const libraryNames = new Set(exercises.map(e => (e.name || "").trim().toLowerCase()));
  // Platform exercises already in this PT's library: linked (id = platform id) or
  // customised into their own copy (platformExerciseId). Name is a fallback check.
  const libraryPlatformIds = new Set(exercises.map(e => e.linked ? e.id : e.platformExerciseId).filter(Boolean));
  const inPTLibrary = r => libraryPlatformIds.has(r.id) || libraryNames.has((r.name || "").trim().toLowerCase());
  const filtered = exercises.filter(e =>
    (muscleFilter === "All" || e.muscle === muscleFilter) &&
    (e.name || "").toLowerCase().includes(search.toLowerCase()) &&
    (!mediaFilter || !!(toYouTubeId(e.videoUrl) || e.media)) &&
    (sourcesOn.size === 0 || sourcesOn.has(e.source || "seed"))
  );
  const filteredCommunity = community.filter(e =>
    (e.name || "").toLowerCase().includes(communitySearch.toLowerCase())
  );

  // ── helpers ──────────────────────────────────────────────────────────────────
  const refresh = async () => {
    if (isDemo) { setExercises(getAll("exercises")); return; }
    // A PT's library = their own exercises + the platform exercises they've linked.
    const [{ data: own }, { data: refs }] = await Promise.all([
      fetchAllRows(() => supabase.from("exercises").select("*").eq("pt_id", ptId).order("id")),
      fetchAllRows(() => supabase.from("pt_exercise_refs").select("exercise_id, exercises(*)").eq("pt_id", ptId).order("exercise_id")),
    ]);
    const mine = (own || []).map(mapEx);
    setExercises([...mine, ...linkedExercises(mine, refs, mapEx)]);
  };
  useEffect(() => { if (!isDemo) refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 2500);
  };

  const closeCreate = () => {
    setShowCreate(false); setDraft(EMPTY_EXERCISE); setStagedId(null); setEditingId(null); setError("");
  };

  const openEdit = (ex) => {
    setSelected(null);
    setEditingId(ex.id);
    setDraft({
      name: ex.name || "", description: ex.description || "",
      teachingPoints: ex.teachingPoints || "", adaptations: ex.adaptations || "",
      bodyArea: ex.bodyArea || "Shoulder", muscle: ex.muscle || "",
      category: ex.category || "Strength", media: ex.media || "", videoUrl: ex.videoUrl || "",
    });
    setShowCreate(true);
  };

  const saveEdit = async () => {
    setError("");
    if (!draft.name.trim()) { setError("Exercise name is required."); return; }
    try {
      if (isDemo) updateExercise(editingId, draft);
      else { const { error } = await supabase.from("exercises").update(toRow(draft)).eq("id", editingId); if (error) throw error; }
      await refresh();
      closeCreate();
    } catch (e) { setError(e.message); }
  };

  const importLibrary = () => {
    setImportMsg("");
    try {
      const n = importExerciseLibrary(PT_ID, OPEN_EXERCISE_LIBRARY);
      refresh();
      setImportMsg(n === 0 ? "Library already imported — no new exercises added." : `Imported ${n} exercises from the open-source library.`);
      setTimeout(() => setImportMsg(""), 4000);
    } catch (e) { setImportMsg(e.message); }
  };

  const stageExercise = async () => {
    setError("");
    if (!draft.name.trim()) { setError("Exercise name is required."); return; }
    try {
      if (isDemo) { const ex = createExercise(PT_ID, draft); setStagedId(ex.id); }
      else { const { data, error } = await supabase.from("exercises").insert(toRow(draft, { pt_id: ptId, in_library: true })).select("id").single(); if (error) throw error; setStagedId(data.id); }
      await refresh();
    } catch (e) { setError(e.message); }
  };

  const publishToLibrary = async () => {
    setError("");
    try {
      if (isDemo) { addExerciseToLibrary(stagedId); }  // real PTs' exercises are already in_library on insert
      await refresh();
      closeCreate();
    } catch (e) { setError(e.message); }
  };

  const handleShare = (exercise, ev) => {
    ev.stopPropagation();
    const ok = shareToCommunity(exercise, PT_NAME);
    if (ok) {
      setSharedNames(prev => new Set([...prev, exercise.name.trim().toLowerCase()]));
      showToast(`"${exercise.name}" shared to community!`);
    } else {
      showToast("Already shared.");
    }
  };

  const handleCommunityAdd = (exercise) => {
    try {
      importExerciseLibrary(PT_ID, [exercise]);
      refresh();
      showToast(`"${exercise.name}" added to your library.`);
    } catch (e) { showToast(e.message); }
  };

  const handleWgerFetch = async () => {
    setWgerError(""); setWgerResults([]); setWgerLoading(true);
    try {
      const results = await fetchWgerExercises(wgerCategory.id);
      setWgerResults(results);
    } catch (e) { setWgerError(e.message); }
    setWgerLoading(false);
  };

  const handleWgerAdd = async (exercise) => {
    setWgerImporting(prev => new Set([...prev, exercise.name]));
    try {
      importExerciseLibrary(PT_ID, [exercise]);
      refresh();
      showToast(`"${exercise.name}" added to your library.`);
    } catch (e) { showToast(e.message); }
    setWgerImporting(prev => { const s = new Set(prev); s.delete(exercise.name); return s; });
  };

  const handleWgerAddAll = async () => {
    try {
      importExerciseLibrary(PT_ID, wgerResults);
      refresh();
      showToast(`${wgerResults.length} exercises added to your library.`);
    } catch (e) { showToast(e.message); }
  };

  // refresh community list when tab changes
  // Platform library = exercises with pt_id NULL, curated by the admin (e.g. the
  // free-exercise-db import). Signed-in users can read it (e_platform_read). "Add"
  // LINKS a platform exercise into the PT's library (pt_exercise_refs) rather than
  // copying it, so admin updates flow through; "Customise" makes the PT's own copy.
  // Linked exercises appear in the library and the plan builder's picker.
  const loadPlatform = async () => {
    setPlatformErr("");
    // Page through: the API returns at most 1000 rows per request (a bigger
    // .limit() is silently capped).
    const cols = "id,name,body_area,muscle,category,description,teaching_points,adaptations,video_url,image_url,equipment,level,source,source_id";
    const rows = [];
    for (let from = 0; ; from += 1000) {
      const { data, error } = await supabase.from("exercises").select(cols).is("pt_id", null).order("name").order("id").range(from, from + 999);
      if (error) { setPlatformErr(error.message); setPlatform(rows); return; }
      rows.push(...(data || []));
      if (!data || data.length < 1000) break;
    }
    setPlatform(rows);
  };

  const addFromPlatform = async (rows) => {
    const fresh = rows.filter(r => !inPTLibrary(r));
    if (!fresh.length) { showToast("Already in your library."); return; }
    setPlatformAdding(new Set(fresh.map(r => r.id)));
    try {
      // Link, don't copy — the platform row stays the single source of truth.
      for (let i = 0; i < fresh.length; i += 500) {
        const links = fresh.slice(i, i + 500).map(r => ({ pt_id: ptId, exercise_id: r.id }));
        const { error } = await supabase.from("pt_exercise_refs").upsert(links, { onConflict: "pt_id,exercise_id", ignoreDuplicates: true });
        if (error) throw error;
      }
      await refresh();
      showToast(fresh.length === 1 ? `"${fresh[0].name}" added to your library` : `${fresh.length} exercises added to your library`);
    } catch (e) { showToast(`Couldn't add: ${e.message}`); await refresh(); }
    setPlatformAdding(new Set());
  };

  // Remove a linked platform exercise from this PT's library. Plans keep their own
  // name snapshot, so existing plans are unaffected.
  const unlinkExercise = async (ex) => {
    const { error } = await supabase.from("pt_exercise_refs").delete().eq("pt_id", ptId).eq("exercise_id", ex.id);
    if (error) { showToast(`Couldn't remove: ${error.message}`); return; }
    setSelected(null);
    await refresh();
    showToast(`"${ex.name}" removed from your library`);
  };

  // Copy-on-write: make the PT's own editable copy of a linked platform exercise,
  // remember where it came from, drop the link, then open the copy for editing.
  const customiseExercise = async (ex) => {
    const { data, error } = await supabase.from("exercises").insert(toRow(ex, {
      pt_id: ptId, in_library: true, platform_exercise_id: ex.id,
      equipment: ex.equipment || null, level: ex.level || null, alternatives: ex.alternatives || [],
      source: ex.source || null, source_id: ex.sourceId || null,
    })).select("*").single();
    if (error) { showToast(`Couldn't customise: ${error.message}`); return; }
    await supabase.from("pt_exercise_refs").delete().eq("pt_id", ptId).eq("exercise_id", ex.id);
    await refresh();
    openEdit(mapEx(data));
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === "community") setCommunity(getCommunityExercises());
    if (tab === "platform" && platform === null) loadPlatform();
  };

  // Alternative exercises for the detail popup. Pool = the PT's library plus the
  // platform library (not-yet-added rows open in preview mode, so "Add to my
  // library" is the only action). Ranked by shared muscle / body area / category /
  // equipment; names listed in the exercise's own `alternatives` always rank first.
  const alternativesFor = (ex) => {
    const norm = v => String(v || "").trim().toLowerCase();
    const named = new Set((Array.isArray(ex.alternatives) ? ex.alternatives : []).map(norm));
    const pool = [
      ...exercises,
      ...(platform || []).filter(r => !inPTLibrary(r)).map(r => ({ ...mapEx({ ...r, pt_id: null }), preview: true })),
    ];
    const seen = new Set([norm(ex.name)]);
    return pool
      .map(c => {
        if (!c.name || (ex.id && c.id === ex.id && !!c.preview === !!ex.preview)) return null;
        const byName = named.has(norm(c.name));
        const muscle = ex.muscle && norm(c.muscle) === norm(ex.muscle);
        const area   = ex.bodyArea && norm(c.bodyArea) === norm(ex.bodyArea);
        if (!byName && !muscle && !area) return null;          // must work the same area
        const score = (byName ? 10 : 0) + (muscle ? 3 : 0) + (area ? 2 : 0)
          + (ex.category && norm(c.category) === norm(ex.category) ? 2 : 0)
          + (ex.equipment && norm(c.equipment) === norm(ex.equipment) ? 1 : 0)
          + (c.preview ? 0 : 0.5);                             // prefer the PT's own library on ties
        return { c, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || a.c.name.localeCompare(b.c.name))
      .filter(({ c }) => !seen.has(norm(c.name)) && seen.add(norm(c.name)))
      .slice(0, 6)
      .map(({ c }) => c);
  };

  // Platform rows feed the alternatives, so load them the first time a popup opens.
  useEffect(() => {
    if (selected && !isDemo && platform === null) loadPlatform();
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // source filter toggle
  const toggleSource = (src) => {
    setSourcesOn(prev => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src); else next.add(src);
      return next;
    });
  };

  // source pill definitions
  const SOURCE_PILLS = [
    { src: "custom",  label: "My Exercises",   icon: "🧑‍💻" },
    { src: "amended", label: "Amended",         icon: "✏️" },
    { src: "seed",    label: "myPT Library",    icon: "📚" },
    { src: "wger",    label: "Wger",            icon: "🌐" },
    { src: "free-exercise-db", label: "Open source", icon: "📖" },
  ];

  // ── pill style helper ────────────────────────────────────────────────────────
  const pillStyle = (active) => ({
    padding: "8px 20px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 500,
    background: active ? C.primary : C.surfaceAlt,
    color: active ? "#000" : C.textSub,
    border: `1px solid ${active ? C.primary : C.border}`,
    transition: "all 0.15s",
  });

  return (
    <div className="page-content">
      {/* ── Toast ── */}
      {toastMsg && (
        <div style={{ position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)", background: C.primary, color: "#000", padding: "10px 24px", borderRadius: 24, fontWeight: 600, fontSize: 13, zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
          {toastMsg}
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div className="accent-line" />
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.5px" }}>Exercise Library</h1>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6 }}>Create exercises to use inside fitness plans.</div>
        </div>
        {activeTab === "library" && (
          <button className="btn btn-primary" onClick={() => { setEditingId(null); setDraft(EMPTY_EXERCISE); setStagedId(null); setShowCreate(true); }}>+ Create Exercise</button>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        <button style={pillStyle(activeTab === "library")} onClick={() => switchTab("library")}>My Library</button>
        {!isDemo && <button style={pillStyle(activeTab === "platform")} onClick={() => switchTab("platform")}>Platform Library</button>}
        {isDemo && <button style={pillStyle(activeTab === "community")} onClick={() => switchTab("community")}>Community</button>}
        {isAdmin && (
          <button style={pillStyle(activeTab === "import")} onClick={() => switchTab("import")}>Import Sources</button>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* MY LIBRARY TAB                                                        */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "library" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
            <input className="input" placeholder="Search exercises..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 260 }} />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {muscles.map(m => (
                <button key={m} onClick={() => setMuscleFilter(m)} style={pillStyle(muscleFilter === m)}>{m}</button>
              ))}
              <button onClick={() => setMediaFilter(f => !f)} style={{ ...pillStyle(mediaFilter), borderColor: mediaFilter ? C.primary : C.border }}>🎬 Has Media</button>
            </div>
            {/* Source filters */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderLeft: `1px solid ${C.border}`, paddingLeft: 10 }}>
              {SOURCE_PILLS.map(({ src, label, icon }) => {
                const on = sourcesOn.has(src);
                return (
                  <button key={src} onClick={() => toggleSource(src)} style={{ ...pillStyle(on), opacity: on ? 1 : 0.65 }}>
                    {icon} {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 16 }}>
            {filtered.map(e => {
              const isShared = sharedNames.has((e.name || "").trim().toLowerCase());
              return (
                <FlippableExerciseCard
                  key={e.id}
                  e={e}
                  isShared={isShared}
                  onOpen={setSelected}
                  onShare={ex => handleShare(ex, { stopPropagation: () => {} })}
                />
              );
            })}
          </div>
        </>
      )}

      {/* ── PLATFORM LIBRARY TAB (real PTs) ── browse admin-curated exercises and copy them in */}
      {activeTab === "platform" && (() => {
        const rows = platform || [];
        const areas = ["All", ...Array.from(new Set(rows.map(r => r.body_area).filter(Boolean))).sort()];
        const cats  = ["All", ...Array.from(new Set(rows.map(r => r.category).filter(Boolean))).sort()];
        const shown = rows.filter(r =>
          (platformArea === "All" || r.body_area === platformArea) &&
          (platformCat === "All" || r.category === platformCat) &&
          (r.name || "").toLowerCase().includes(platformSearch.toLowerCase()));
        const notAdded = shown.filter(r => !inPTLibrary(r));
        const busy = platformAdding.size > 0;
        return (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <input className="input" placeholder="Search the platform library..." value={platformSearch} onChange={e => setPlatformSearch(e.target.value)} style={{ maxWidth: 260 }} />
              <select className="input" value={platformArea} onChange={e => setPlatformArea(e.target.value)} style={{ maxWidth: 170 }}>
                {areas.map(a => <option key={a} value={a}>{a === "All" ? "All body areas" : a}</option>)}
              </select>
              <select className="input" value={platformCat} onChange={e => setPlatformCat(e.target.value)} style={{ maxWidth: 170 }}>
                {cats.map(c => <option key={c} value={c}>{c === "All" ? "All categories" : c}</option>)}
              </select>
              <div style={{ flex: 1 }} />
              {platform && <span style={{ fontSize: 12, color: C.textMuted }}>{shown.length} shown · {notAdded.length} not in your library</span>}
              <button className="btn btn-primary btn-sm" disabled={!notAdded.length || busy} onClick={() => addFromPlatform(notAdded)}>
                {busy && platformAdding.size > 1 ? "Adding…" : `Add all shown (${notAdded.length})`}
              </button>
            </div>
            {platformErr && <div style={{ padding: "10px 12px", background: C.dangerLight, border: `1px solid ${C.danger}40`, borderRadius: 8, fontSize: 13, color: C.danger, marginBottom: 12 }}>{platformErr}</div>}
            {platform === null && <div style={{ color: C.textMuted, fontSize: 13 }}>Loading platform library…</div>}
            {platform && rows.length === 0 && !platformErr && (
              <div className="card" style={{ color: C.textMuted, fontSize: 13 }}>The platform library is empty for now — exercises appear here once they're added by the myPTOnline team.</div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12 }}>
              {shown.map(r => {
                const inLib = inPTLibrary(r);
                const adding = platformAdding.has(r.id);
                return (
                  <div key={r.id} className="card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, opacity: inLib ? 0.65 : 1 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                      <ExerciseThumb e={r} size={60} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{r.name}</div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {[r.body_area, r.muscle].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {r.equipment && <MetaChip>{r.equipment}</MetaChip>}
                      {r.level && <MetaChip>{capitalise(r.level)}</MetaChip>}
                      {r.video_url && <MetaChip tone="media">▶ Video</MetaChip>}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: "auto" }}>
                      {/* preview: read-only popup — this exercise isn't the PT's yet */}
                      <button className="btn btn-ghost btn-sm" style={{ color: C.textSub, fontSize: 11, padding: "2px 6px" }} onClick={() => setSelected({ ...mapEx({ ...r, pt_id: null }), preview: true })}>Details</button>
                      <button className="btn btn-primary btn-sm" style={{ flexShrink: 0, opacity: inLib ? 0.6 : 1 }} disabled={inLib || adding || busy} onClick={() => addFromPlatform([r])}>
                        {inLib ? "Added ✓" : adding ? "…" : "Add"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* COMMUNITY TAB                                                         */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "community" && (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, color: C.textSub, marginBottom: 16 }}>Exercises shared by PTs on myPTOnline — add any to your library</div>
            <input className="input" placeholder="Search community exercises..." value={communitySearch} onChange={e => setCommunitySearch(e.target.value)} style={{ maxWidth: 320 }} />
          </div>
          {filteredCommunity.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: C.textMuted }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🌐</div>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>No community exercises yet</div>
              <div style={{ fontSize: 13 }}>Share exercises from your library to see them here.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 16 }}>
              {filteredCommunity.map((e, i) => {
                const alreadyInLib = libraryNames.has((e.name || "").trim().toLowerCase());
                return (
                  <div key={e.communityId || i} className="card" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{e.name}</div>
                        {e.muscle && <span className="badge badge-blue" style={{ fontSize: 10 }}>{e.muscle}</span>}
                      </div>
                      {e.bodyArea && <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>{e.bodyArea}</div>}
                      <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6 }}>{(e.description || "").slice(0, 80)}{(e.description || "").length > 80 ? "…" : ""}</p>
                    </div>
                    <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: C.textMuted }}>by {e.sharedBy}</span>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={alreadyInLib}
                        style={alreadyInLib ? { opacity: 0.5, cursor: "default" } : {}}
                        onClick={() => !alreadyInLib && handleCommunityAdd(e)}
                      >
                        {alreadyInLib ? "Already in Library" : "Add to My Library"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* IMPORT SOURCES TAB                                                    */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "import" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
          {/* myPTOnline curated library card */}
          <div className="card" style={{ height: "100%" }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>myPTOnline Curated Library</div>
            <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6, marginBottom: 20 }}>50+ professionally curated exercises with teaching points, adaptations and video links</p>
            <button className="btn btn-primary" onClick={importLibrary}>Import Library</button>
            {importMsg && <div style={{ marginTop: 12, padding: "10px 14px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textSub }}>{importMsg}</div>}
          </div>

          {/* Wger card */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>Wger Exercise Database</div>
            <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6, marginBottom: 16 }}>Open-source database with hundreds of exercises. Select a category to browse and import.</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
              {WGER_CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => setWgerCategory(cat)} style={pillStyle(wgerCategory.id === cat.id)}>{cat.label}</button>
              ))}
            </div>
            <button className="btn btn-outline" onClick={handleWgerFetch} disabled={wgerLoading} style={{ marginBottom: 12 }}>
              {wgerLoading ? "Fetching…" : "Fetch Exercises"}
            </button>
            {wgerError && <div style={{ padding: "10px 12px", background: C.dangerLight, border: `1px solid ${C.danger}40`, borderRadius: 8, fontSize: 13, color: C.danger, marginBottom: 12 }}>{wgerError}</div>}
            {wgerResults.length > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: C.textMuted }}>{wgerResults.length} exercises fetched</span>
                  <button className="btn btn-primary btn-sm" onClick={handleWgerAddAll}>Add All</button>
                </div>
                <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                  {wgerResults.map((ex, i) => {
                    const alreadyIn = libraryNames.has((ex.name || "").trim().toLowerCase());
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: C.surfaceAlt, borderRadius: 8, border: `1px solid ${C.border}` }}>
                        <div>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{ex.name}</span>
                          {ex.muscle && <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 8 }}>{ex.muscle}</span>}
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: alreadyIn ? C.textMuted : C.primary, fontSize: 12 }}
                          disabled={alreadyIn || wgerImporting.has(ex.name)}
                          onClick={() => !alreadyIn && handleWgerAdd(ex)}
                        >
                          {alreadyIn ? "Added" : wgerImporting.has(ex.name) ? "…" : "Add"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Exercise detail modal ── */}
      {selected && (
        <div className="modal-backdrop">
          <div className="modal-card" key={`${selected.preview ? "p" : "l"}-${selected.id}`} style={{ maxWidth: 520 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>{selected.name}</span>
              <CloseBtn onClick={() => setSelected(null)} />
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
              {selected.muscle && <span className="badge badge-blue">{selected.muscle}</span>}
              {selected.bodyArea && <span className="badge badge-lime">{selected.bodyArea}</span>}
              {selected.category && <span className="badge badge-amber">{selected.category}</span>}
              {selected.equipment && <span className="badge badge-blue">{selected.equipment}</span>}
              {selected.level && <span className="badge badge-lime" style={{ textTransform: "capitalize" }}>{selected.level}</span>}
            </div>
            {!selected.videoUrl && selected.media && (
              <img src={selected.media} alt={selected.name} style={{ width: "100%", maxHeight: 280, objectFit: "contain", borderRadius: 8, background: "#fff", marginBottom: 16 }} />
            )}
            {selected.videoUrl && (
              <div style={{ marginBottom: 16 }}>
                <VideoFrame url={selected.videoUrl} title={selected.name} />
              </div>
            )}
            {[["Description", selected.description], ["Teaching Points", selected.teachingPoints], ["Adaptations", selected.adaptations]].map(([label, text]) => text ? (
              <div key={label} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px", color: C.textMuted }}>{label}</div>
                <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.7, whiteSpace: "pre-line" }}>{text}</p>
              </div>
            ) : null)}
            {(() => {
              const alts = alternativesFor(selected);
              const loading = !isDemo && platform === null;
              if (!alts.length && !loading) return null;
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px", color: C.textMuted }}>Alternative exercises</div>
                  {!alts.length && <div style={{ fontSize: 12, color: C.textMuted }}>Finding alternatives…</div>}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {alts.map(a => (
                      <button key={`${a.preview ? "p" : "l"}-${a.id}`} onClick={() => setSelected(a)}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: 8, borderRadius: 10, background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, cursor: "pointer", transition: "border-color 0.15s" }}
                        onMouseEnter={ev => { ev.currentTarget.style.borderColor = C.primary; }}
                        onMouseLeave={ev => { ev.currentTarget.style.borderColor = C.border; }}>
                        <ExerciseThumb e={a} size={36} radius={8} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</span>
                          <span style={{ display: "block", fontSize: 11, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[a.muscle || a.bodyArea, a.equipment].filter(Boolean).join(" · ")}</span>
                        </span>
                        <MetaChip tone={a.preview ? undefined : "media"}>{a.preview ? "Platform" : "My library"}</MetaChip>
                        <span style={{ color: C.primary, fontSize: 14 }}>→</span>
                      </button>
                    ))}
                  </div>
                  {!isDemo && selected.bodyArea && (
                    <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, padding: "4px 0", fontSize: 12, color: C.primary }}
                      onClick={() => { setPlatformArea(selected.bodyArea); setPlatformCat("All"); setPlatformSearch(""); setSelected(null); switchTab("platform"); }}>
                      Browse all {selected.bodyArea} exercises in the Platform Library →
                    </button>
                  )}
                </div>
              );
            })()}
            {selected.linked && (
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, lineHeight: 1.6 }}>
                From the myPTOnline platform library — updates to it show here automatically. Customise to make your own editable copy.
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
              <button className="btn btn-outline btn-sm" onClick={() => setSelected(null)}>Close</button>
              {selected.preview ? (
                /* Previewing a platform exercise the PT hasn't added: the only action
                   is adding it. Edit/Share would write to a platform row (RLS-blocked). */
                <button className="btn btn-primary btn-sm" disabled={inPTLibrary(selected)}
                  onClick={() => { const row = selected; setSelected(null); addFromPlatform([row]); }}>
                  {inPTLibrary(selected) ? "Already in your library" : "Add to my library"}
                </button>
              ) : (
                <>
                  {!selected.linked && (
                    <button className="btn btn-outline btn-sm" onClick={(ev) => handleShare(selected, ev)}>
                      {sharedNames.has((selected.name || "").trim().toLowerCase()) ? "Shared ✓" : "Share 🌐"}
                    </button>
                  )}
                  {selected.linked ? (
                    <>
                      <button className="btn btn-outline btn-sm" onClick={() => unlinkExercise(selected)}>Remove from library</button>
                      <button className="btn btn-primary btn-sm" onClick={() => customiseExercise(selected)}>Customise</button>
                    </>
                  ) : (
                    <button className="btn btn-primary btn-sm" onClick={() => openEdit(selected)}>Edit</button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 560 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>{editingId ? "Edit Exercise" : "Create an Exercise"}</span>
              <CloseBtn onClick={closeCreate} />
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
              {editingId
                ? (() => {
                    const ex = exercises.find(x => x.id === editingId);
                    const isLibraryEx = ex && ex.source !== "custom" && ex.source !== "amended";
                    return isLibraryEx
                      ? <span>Saving changes will create <strong style={{ color: C.primary }}>your personal copy</strong> of this exercise — the original library version is kept intact.</span>
                      : "Update the exercise details and save.";
                  })()
                : (stagedId ? "Review the exercise — then add it to the library." : "Step 1 — provide the exercise information.")}
            </div>
            <div style={{ marginBottom: 12 }}><label className="label">Exercise name</label>
              <input className="input" placeholder="e.g. Seated Shoulder Press" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} disabled={!!stagedId} /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><label className="label">Body area</label>
                <select className="input" value={draft.bodyArea} onChange={e => setDraft({ ...draft, bodyArea: e.target.value })} disabled={!!stagedId}>
                  {["Shoulder", "Chest", "Arms", "Back", "Legs", "Core", "Cardio", "Full Body"].map(b => <option key={b}>{b}</option>)}
                </select></div>
              <div><label className="label">Major muscle</label>
                <input className="input" placeholder="e.g. Deltoid" value={draft.muscle} onChange={e => setDraft({ ...draft, muscle: e.target.value })} disabled={!!stagedId} /></div>
            </div>
            <div style={{ marginBottom: 12 }}><label className="label">Category</label>
              <select className="input" value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} disabled={!!stagedId}>
                {["Strength", "Hypertrophy", "Cardio", "Mobility", "Warm Up", "Cool Down", "Rehab"].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div style={{ marginBottom: 12 }}><label className="label">Description</label>
              <textarea className="input" rows={3} placeholder="How to perform the exercise..." value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} disabled={!!stagedId} /></div>
            <div style={{ marginBottom: 12 }}><label className="label">Teaching points</label>
              <textarea className="input" rows={2} placeholder="Key cues for correct technique..." value={draft.teachingPoints} onChange={e => setDraft({ ...draft, teachingPoints: e.target.value })} disabled={!!stagedId} /></div>
            <div style={{ marginBottom: 12 }}><label className="label">Adaptations</label>
              <textarea className="input" rows={2} placeholder="Easier or harder variations..." value={draft.adaptations} onChange={e => setDraft({ ...draft, adaptations: e.target.value })} disabled={!!stagedId} /></div>
            <div style={{ marginBottom: 12 }}><label className="label">Picture URL (optional)</label>
              <input className="input" placeholder="https://..." value={draft.media} onChange={e => setDraft({ ...draft, media: e.target.value })} disabled={!!stagedId} /></div>
            <div style={{ marginBottom: 16 }}><label className="label">Video URL (YouTube)</label>
              <input className="input" placeholder="YouTube, TikTok, Instagram or Vimeo link" value={draft.videoUrl} onChange={e => setDraft({ ...draft, videoUrl: e.target.value })} disabled={!!stagedId} /></div>
            {error && <div style={{ padding: "10px 12px", background: C.dangerLight, border: `1px solid ${C.danger}40`, borderRadius: 8, fontSize: 13, color: C.danger, marginBottom: 14 }}>{error}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-outline btn-sm" onClick={closeCreate}>Cancel</button>
              {editingId && <button className="btn btn-primary btn-sm" onClick={saveEdit}>Save Changes</button>}
              {!editingId && !stagedId && <button className="btn btn-primary btn-sm" onClick={stageExercise}>Create Exercise →</button>}
              {!editingId && stagedId && <button className="btn btn-primary btn-sm" onClick={publishToLibrary}>Add to Library</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CONTACT ME ────────────────────────────────────────────────────────────────
function ContactMePage() {
  const [contacts, setContacts] = useState(() => getAll("contacts"));

  const markContacted = (id) => {
    update("contacts", id, { contacted: true });
    setContacts(getAll("contacts"));
  };

  return (
    <div className="page-content">
      <div style={{ marginBottom:28 }}>
        <div className="accent-line" />
        <h1 style={{fontSize:28,fontWeight:700,letterSpacing:"-0.5px"}}>Contact Me</h1>
        <p style={{color:C.textSub,marginTop:8,fontSize:14}}>Manage enquiries from potential clients.</p>
      </div>
      <div className="card" style={{padding:0,overflow:"hidden"}}>
        <table className="dark-table">
          <thead>
            <tr>{["Name","Preference","Phone","Mobile","Email","Best Time","Date","Status",""].map(h=><th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {contacts.map((c)=>(
              <tr key={c.id} style={{opacity:c.contacted?0.5:1}}>
                <td><div style={{fontWeight:500}}>{c.name}</div></td>
                <td><span className="badge badge-blue">{c.preference}</span></td>
                <td style={{color:C.textSub,fontSize:13}}>{c.phone}</td>
                <td style={{color:C.textSub,fontSize:13}}>{c.mobile}</td>
                <td style={{color:C.textSub,fontSize:13}}>{c.email}</td>
                <td style={{fontSize:13}}>{c.bestTime}</td>
                <td style={{color:C.textMuted,fontSize:13}}>{c.date}</td>
                <td><span className={`badge ${c.contacted?"badge-green":"badge-amber"}`}>{c.contacted?"Contacted":"Pending"}</span></td>
                <td>{!c.contacted && <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>markContacted(c.id)}>Mark contacted</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── FIND A PT ─────────────────────────────────────────────────────────────────
// ── PT PROFILE PAGE (public, viewed from Find a PT) ──────────────────────────
// ── STAR RATING DISPLAY ───────────────────────────────────────────────────────
function StarRating({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} onClick={() => onChange?.(n)}
          style={{ fontSize: 20, cursor: onChange ? "pointer" : "default",
            color: n <= value ? "#fbbf24" : C.border, transition: "color 0.15s" }}>★</span>
      ))}
    </div>
  );
}

// ── TESTIMONIALS PAGE (PT dashboard) ─────────────────────────────────────────
function TestimonialsPage({ user }) {
  const PT_ID = 1;
  const isDemo = typeof user?.id !== "number";
  const ptId = isDemo ? PT_ID : user.id;
  const mapT = t => ({ id:t.id, clientName:t.client_name, text:t.text, rating:t.rating, date:t.date, visible:t.visible });
  const [testimonials, setTestimonials] = useState(() => isDemo ? getTestimonials(PT_ID) : []);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ clientName: "", text: "", rating: 5, date: "" });
  const [error, setError] = useState("");

  const refresh = async () => {
    if (isDemo) { setTestimonials(getTestimonials(PT_ID)); return; }
    const { data } = await supabase.from("testimonials").select("*").eq("pt_id", ptId).order("id", { ascending:false });
    setTestimonials((data || []).map(mapT));
  };
  useEffect(() => { if (!isDemo) refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => { setDraft({ clientName: "", text: "", rating: 5, date: new Date().toISOString().slice(0,10) }); setEditId(null); setError(""); setShowForm(true); };
  const openEdit = (t) => { setDraft({ clientName: t.clientName, text: t.text, rating: t.rating, date: t.date }); setEditId(t.id); setError(""); setShowForm(true); };

  const save = async () => {
    if (!draft.clientName.trim()) { setError("Client name is required."); return; }
    if (!draft.text.trim()) { setError("Testimonial text is required."); return; }
    try {
      if (isDemo) { editId ? updateTestimonial(PT_ID, editId, draft) : addTestimonial(PT_ID, draft); }
      else {
        const row = { client_name: draft.clientName.trim(), text: draft.text.trim(), rating: draft.rating, date: draft.date };
        const { error } = editId ? await supabase.from("testimonials").update(row).eq("id", editId) : await supabase.from("testimonials").insert({ pt_id: ptId, ...row });
        if (error) throw error;
      }
      await refresh(); setShowForm(false);
    } catch (e) { setError(e.message); }
  };

  const toggleVisible = async (t) => {
    if (isDemo) updateTestimonial(PT_ID, t.id, { visible: !t.visible });
    else await supabase.from("testimonials").update({ visible: !t.visible }).eq("id", t.id);
    refresh();
  };
  const remove = async (t) => {
    if (!window.confirm(`Remove testimonial from ${t.clientName}?`)) return;
    if (isDemo) deleteTestimonial(PT_ID, t.id);
    else await supabase.from("testimonials").delete().eq("id", t.id);
    refresh();
  };

  return (
    <div className="page-content">
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:28 }}>
        <div>
          <div className="accent-line" />
          <h1 style={{ fontSize:28, fontWeight:700, letterSpacing:"-0.5px" }}>Testimonials</h1>
          <div style={{ fontSize:13, color:C.textMuted, marginTop:6 }}>Manage client reviews shown on your public profile.</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Testimonial</button>
      </div>

      {testimonials.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 20px", color:C.textMuted }}>
          <div style={{ fontSize:48, marginBottom:16 }}>⭐</div>
          <div style={{ fontWeight:600, fontSize:17, marginBottom:8 }}>No testimonials yet</div>
          <div style={{ fontSize:13, marginBottom:24 }}>Add your first client review to show on your profile.</div>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Testimonial</button>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:20 }}>
          {testimonials.map(t => (
            <div key={t.id} className="card" style={{ opacity: t.visible ? 1 : 0.5, position:"relative" }}>
              {!t.visible && (
                <span style={{ position:"absolute", top:12, right:12, fontSize:10, padding:"2px 8px", background:C.surfaceAlt, color:C.textMuted, borderRadius:20, border:`1px solid ${C.border}` }}>Hidden</span>
              )}
              <StarRating value={t.rating} />
              <p style={{ fontSize:14, color:C.textSub, lineHeight:1.75, margin:"12px 0", fontStyle:"italic" }}>"{t.text}"</p>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{t.clientName}</div>
                  <div style={{ fontSize:11, color:C.textMuted }}>{t.date}</div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={() => toggleVisible(t)}>{t.visible ? "Hide" : "Show"}</button>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={() => openEdit(t)}>Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:11, color:C.danger }} onClick={() => remove(t)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth:500 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:600, fontSize:16 }}>{editId ? "Edit Testimonial" : "Add Testimonial"}</span>
              <CloseBtn onClick={() => setShowForm(false)} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label className="label">Client name</label>
              <input className="input" placeholder="e.g. Sarah M." value={draft.clientName} onChange={e => setDraft(d => ({...d, clientName: e.target.value}))} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label className="label">Rating</label>
              <StarRating value={draft.rating} onChange={v => setDraft(d => ({...d, rating: v}))} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label className="label">Testimonial</label>
              <textarea className="input" rows={4} placeholder="What did the client say?" value={draft.text} onChange={e => setDraft(d => ({...d, text: e.target.value}))} />
            </div>
            <div style={{ marginBottom:16 }}>
              <label className="label">Date</label>
              <input className="input" type="date" value={draft.date} onChange={e => setDraft(d => ({...d, date: e.target.value}))} />
            </div>
            {error && <div style={{ padding:"10px 14px", background:"rgba(255,77,77,0.1)", border:"1px solid rgba(255,77,77,0.3)", borderRadius:8, fontSize:13, color:C.danger, marginBottom:12 }}>{error}</div>}
            <div style={{ display:"flex", gap:10 }}>
              <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editId ? "Save Changes" : "Add Testimonial"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SOCIAL EMBED HELPERS ──────────────────────────────────────────────────────
// Returns an embed iframe src if the URL is an embeddable post/video, else null.
function toInstaEmbed(url) {
  if (!url) return null;
  // instagram.com/p/CODE or instagram.com/reel/CODE
  const m = url.match(/instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
  return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed/` : null;
}
function toTikTokEmbed(url) {
  if (!url) return null;
  // tiktok.com/@handle/video/ID
  const m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  return m ? `https://www.tiktok.com/embed/v2/${m[1]}` : null;
}
function toSocialHref(key, val) {
  if (!val) return "#";
  if (val.startsWith("http")) return val;
  const map = { instagram: "https://instagram.com/", tiktok: "https://tiktok.com/@", twitter: "https://x.com/" };
  return (map[key] || "") + val.replace("@", "");
}

function PTProfilePage({ pt: initialPt, onClose, onContact, editable = false, accountEmail = "" }) {
  const [pt, setPt] = useState(initialPt);
  const [editingAvatar, setEditingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(pt.avatar || "");
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({});
  const testimonials = getTestimonials(pt.id).filter(t => t.visible);
  const initials = (pt.name || "").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();

  const openEditProfile = () => {
    setProfileDraft({
      location:              pt.location              || "",
      specialism:            pt.specialism            || "",
      rate:                  pt.rate                  || "",
      experience:            pt.experience            || "",
      membership:            pt.membership            || "REPS",
      level:                 pt.level                 || 3,
      gender:                pt.gender                || "M",
      instagram:             pt.instagram             || "",
      youtube:               pt.youtube               || "",
      tiktok:                pt.tiktok                || "",
      facebook:              pt.facebook              || "",
      linkedin:              pt.linkedin              || "",
      twitter:               pt.twitter               || "",
      contactMeEmailNotif:   pt.contactMeEmailNotif   ?? false,
    });
    setEditingProfile(true);
  };

  const saveProfile = () => {
    const patch = {
      ...profileDraft,
      rate:  parseFloat(profileDraft.rate)  || 0,
      level: parseInt(profileDraft.level)   || 3,
    };
    const updated = update("pts", pt.id, patch);
    setPt(updated);
    setEditingProfile(false);
  };

  const updDraft = (k, v) => setProfileDraft(d => ({ ...d, [k]: v }));

  const saveAvatarUrl = () => {
    const updated = update("pts", pt.id, { avatar: avatarUrl.trim() });
    setPt(updated);
    setEditingAvatar(false);
  };

  const removeAvatar = () => {
    const updated = update("pts", pt.id, { avatar: "" });
    setPt(updated);
    setAvatarUrl("");
  };

  // ── social link helpers ─────────────────────────────────────────────────────
  const SOCIAL = [
    { key: "instagram", icon: "📸", label: "Instagram" },
    { key: "youtube",   icon: "▶️",  label: "YouTube"   },
    { key: "tiktok",    icon: "🎵", label: "TikTok"    },
    { key: "facebook",  icon: "📘", label: "Facebook"  },
    { key: "linkedin",  icon: "💼", label: "LinkedIn"  },
    { key: "twitter",   icon: "𝕏",  label: "Twitter"   },
  ].filter(s => pt[s.key]);

  const ytEmbed    = toYouTubeEmbed(pt.youtube);
  const instaEmbed = toInstaEmbed(pt.instagram);
  const ttEmbed    = toTikTokEmbed(pt.tiktok);

  const avgRating = testimonials.length ? (testimonials.reduce((s,t) => s + t.rating, 0) / testimonials.length).toFixed(1) : null;

  return (
    <div className="page-content" style={{ maxWidth: 960 }}>

      {/* ── Back nav ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ color:C.textMuted }}>← {editable ? "Back to dashboard" : "Back to results"}</button>
        {editable && <button className="btn btn-primary btn-sm" onClick={openEditProfile}>✏️ Edit Profile</button>}
      </div>

      {/* ── Edit profile modal ── */}
      {editable && editingProfile && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth:560, maxHeight:"85vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h2 style={{ fontSize:18, fontWeight:700 }}>Edit Profile</h2>
              <CloseBtn onClick={() => setEditingProfile(false)} />
            </div>

            {/* Location + Rate */}
            <div className="grid-2-form" style={{ gap:12, marginBottom:12 }}>
              <div><label className="label">Location / Area</label><input className="input" value={profileDraft.location} onChange={e=>updDraft("location",e.target.value)} placeholder="e.g. Clapham, London" /></div>
              <div><label className="label">Rate (£/hour)</label><input className="input" type="number" value={profileDraft.rate} onChange={e=>updDraft("rate",e.target.value)} placeholder="e.g. 40" /></div>
            </div>

            {/* Specialism + Gender */}
            <div className="grid-2-form" style={{ gap:12, marginBottom:12 }}>
              <div>
                <label className="label">Specialism</label>
                <select className="input" value={profileDraft.specialism} onChange={e=>updDraft("specialism",e.target.value)}>
                  <option value="">Select…</option>
                  {["Weight Loss","Muscle Building","Muscular Sculpting","Sports Recovery","Post-natal Fitness","Cardio Fitness","Strength & Conditioning","Nutrition","Rehabilitation","Boxing & Combat"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Gender</label>
                <select className="input" value={profileDraft.gender} onChange={e=>updDraft("gender",e.target.value)}>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
            </div>

            {/* Membership + Level */}
            <div className="grid-2-form" style={{ gap:12, marginBottom:12 }}>
              <div>
                <label className="label">Professional body</label>
                <select className="input" value={profileDraft.membership} onChange={e=>updDraft("membership",e.target.value)}>
                  {["REPS","NRPT","YMCA","Other"].map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Level</label>
                <select className="input" value={profileDraft.level} onChange={e=>updDraft("level",e.target.value)}>
                  {[1,2,3,4].map(l=><option key={l} value={l}>Level {l}</option>)}
                </select>
              </div>
            </div>

            {/* About */}
            <div style={{ marginBottom:12 }}>
              <label className="label">About Me</label>
              <textarea className="input" rows={4} style={{ resize:"vertical" }} value={profileDraft.experience} onChange={e=>updDraft("experience",e.target.value)} placeholder="Describe your experience and approach…" />
            </div>

            {/* Social links */}
            <div style={{ marginBottom:8, fontSize:12, fontWeight:600, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px" }}>Social media</div>
            <div className="grid-2-form" style={{ gap:10, marginBottom:10 }}>
              <div><label className="label">📸 Instagram</label><input className="input" placeholder="@handle or post URL" value={profileDraft.instagram} onChange={e=>updDraft("instagram",e.target.value)} /></div>
              <div><label className="label">▶️ YouTube</label><input className="input" placeholder="Channel or video URL" value={profileDraft.youtube} onChange={e=>updDraft("youtube",e.target.value)} /></div>
            </div>
            <div className="grid-2-form" style={{ gap:10, marginBottom:10 }}>
              <div><label className="label">🎵 TikTok</label><input className="input" placeholder="@handle or video URL" value={profileDraft.tiktok} onChange={e=>updDraft("tiktok",e.target.value)} /></div>
              <div><label className="label">📘 Facebook</label><input className="input" placeholder="Page URL" value={profileDraft.facebook} onChange={e=>updDraft("facebook",e.target.value)} /></div>
            </div>
            <div className="grid-2-form" style={{ gap:10, marginBottom:20 }}>
              <div><label className="label">💼 LinkedIn</label><input className="input" placeholder="Profile URL" value={profileDraft.linkedin} onChange={e=>updDraft("linkedin",e.target.value)} /></div>
              <div><label className="label">𝕏 Twitter / X</label><input className="input" placeholder="@handle" value={profileDraft.twitter} onChange={e=>updDraft("twitter",e.target.value)} /></div>
            </div>

            {/* Email notification opt-in */}
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background:C.primaryGlow, border:`1px solid ${C.primary}20`, borderRadius:8, marginBottom:20 }}>
              <label style={{ display:"flex", alignItems:"center", gap:10, flex:1, cursor:"pointer" }}>
                <input type="checkbox" checked={!!profileDraft.contactMeEmailNotif} onChange={e=>updDraft("contactMeEmailNotif", e.target.checked)} />
                <div>
                  <div style={{ fontSize:13, color:C.primary, fontWeight:600 }}>Email me when I receive a new enquiry</div>
                  <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>Get notified by email each time a client submits a Contact Me request</div>
                </div>
              </label>
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button className="btn btn-ghost" style={{ flex:1 }} onClick={() => setEditingProfile(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:2 }} onClick={saveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ── HERO CARD ── */}
      <div className="card" style={{ marginBottom:20, position:"relative", overflow:"hidden" }}>
        {/* Free tier badge */}
        <div style={{ position:"absolute", top:16, right:16, fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:20, background:C.surfaceAlt, color:C.textMuted, border:`1px solid ${C.border}`, letterSpacing:"0.5px", textTransform:"uppercase" }}>Free Profile</div>

        <div style={{ display:"flex", gap:28, alignItems:"flex-start", flexWrap:"wrap" }}>
          {/* Avatar — URL-based, no file hosting */}
          <div style={{ flexShrink:0, textAlign:"center" }}>
            {pt.avatar
              ? <img src={pt.avatar} alt={pt.name} style={{ width:108, height:108, borderRadius:"50%", objectFit:"cover", border:`3px solid ${C.primary}${editable?"80":"40"}` }} onError={e => { e.target.style.display="none"; }} />
              : <div style={{ width:108, height:108, borderRadius:"50%", background:C.primaryGlow, border:`2px ${editable?"dashed":"solid"} ${C.primary}${editable?"80":"40"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:36, fontWeight:700, color:C.primary }}>{initials}</div>
            }
            {editable && !editingAvatar && (
              <button onClick={() => setEditingAvatar(true)} style={{ marginTop:6, fontSize:11, background:"none", border:"none", color:C.primary, cursor:"pointer", textDecoration:"underline" }}>
                {pt.avatar ? "Change photo URL" : "Add photo URL"}
              </button>
            )}
            {editable && editingAvatar && (
              <div style={{ marginTop:8, width:120 }}>
                <input className="input" placeholder="Paste image URL" value={avatarUrl} onChange={e => setAvatarUrl(e.target.value)} style={{ fontSize:11, padding:"5px 8px", marginBottom:6 }} />
                <div style={{ display:"flex", gap:4 }}>
                  <button className="btn btn-primary btn-sm" style={{ fontSize:10, flex:1 }} onClick={saveAvatarUrl}>Save</button>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:10 }} onClick={() => setEditingAvatar(false)}>✕</button>
                </div>
                {pt.avatar && <button onClick={removeAvatar} style={{ marginTop:4, fontSize:10, background:"none", border:"none", color:C.danger, cursor:"pointer" }}>Remove photo</button>}
              </div>
            )}
            {editable && !editingAvatar && <div style={{ fontSize:10, color:C.textMuted, marginTop:4, maxWidth:110 }}>Link from LinkedIn, website, etc.</div>}
          </div>

          {/* Name & details */}
          <div style={{ flex:1, minWidth:220 }}>
            <div className="accent-line" />
            <h1 style={{ fontSize:32, fontWeight:800, letterSpacing:"-0.8px", marginBottom:4 }}>{pt.name}</h1>
            {/* Login email — own profile only; never shown on the public profile */}
            {editable && accountEmail && (
              <div style={{ fontSize:13, color:C.textMuted, marginBottom:8, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                <span>✉️</span>
                <span style={{ color:C.textSub, fontWeight:600 }}>{accountEmail}</span>
                <span style={{ fontSize:11, color:C.textMuted }}>· your login email (only visible to you)</span>
              </div>
            )}
            <div style={{ fontSize:15, color:C.textSub, marginBottom:12 }}>
              📍 {pt.location} &nbsp;·&nbsp; <span style={{ color:C.primary, fontWeight:700 }}>£{pt.rate}/hr</span>
              {avgRating && <span style={{ marginLeft:12, color:"#fbbf24", fontWeight:600 }}>★ {avgRating} <span style={{ color:C.textMuted, fontWeight:400, fontSize:12 }}>({testimonials.length} review{testimonials.length !== 1 ? "s" : ""})</span></span>}
            </div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:SOCIAL.length ? 16 : 0 }}>
              <span className="badge badge-lime">{pt.specialism}</span>
              <span className="badge badge-blue">{pt.membership} · Level {pt.level}</span>
              <span style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, padding:"3px 10px", borderRadius:20, fontSize:11, color:C.textSub }}>{pt.gender === "M" ? "Male" : "Female"}</span>
            </div>

            {/* Social icons */}
            {SOCIAL.length > 0 && (
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {SOCIAL.map(s => (
                  <a key={s.key} href={toSocialHref(s.key, pt[s.key])} target="_blank" rel="noreferrer"
                    style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:600, background:C.surfaceAlt, color:C.textSub, border:`1px solid ${C.border}`, textDecoration:"none", transition:"all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textSub; }}
                  >
                    {s.icon} {s.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CTA — only show on public (non-editable) profile */}
          {!editable && (
            <div style={{ flexShrink:0 }}>
              <button className="btn btn-primary" style={{ fontSize:15, padding:"12px 28px" }} onClick={onContact}>Contact Me →</button>
            </div>
          )}
        </div>
      </div>

      {/* ── About ── */}
      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:12, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.5px", color:C.textMuted }}>About</div>
        <p style={{ fontSize:15, color:C.textSub, lineHeight:1.85 }}>{pt.experience}</p>
      </div>

      {/* ── Qualifications + At a glance ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:16, marginBottom:20 }}>
        <div className="card">
          <div style={{ fontWeight:700, fontSize:12, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.5px", color:C.textMuted }}>Qualifications</div>
          <ul style={{ margin:0, padding:0, listStyle:"none" }}>
            {(pt.qualifications || []).map(q => (
              <li key={q} style={{ display:"flex", alignItems:"center", gap:8, fontSize:14, color:C.textSub, paddingBottom:8, borderBottom:`1px solid ${C.border}`, marginBottom:8 }}>
                <span style={{ color:C.primary, fontWeight:700 }}>✓</span> {q}
              </li>
            ))}
          </ul>
        </div>
        <div className="card">
          <div style={{ fontWeight:700, fontSize:12, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.5px", color:C.textMuted }}>At a glance</div>
          {[["📍 Location", pt.location], ["🏋️ Specialism", pt.specialism], ["🏅 Membership", `${pt.membership} (Level ${pt.level})`], ["💷 Rate", `£${pt.rate}/hr`]].map(([lbl, val]) => (
            <div key={lbl} style={{ display:"flex", justifyContent:"space-between", fontSize:14, paddingBottom:8, borderBottom:`1px solid ${C.border}`, marginBottom:8 }}>
              <span style={{ color:C.textMuted }}>{lbl}</span>
              <span style={{ color:C.text, fontWeight:500 }}>{val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Social media embeds ── */}
      {(ytEmbed || instaEmbed || ttEmbed) && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:12, marginBottom:16, textTransform:"uppercase", letterSpacing:"0.5px", color:C.textMuted }}>Featured Content</div>
          <div style={{ display:"grid", gridTemplateColumns: (ytEmbed && (instaEmbed || ttEmbed)) ? "2fr 1fr" : "1fr", gap:16, alignItems:"start" }}>
            {/* YouTube — wide */}
            {ytEmbed && (
              <div className="card" style={{ padding:0, overflow:"hidden" }}>
                <div style={{ position:"relative", paddingBottom:"56.25%", height:0, background:"#000" }}>
                  <iframe src={ytEmbed} title="YouTube" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                    style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", border:"none" }} />
                </div>
              </div>
            )}
            {/* Instagram post */}
            {instaEmbed && (
              <div className="card" style={{ padding:0, overflow:"hidden" }}>
                <iframe src={instaEmbed} title="Instagram" allowTransparency scrolling="no" frameBorder="0"
                  style={{ width:"100%", minHeight:480, border:"none", display:"block" }} />
              </div>
            )}
            {/* TikTok video */}
            {ttEmbed && !instaEmbed && (
              <div className="card" style={{ padding:0, overflow:"hidden" }}>
                <iframe src={ttEmbed} title="TikTok" allowFullScreen scrolling="no" allow="encrypted-media"
                  style={{ width:"100%", height:740, border:"none", display:"block" }} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Testimonials ── */}
      {testimonials.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:12, marginBottom:16, textTransform:"uppercase", letterSpacing:"0.5px", color:C.textMuted }}>What clients say</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 }}>
            {testimonials.map(t => (
              <div key={t.id} className="card" style={{ background:C.primaryGlow, border:`1px solid ${C.primary}20` }}>
                <StarRating value={t.rating} />
                <p style={{ fontSize:14, color:C.textSub, lineHeight:1.75, margin:"10px 0", fontStyle:"italic" }}>"{t.text}"</p>
                <div style={{ fontWeight:600, fontSize:12, color:C.textMuted }}>{t.clientName} · {t.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Footer CTA ── */}
      <div style={{ display:"flex", gap:10, justifyContent:"space-between", alignItems:"center", padding:"20px 24px", background:C.surfaceAlt, borderRadius:12, border:`1px solid ${C.border}` }}>
        <button className="btn btn-outline" onClick={onClose}>← {editable ? "Back to dashboard" : "Back to results"}</button>
        {!editable && <button className="btn btn-primary" style={{ fontSize:15, padding:"12px 28px" }} onClick={onContact}>Contact {pt.name.split(" ")[0]} →</button>}
      </div>
    </div>
  );
}

function FindPT() {
  const [pts] = useState(() => getAll("pts"));
  const [location, setLocation] = useState("");
  const [specialism, setSpecialism] = useState("");
  const [gender, setGender] = useState("no preference");
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [contactModal, setContactModal] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({ firstName:"",lastName:"",phoneCode:"+44",phone:"",mobileCode:"+44",mobile:"",email:"",preference:"SMS",msgApp:"",msgPhoneCode:"+44",msgPhone:"",msgHandle:"" });
  const [profilePT, setProfilePT] = useState(null);
  const [locSug, setLocSug] = useState([]);         // location autocomplete suggestions
  const [showLocSug, setShowLocSug] = useState(false);
  const [picked, setPicked] = useState(null);       // {label,lat,lng} of the chosen place — kept for later radius matching
  const geoTimer = useRef();

  const onLocChange = (v) => {
    setLocation(v); setPicked(null);
    clearTimeout(geoTimer.current);
    if (v.trim().length < 2) { setLocSug([]); setShowLocSug(false); return; }
    geoTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/resolve-pt-slug?geocode=${encodeURIComponent(v.trim())}`);
        const data = await r.json();
        setLocSug(Array.isArray(data) ? data : []); setShowLocSug(true);
      } catch { setLocSug([]); }
    }, 300);
  };
  const pickLoc = (s) => { setLocation(s.label); setPicked(s); setShowLocSug(false); setLocSug([]); };

  if (profilePT) {
    return <PTProfilePage pt={profilePT} onClose={() => setProfilePT(null)} onContact={() => { setContactModal(profilePT); setSubmitted(false); setProfilePT(null); }} />;
  }

  const search = () => {
    setSearched(true);
    const loc = (picked?.town || location).trim().toLowerCase();
    setResults(pts.filter(p =>
      (gender==="no preference"||(gender==="M"&&p.gender==="M")||(gender==="F"&&p.gender==="F")) &&
      (!specialism||p.specialism.toLowerCase().includes(specialism.toLowerCase())) &&
      (!loc || (p.location||"").toLowerCase().includes(loc))
    ));
  };

  return (
    <div className="page-content" style={{ maxWidth: 960 }}>
      <div style={{ textAlign:"center", marginBottom:48 }}>
        <div className="accent-line" style={{margin:"0 auto 20px"}} />
        <h1 style={{fontSize:40,fontWeight:700,letterSpacing:"-1px"}}>Find a Personal Trainer</h1>
        <p style={{color:C.textSub,marginTop:10,fontSize:16}}>Search our network of qualified personal trainers</p>
      </div>

      <div className="card" style={{marginBottom:36}}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:12, alignItems:"flex-end" }}>
          <div style={{ position:"relative" }}><label className="label">Location</label>
            <input className="input" placeholder="Town or postcode…" value={location} autoComplete="off"
              onChange={e=>onLocChange(e.target.value)}
              onFocus={()=>{ if(locSug.length) setShowLocSug(true); }}
              onBlur={()=>setTimeout(()=>setShowLocSug(false),150)} />
            {showLocSug && locSug.length>0 && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:30, background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, marginTop:4, maxHeight:220, overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,0.45)" }}>
                {locSug.map((s,i)=>(
                  <div key={i} onMouseDown={()=>pickLoc(s)}
                    style={{ padding:"9px 12px", fontSize:13, cursor:"pointer", color:C.text, borderBottom: i<locSug.length-1?`1px solid ${C.border}`:"none" }}
                    onMouseEnter={e=>e.currentTarget.style.background=C.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    📍 {s.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div><label className="label">Specialism</label>
            <select aria-label="Specialism" className="input" value={specialism} onChange={e=>setSpecialism(e.target.value)}>
              <option value="">Any</option><option>Weight Loss</option><option>Muscular Sculpting</option><option>Post-natal</option><option>Sports Recovery</option><option>Cardio Fitness</option>
            </select>
          </div>
          <div><label className="label">Gender preference</label>
            <select aria-label="Gender preference" className="input" value={gender} onChange={e=>setGender(e.target.value)}>
              <option value="no preference">No preference</option><option value="M">Male</option><option value="F">Female</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={search} style={{height:42}}>Search</button>
        </div>
      </div>

      {searched && (
        <>
          <div style={{color:C.textMuted,fontSize:13,marginBottom:20,fontWeight:500,textTransform:"uppercase",letterSpacing:"0.5px"}}>{results.length} trainer{results.length!==1?"s":""} found</div>
          {results.map(pt=>(
            <div key={pt.id} className="card" style={{ marginBottom:16, display:"grid", gridTemplateColumns:"1fr auto", gap:24 }}>
              <div>
                <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:12 }}>
                  <Avatar name={pt.name} size={48} />
                  <div>
                    <div style={{fontWeight:700,fontSize:17}}>{pt.name}</div>
                    <div style={{fontSize:13,color:C.textSub,marginTop:2}}>{pt.location} · <span style={{color:C.primary}}>£{pt.rate}/hr</span></div>
                  </div>
                </div>
                <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
                  <span className="badge badge-lime">{pt.specialism}</span>
                  <span className="badge badge-blue">{pt.membership} Level {pt.level}</span>
                  <span style={{background:C.surfaceAlt,border:`1px solid ${C.border}`,padding:"3px 10px",borderRadius:20,fontSize:11,color:C.textSub}}>{pt.gender==="M"?"Male":"Female"}</span>
                </div>
                <p style={{fontSize:14,color:C.textSub,lineHeight:1.7,marginBottom:8}}>{pt.experience}</p>
                <div style={{fontSize:12,color:C.textMuted}}>Qualifications: {(pt.qualifications || []).join(" · ")}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setProfilePT(pt)}>View Profile</button>
                <button className="btn btn-primary btn-sm" onClick={()=>{setContactModal(pt);setSubmitted(false);}}>Contact Me</button>
              </div>
            </div>
          ))}
          {results.length===0 && (
            <div style={{textAlign:"center",padding:56,color:C.textMuted}}>
              <div style={{fontSize:36,marginBottom:12}}>◎</div>
              No trainers found. Try adjusting your filters.
            </div>
          )}
        </>
      )}

      {contactModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{maxWidth:440}}>
            {submitted ? (
              <div style={{textAlign:"center",padding:16}}>
                <div style={{ width:64,height:64,borderRadius:"50%",background:C.primaryGlow,border:`2px solid ${C.primary}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 20px" }}>✓</div>
                <div style={{fontWeight:700,fontSize:18,marginBottom:8}}>Request sent!</div>
                <p style={{fontSize:13,color:C.textSub,marginBottom:24,lineHeight:1.6}}>{contactModal.name} will be in touch at your preferred time. Mention myPTOnline when they call for your discount!</p>
                <button className="btn btn-primary btn-sm" onClick={()=>setContactModal(null)}>Back to results</button>
              </div>
            ) : (
              <>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                  <span style={{fontWeight:600,fontSize:15}}>Contact Me — {contactModal.name}</span>
                  <CloseBtn onClick={()=>setContactModal(null)} />
                </div>
                <div style={{ padding:"10px 14px", background:C.amberLight, border:`1px solid ${C.amber}30`, borderRadius:8, fontSize:13, color:C.amber, marginBottom:18 }}>
                  Mention myPTOnline when the PT calls to receive your discount!
                </div>
                {/* ── Name ── */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <div><label className="label">First name</label><input className="input" value={contactForm.firstName} onChange={e=>setContactForm(f=>({...f,firstName:e.target.value}))} /></div>
                  <div><label className="label">Last name</label><input className="input" value={contactForm.lastName} onChange={e=>setContactForm(f=>({...f,lastName:e.target.value}))} /></div>
                </div>

                {/* ── Email ── */}
                <div style={{marginBottom:10}}><label className="label">Email</label><input className="input" type="email" value={contactForm.email} onChange={e=>setContactForm(f=>({...f,email:e.target.value}))} /></div>

                {/* ── Phone numbers ── */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <PhoneInput label="Landline" codeKey="phoneCode" numKey="phone" form={contactForm} upd={(k,v)=>setContactForm(f=>({...f,[k]:v}))} COUNTRY_CODES={COUNTRY_CODES} optional />
                  <PhoneInput label="Mobile"   codeKey="mobileCode" numKey="mobile" form={contactForm} upd={(k,v)=>setContactForm(f=>({...f,[k]:v}))} COUNTRY_CODES={COUNTRY_CODES} />
                </div>

                {/* ── Contact preference ── */}
                <div style={{marginBottom:16}}>
                  <label className="label">Contact preference</label>
                  <select aria-label="Contact preference" className="input" value={contactForm.preference} onChange={e=>setContactForm(f=>({...f,preference:e.target.value}))}>
                    <option>SMS</option><option>Mobile Call</option><option>Landline</option><option>Email</option>
                  </select>
                </div>

                {/* ── Messaging app ── */}
                <div style={{marginBottom:16}}>
                  <label className="label">Messaging app <span style={{color:C.textMuted,fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></label>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                    {MSG_APPS.map(app => {
                      const active = contactForm.msgApp === app.id;
                      return (
                        <button
                          key={app.id}
                          type="button"
                          onClick={() => setContactForm(f => ({ ...f, msgApp: active ? "" : app.id, msgPhone:"", msgHandle:"" }))}
                          style={{
                            display:"inline-flex", alignItems:"center", gap:5,
                            padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:600,
                            cursor:"pointer", transition:"all 0.15s",
                            background: active ? C.primary : C.surfaceAlt,
                            color: active ? "#0c0c0e" : C.textSub,
                            border: `1px solid ${active ? C.primary : C.border}`,
                          }}
                        >
                          <span>{app.icon}</span> {app.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Phone-based app input */}
                  {contactForm.msgApp && MSG_APPS.find(a=>a.id===contactForm.msgApp)?.type==="phone" && (
                    <PhoneInput
                      label={`${MSG_APPS.find(a=>a.id===contactForm.msgApp)?.label} number`}
                      codeKey="msgPhoneCode" numKey="msgPhone"
                      form={contactForm} upd={(k,v)=>setContactForm(f=>({...f,[k]:v}))}
                      COUNTRY_CODES={COUNTRY_CODES}
                      icon={MSG_APPS.find(a=>a.id===contactForm.msgApp)?.icon}
                    />
                  )}

                  {/* Handle-based app input */}
                  {contactForm.msgApp && MSG_APPS.find(a=>a.id===contactForm.msgApp)?.type==="handle" && (
                    <div>
                      <label className="label">{MSG_APPS.find(a=>a.id===contactForm.msgApp)?.label} username / handle</label>
                      <div style={{ display:"flex", alignItems:"center", border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden", background:C.surfaceAlt }}>
                        <span style={{ padding:"0 10px", fontSize:16, borderRight:`1px solid ${C.border}`, display:"flex", alignItems:"center" }}>
                          {MSG_APPS.find(a=>a.id===contactForm.msgApp)?.icon}
                        </span>
                        <span style={{ padding:"0 6px", color:C.textMuted, fontSize:13 }}>@</span>
                        <input
                          className="input"
                          type="text"
                          placeholder="your_handle"
                          value={contactForm.msgHandle}
                          onChange={e=>setContactForm(f=>({...f,msgHandle:e.target.value}))}
                          style={{ border:"none", borderRadius:0, background:"transparent", flex:1 }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <button className="btn btn-primary" style={{width:"100%"}} onClick={async () => {
                  // Save contact to DB
                  const contact = {
                    name: `${contactForm.firstName} ${contactForm.lastName}`.trim(),
                    phone: contactForm.phone ? `${contactForm.phoneCode} ${contactForm.phone}` : "",
                    mobile: contactForm.mobile ? `${contactForm.mobileCode} ${contactForm.mobile}` : "",
                    email: contactForm.email,
                    preference: contactForm.preference,
                    bestTime: "",
                    date: new Date().toLocaleDateString("en-GB"),
                    contacted: false,
                    ptId: contactModal.id,
                  };
                  add("contacts", contact);
                  // Notify PT by email if they opted in
                  fetch("/api/notify-contact", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ptId: contactModal.id, ptName: contactModal.name, contact }),
                  }).catch(() => {});
                  setSubmitted(true);
                }}>Submit Request</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── CLIENT DASHBOARD ──────────────────────────────────────────────────────────
// ── Client session feedback modal ─────────────────────────────────────────────
function ClientSessionFeedbackModal({ session, onClose, onSubmit }) {
  const [rating, setRating]   = useState(session.clientRating || 0);
  const [comment, setComment] = useState(session.clientComment || "");
  const [hover, setHover]     = useState(0);
  const plan = findPlanForSession(session.name);
  const alreadySubmitted = !!session.clientComment || !!session.clientRating;

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 600, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Session {session.id} — {session.date}</span>
          <CloseBtn onClick={onClose} />
        </div>
        <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 18 }}>{session.name}{session.time ? ` · ${session.time}` : ""}</div>

        {plan && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Your session plan</div>
            <div style={{ marginBottom: 8, padding: "10px 14px", background: C.primaryGlow, border: `1px solid ${C.primary}20`, borderRadius: 8, fontSize: 13, color: C.primary }}>{plan.name}</div>
            <div style={{ overflowX: "auto" }}>
              <table className="dark-table" style={{ fontSize: 13 }}>
                <thead><tr><th>Exercise</th><th>Sets</th><th>Reps</th><th>Kg</th></tr></thead>
                <tbody>
                  {(plan.exercises || []).map((e, i) => { const ex = normalizeEx(e); return (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{ex.name}</td>
                      <td style={{ textAlign: "center", color: C.textSub }}>{ex.sets}</td>
                      <td style={{ textAlign: "center", color: C.textSub }}>{ex.reps}</td>
                      <td style={{ textAlign: "center", color: C.textSub }}>{ex.kg ?? "—"}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(() => {
          const perf = parsePerf(session.performance);
          const names = Object.keys(perf);
          if (!names.length) return null;
          return (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Your results (actual)</div>
              <div style={{ overflowX: "auto" }}>
                <table className="dark-table" style={{ fontSize: 13 }}>
                  <thead><tr><th>Exercise</th><th>Set</th><th>Reps</th><th>Kg</th></tr></thead>
                  <tbody>
                    {names.flatMap(n => (perf[n] || []).map((s, i) => (
                      <tr key={n + i}>
                        <td style={{ fontWeight: 500 }}>{i === 0 ? n : ""}</td>
                        <td style={{ textAlign: "center", color: C.textSub }}>{i + 1}</td>
                        <td style={{ textAlign: "center", color: C.textSub }}>{s.reps || "—"}</td>
                        <td style={{ textAlign: "center", color: C.textSub }}>{s.kg || "—"}</td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        <div style={{ marginBottom: 18 }}>
          <label className="label">Your PT's feedback</label>
          <div style={{ padding: "10px 14px", background: C.surfaceAlt, borderRadius: 8, fontSize: 13, color: session.coachNotes ? C.textSub : C.textMuted }}>
            {session.coachNotes || "Your PT hasn't added their feedback for this session yet."}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label className="label">How did the session go?</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <span key={n} onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                style={{ fontSize: 28, cursor: "pointer", color: (hover || rating) >= n ? C.primary : C.border, transition: "color .1s" }}>★</span>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <label className="label">Your feedback</label>
          <textarea className="input" rows={4} value={comment} onChange={e => setComment(e.target.value)} placeholder="How did you find the session? Anything your PT should know?" />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={!comment.trim() && !rating} onClick={() => onSubmit({ rating, comment: comment.trim() })}>
            {alreadySubmitted ? "Update feedback" : "Submit feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientDashboard({ user }) {
  const [sessions, setSessions] = useState(() => getAll("sessions"));
  const [explorer, setExplorer] = useState(false);  // true → full charts explorer
  const [detail, setDetail] = useState(null);        // non-null → read-only appraisal detail
  const [feedbackSession, setFeedbackSession] = useState(null); // non-null → feedback modal
  const [editOpen, setEditOpen] = useState(false);   // client editing their own personal details
  const [resolvedPtName, setResolvedPtName] = useState(user.ptName || "");

  const submitFeedback = (sessionId, { rating, comment }) => {
    update("sessions", sessionId, { clientComment: comment, clientRating: rating });
    setSessions(getAll("sessions"));
    setFeedbackSession(null);
  };

  // Resolve the client's own record + their appraisals (match by id, then name).
  const isRealClient = !!user?.email;
  const allClients   = getAll("clients");
  const clientRecord = allClients.find(c => c.id === user.id) || allClients.find(c => c.name === user.name) || { id: user.id, name: user.name };
  const ptName = resolvedPtName || user.ptName || "Your trainer";
  const [appraisals, setAppraisals] = useState(() => isRealClient ? [] :
    query("appraisals", a => a.clientId === clientRecord.id || (a.clientName && a.clientName === clientRecord.name)).sort((a, b) => _parseDM(b.date) - _parseDM(a.date)));
  const [nextSession, setNextSession] = useState(null);
  useEffect(() => {
    if (!isRealClient) return;
    (async () => {
      const [{ data: ap }, { data: up }, { data: clientRow }] = await Promise.all([
        supabase.from("appraisals").select("*").eq("client_id", user.id).order("id", { ascending:false }),
        supabase.from("upcoming_sessions").select("*").eq("client_id", user.id).order("date").limit(1),
        supabase.from("clients").select("pt_id").eq("id", user.id).maybeSingle(),
      ]);
      if (!resolvedPtName && clientRow?.pt_id) {
        const { data: ptRow } = await supabase.from("pts").select("name").eq("id", clientRow.pt_id).maybeSingle();
        if (ptRow?.name) setResolvedPtName(ptRow.name);
      }
      setAppraisals((ap || []).map(r => ({ ...(r.data || {}), id:r.id, date:r.date })));
      setNextSession((up && up[0]) || null);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const latest = appraisals[0];
  const prev   = appraisals[1];

  // Trend (vs previous appraisal) for the progress list.
  const trendFor = (metric) => {
    if (!latest || !prev) return null;
    const d = metric.get(latest) - metric.get(prev);
    if (Math.abs(d) < 0.01) return { label: "→ Stable", color: C.textMuted };
    const improved = metric.goal === "down" ? d < 0 : d > 0;
    return { label: `${d > 0 ? "↑" : "↓"} ${improved ? "Good" : "Watch"}`, color: improved ? C.green : C.amber };
  };
  const progressMetrics = ["bodyFat", "bmi", "vo2", "pressUps"].map(k => APPRAISAL_METRICS.find(m => m.key === k));

  return (
    <div className="page-content" style={{ maxWidth: 1060 }}>
      {editOpen && <ClientEditModal clientId={user.id} onClose={()=>setEditOpen(false)} onSaved={()=>setEditOpen(false)} />}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:32 }}>
        <div>
          <div className="accent-line" />
          <h1 style={{fontSize:28,fontWeight:700,letterSpacing:"-0.5px"}}>Welcome, {user.name.split(" ")[0]}</h1>
          <p style={{color:C.textSub,fontSize:14,marginTop:6}}>Your PT: <strong style={{color:C.text}}>{ptName}</strong></p>
          <button className="btn btn-outline btn-sm" style={{marginTop:12}} onClick={()=>setEditOpen(true)}>Edit my details</button>
        </div>
        <div className="card" style={{background:C.primaryGlow,border:`1px solid ${C.primary}20`,textAlign:"right"}}>
          <div style={{fontSize:11,color:C.textMuted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Next session</div>
          <div style={{fontWeight:700,color:C.primary,fontSize:14}}>{isRealClient ? (nextSession ? `${nextSession.date} — ${nextSession.name} ${nextSession.time||""}` : "None booked") : "30/03/2025 — Body Build 19:00"}</div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24, marginBottom:24 }}>
        <div className="card">
          <div style={{fontWeight:600,marginBottom:14,fontSize:15}}>Health & Fitness Objectives</div>
          <p style={{fontSize:13,color:C.textSub,lineHeight:1.7,marginBottom:16}}>{clientRecord.objectives || "Build up arms and stabilise weight at 80kg. Improve cardiovascular fitness and overall strength."}</p>
          <hr className="divider" />
          <div style={{fontWeight:600,marginBottom:12,fontSize:14}}>Latest appraisal {latest ? <span style={{fontSize:12,fontWeight:400,color:C.textMuted}}>· {latest.date}</span> : null}</div>
          {latest ? progressMetrics.map(m => {
            const v = m.get(latest); const t = trendFor(m);
            return (
              <div key={m.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{fontSize:13,color:C.textSub}}>{m.label}</span>
                <span style={{fontWeight:600,fontSize:13}}>{v ? _fmt(v) + m.unit : "—"}</span>
                <span style={{ fontSize:12, color: t ? t.color : C.textMuted, minWidth:64, textAlign:"right" }}>{t ? t.label : "—"}</span>
              </div>
            );
          }) : (
            <p style={{fontSize:13,color:C.textMuted,lineHeight:1.7}}>Your PT hasn't recorded an appraisal yet. Your fitness assessment results will appear here.</p>
          )}
        </div>

        <div className="card"
          onClick={() => latest && setExplorer(true)}
          style={{ cursor: latest ? "pointer" : "default", transition:"border-color .15s" }}
          onMouseEnter={e => { if (latest) e.currentTarget.style.borderColor = C.primary; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = ""; }}
          title={latest ? "Open progress charts" : undefined}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
            <div style={{fontWeight:600,fontSize:15}}>Appraisal Progress</div>
            {latest && <span className="btn btn-ghost btn-sm" style={{ color:C.primary, pointerEvents:"none" }}>View all charts →</span>}
          </div>
          {latest ? (
            <>
              <div style={{fontSize:13,color:C.textMuted,marginBottom:16}}>
                Latest · {latest.date}{appraisals.length > 1 ? ` — tap to explore ${appraisals.length} appraisals & all trends` : " — tap to explore all charts"}
              </div>
              <AppraisalCharts appraisal={latest} compact />
            </>
          ) : (
            <div style={{fontSize:13,color:C.textMuted,marginTop:20}}>No appraisal charts to show yet.</div>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{fontWeight:600,marginBottom:16,fontSize:15}}>Recent Sessions</div>
        {sessions.slice(0,3).map(s=>{
          const st = sessionFeedbackState(s);
          const b  = sessionBadge(s);
          const hasClientFb = !!s.clientRating || !!(s.clientComment && s.clientComment.trim());
          return (
            <div key={s.id} onClick={()=>setFeedbackSession(s)}
              style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 14px", margin:"0 -14px", borderBottom:`1px solid ${C.border}`, cursor:"pointer", borderRadius:8, transition:"background .15s" }}
              onMouseEnter={e=>{ e.currentTarget.style.background=C.surfaceAlt; }}
              onMouseLeave={e=>{ e.currentTarget.style.background=""; }}
              title="View session & leave feedback">
              <div>
                <div style={{fontWeight:500,fontSize:14}}>Session {s.id} — {s.date} — {s.name}</div>
                {hasClientFb
                  ? <div style={{fontSize:12,color:C.textMuted,marginTop:3}}>Your feedback submitted{s.clientRating ? ` · ${"★".repeat(s.clientRating)}` : ""}</div>
                  : st==="awaiting-client"
                    ? <div style={{fontSize:12,color:C.primary,marginTop:3}}>Tap to leave your feedback →</div>
                    : <div style={{fontSize:12,color:C.textMuted,marginTop:3}}>Tap to view{st==="awaiting-pt" ? " — awaiting your PT's feedback" : ""}</div>}
              </div>
              <span className={`badge ${b.cls}`}>{b.label}</span>
            </div>
          );
        })}
      </div>

      {explorer && <ChartsExplorerModal appraisals={appraisals} onClose={() => setExplorer(false)} onOpenAppraisal={(a) => { setExplorer(false); setDetail(a); }} />}
      {detail && <AppraisalDetailModal appraisal={detail} onClose={() => setDetail(null)} />}
      {feedbackSession && <ClientSessionFeedbackModal session={feedbackSession} onClose={() => setFeedbackSession(null)} onSubmit={(fb) => submitFeedback(feedbackSession.id, fb)} />}
    </div>
  );
}

// ── MONTHLY REPORT ────────────────────────────────────────────────────────────
function MonthlyReport({ user, onBack }) {
  const allSessions = getAll("sessions");
  const allClients  = getAll("clients");
  const allPts      = getAll("pts");

  const now      = new Date();
  const [year,   setYear]  = useState(now.getFullYear());
  const [month,  setMonth] = useState(now.getMonth()); // 0-indexed
  const [drillDown, setDrillDown] = useState(false);

  const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // Filter sessions to selected month (using seed date format DD/MM/YYYY)
  const monthSessions = allSessions.filter(s => {
    if (!s.date) return false;
    const [dd, mm, yy] = s.date.split("/");
    return parseInt(mm) - 1 === month && parseInt(yy) === year;
  });

  // Per-client breakdown
  const clientCounts = {};
  monthSessions.forEach(s => {
    const client = allClients.find(c => c.id === s.clientId);
    const name = client?.name || `Client ${s.clientId}`;
    clientCounts[name] = (clientCounts[name] || 0) + 1;
  });
  const clientRows = Object.entries(clientCounts).sort((a,b) => b[1]-a[1]);
  const maxCount   = Math.max(...clientRows.map(r => r[1]), 1);

  // Sessions by week
  const weekCounts = [0,0,0,0];
  monthSessions.forEach(s => {
    const [dd] = s.date.split("/");
    const w = Math.min(Math.floor((parseInt(dd)-1)/7), 3);
    weekCounts[w]++;
  });
  const maxWeek = Math.max(...weekCounts, 1);

  // KPIs
  const completed   = monthSessions.filter(s => sessionFeedbackState(s) === "complete").length;
  const ptRow       = allPts.find(p => p.id === user?.id || p.name === user?.name);
  const rate        = ptRow?.rate || 40;
  const revenueEst  = `£${monthSessions.length * rate}`;
  const completion  = monthSessions.length ? Math.round((completed / monthSessions.length) * 100) : 0;

  const AVATAR_COLORS = ["#c8ff57","#60a5fa","#a78bfa","#f59e0b","#34d399"];

  // ── Drill-down: filtered session log ────────────────────────────────────────
  if (drillDown) {
    return (
      <div className="page-content" style={{ maxWidth:960 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setDrillDown(false)} style={{ color:C.textMuted }}>← Monthly Report</button>
          <h1 style={{ fontSize:22, fontWeight:700 }}>All Sessions — {MONTH_NAMES[month]} {year}</h1>
          <span style={{ marginLeft:"auto", background:C.primaryGlow, border:`1px solid ${C.primary}20`, color:C.primary, padding:"3px 12px", borderRadius:20, fontSize:12, fontWeight:600 }}>{monthSessions.length} session{monthSessions.length!==1?"s":""}</span>
        </div>

        {/* Month selector */}
        <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
          {MONTH_NAMES.map((m,i) => (
            <button key={m} onClick={() => setMonth(i)}
              style={{ padding:"5px 14px", borderRadius:20, fontSize:12, cursor:"pointer", fontWeight:600,
                background: i===month ? C.primary : C.surfaceAlt,
                color:      i===month ? "#0c0c0e" : C.textMuted,
                border:`1px solid ${i===month ? C.primary : C.border}` }}>
              {m}
            </button>
          ))}
        </div>

        <div className="card" style={{ padding:0, overflow:"hidden" }}>
          {monthSessions.length === 0 ? (
            <div style={{ padding:40, textAlign:"center", color:C.textMuted }}>No sessions recorded for {MONTH_NAMES[month]} {year}</div>
          ) : (
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:14 }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                  {["Date","Client","Plan","Status"].map(h => (
                    <th key={h} style={{ padding:"12px 16px", textAlign:"left", color:C.textMuted, fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthSessions.map(s => {
                  const client = allClients.find(c => c.id === s.clientId);
                  const st = sessionFeedbackState(s);
                  const done = st === "complete";
                  const tone = done ? C.green : st === "awaiting-pt" ? C.amber : C.blue;
                  return (
                    <tr key={s.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                      <td style={{ padding:"12px 16px", color:C.textMuted }}>{s.date}</td>
                      <td style={{ padding:"12px 16px", fontWeight:600 }}>{client?.name || `Client ${s.clientId}`}</td>
                      <td style={{ padding:"12px 16px", color:C.textSub }}>{s.name}</td>
                      <td style={{ padding:"12px 16px" }}>
                        <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600,
                          background: `${tone}20`, color: tone }}>
                          {sessionBadge(s).label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ── Monthly report ───────────────────────────────────────────────────────────
  return (
    <div className="page-content" style={{ maxWidth:960 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ color:C.textMuted }}>← Dashboard</button>
        <div className="accent-line" style={{ margin:"0 8px" }} />
        <h1 style={{ fontSize:22, fontWeight:700 }}>Monthly Report</h1>
        <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={() => { if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); }}
            style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, color:C.textSub, padding:"5px 10px", borderRadius:8, cursor:"pointer" }}>‹</button>
          <span style={{ fontWeight:700, minWidth:110, textAlign:"center" }}>{MONTH_NAMES[month]} {year}</span>
          <button onClick={() => { if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); }}
            style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, color:C.textSub, padding:"5px 10px", borderRadius:8, cursor:"pointer" }}>›</button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {[
          { num: monthSessions.length, label: "Sessions" },
          { num: Object.keys(clientCounts).length, label: "Clients seen" },
          { num: revenueEst, label: "Revenue est." },
          { num: `${completion}%`, label: "Completion rate" },
        ].map(k => (
          <div key={k.label} className="card" style={{ textAlign:"center", padding:"16px 12px" }}>
            <div style={{ fontSize:28, fontWeight:800, color:C.primary }}>{k.num}</div>
            <div style={{ fontSize:11, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px", marginTop:4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {/* Per-client breakdown */}
        <div className="card">
          <div style={{ fontWeight:700, fontSize:12, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:16 }}>Sessions per client</div>
          {clientRows.length === 0
            ? <div style={{ color:C.textMuted, fontSize:13 }}>No sessions this month</div>
            : clientRows.map(([name, count], i) => {
                const initials = name.split(" ").map(s=>s[0]).join("").slice(0,2).toUpperCase();
                const col = AVATAR_COLORS[i % AVATAR_COLORS.length];
                return (
                  <div key={name} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                    <div style={{ width:32, height:32, borderRadius:"50%", background:`${col}20`, border:`1px solid ${col}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:col, flexShrink:0 }}>{initials}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:13 }}>
                        <span style={{ fontWeight:600 }}>{name}</span>
                        <span style={{ color:C.textMuted }}>{count} session{count!==1?"s":""}</span>
                      </div>
                      <div style={{ height:6, background:C.border, borderRadius:3 }}>
                        <div style={{ height:"100%", width:`${(count/maxCount)*100}%`, background:col, borderRadius:3, transition:"width 0.4s" }} />
                      </div>
                    </div>
                  </div>
                );
              })
          }
        </div>

        {/* Weekly bar chart */}
        <div className="card">
          <div style={{ fontWeight:700, fontSize:12, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:16 }}>Sessions by week</div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:8, height:100 }}>
            {weekCounts.map((v,i) => (
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ fontSize:12, color:C.textMuted, fontWeight:600 }}>{v}</div>
                <div style={{ width:"100%", background:`linear-gradient(to top, ${C.primary}, ${C.primaryDark||C.primary})`, borderRadius:"4px 4px 0 0", height:`${maxWeek > 0 ? (v/maxWeek)*80 : 0}px`, minHeight: v > 0 ? 6 : 0, transition:"height 0.4s" }} />
                <div style={{ fontSize:11, color:C.textMuted }}>W{i+1}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Drill-down link */}
      <div style={{ textAlign:"right" }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setDrillDown(true)} style={{ color:C.primary }}>
          View all {monthSessions.length} sessions →
        </button>
      </div>
    </div>
  );
}

// ── CLIENT SESSIONS ───────────────────────────────────────────────────────────
function ClientSessions({ user }) {
  const [sessions, setSessions] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showFeedback, setShowFeedback] = useState(null);
  const [showPlan, setShowPlan] = useState(null);

  const load = async () => {
    if (!user?.email) { // demo client — keep hardcoded data
      setUpcoming(getAll("upcomingSessions")); setSessions(getAll("sessions")); setLoading(false); return;
    }
    const cid = user.id;
    const [{ data: up }, { data: se }] = await Promise.all([
      supabase.from("upcoming_sessions").select("*").eq("client_id", cid).order("date"),
      supabase.from("sessions").select("*").eq("client_id", cid).order("id", { ascending:false }),
    ]);
    setUpcoming(up || []);
    setSessions((se || []).map(s => ({ ...s, coachNotes: s.coach_notes, clientRating: s.client_rating, clientComment: s.client_comment })));
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="page-content" style={{ maxWidth: 960 }}>
      <div style={{ marginBottom:28 }}>
        <div className="accent-line" />
        <h1 style={{fontSize:28,fontWeight:700,letterSpacing:"-0.5px"}}>My Sessions</h1>
      </div>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:600,color:C.textMuted,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.8px"}}>Upcoming</div>
        {!loading && upcoming.length === 0 && <div style={{ color:C.textMuted, fontSize:13, marginBottom:8 }}>No upcoming sessions.</div>}
        {upcoming.map((s,i)=>(
          <div key={i} className="card" style={{ marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 18px" }}>
            <div><span style={{fontWeight:500}}>{s.date} — {s.name}</span><span style={{color:C.textMuted,fontSize:13,marginLeft:8}}>{s.time}</span></div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span className="badge badge-blue">Upcoming</span>
              {findPlanForSession(s.name) && (
                <button className="btn btn-ghost btn-sm" style={{color:C.primary}} onClick={()=>setShowPlan(findPlanForSession(s.name))}>Session Plan</button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:C.textMuted,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.8px"}}>Recent sessions</div>
        {!loading && sessions.length === 0 && <div style={{ color:C.textMuted, fontSize:13 }}>No sessions yet.</div>}
        {sessions.map(s=>{
          const st = sessionFeedbackState(s);
          const b  = sessionBadge(s);
          const hasClientFb = !!s.clientRating || !!(s.clientComment && s.clientComment.trim());
          return (
            <div key={s.id} className="card" style={{ marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 18px" }}>
              <span style={{fontWeight:500}}>Session {s.id} — {s.date} — {s.name}</span>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span className={`badge ${b.cls}`}>{b.label}</span>
                <button className={`btn btn-sm ${st==="awaiting-client" ? "btn-primary" : "btn-ghost"}`} style={st==="awaiting-client"?undefined:{color:C.primary}} onClick={()=>setShowFeedback(s)}>
                  {hasClientFb ? "View / edit feedback" : st==="awaiting-client" ? "Add feedback" : "View"}
                </button>
                {findPlanForSession(s.name) && (
                  <button className="btn btn-ghost btn-sm" style={{color:C.textSub}} onClick={()=>setShowPlan(findPlanForSession(s.name))}>Session Plan</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showFeedback && (
        <ClientSessionFeedbackModal
          session={showFeedback}
          onClose={()=>setShowFeedback(null)}
          onSubmit={async (fb)=>{ if (user?.email) await supabase.from("sessions").update({ client_comment: fb.comment, client_rating: fb.rating }).eq("id", showFeedback.id); else update("sessions", showFeedback.id, { clientComment: fb.comment, clientRating: fb.rating }); setShowFeedback(null); load(); }}
        />
      )}

      {showPlan && <PlanDetailModal plan={showPlan} onClose={()=>setShowPlan(null)} />}
    </div>
  );
}

// ── MESSAGES ──────────────────────────────────────────────────────────────────
function MessagesPage({ user, messageClient, clearMessageClient }) {
  const isPT = user.role === "pt";
  const mySender = isPT ? "pt" : "client";
  const MESSAGE_EDIT_WINDOW_MS = 15 * 60 * 1000;
  const [threads, setThreads]         = useState([]);   // PT: their clients · client: [self]
  const [selected, setSelected]       = useState(null);
  const [messages, setMessages]       = useState([]);
  const [reply, setReply]             = useState("");
  const [editingMsg, setEditingMsg]   = useState(null);
  const [editBody, setEditBody]       = useState("");
  const [busy, setBusy]               = useState(false);
  const [editBusy, setEditBusy]       = useState(false);
  const [err, setErr]                 = useState("");
  const [loadingThreads, setLoading]  = useState(true);
  const threadRef = useRef(null);

  const scrollBottom = () => setTimeout(() => { threadRef.current?.lastElementChild?.scrollIntoView({ behavior:"smooth" }); }, 50);
  const fmtTs = ts => new Date(ts).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" });
  const canEditMessage = msg => msg.sender === mySender && Date.now() - new Date(msg.created_at).getTime() <= MESSAGE_EDIT_WINDOW_MS;

  // Build the thread list once.
  useEffect(() => {
    let live = true;
    (async () => {
      if (isPT) {
        const { data } = await supabase.from("clients").select("id,name,username,status,user_id").order("name");
        const list = data || [];
        if (!live) return;
        setThreads(list);
        const target = messageClient ? (list.find(c => c.id === messageClient.id) || messageClient) : list[0];
        setSelected(target || null);
      } else {
        // Client: their one thread is with their PT. user.id === their clients row id.
        let ptName = user.ptName || "Your trainer";
        try {
          const { data: row } = await supabase.from("clients").select("pt_id").eq("id", user.id).maybeSingle();
          if (row) { const { data: pt } = await supabase.from("pts").select("name").eq("id", row.pt_id).maybeSingle(); if (pt?.name) ptName = pt.name; }
        } catch {}
        if (!live) return;
        const me = { id: user.id, name: ptName };
        setThreads([me]); setSelected(me);
      }
      setLoading(false);
      clearMessageClient?.();
    })();
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load + poll the selected thread; mark the other side's messages read.
  useEffect(() => {
    if (!selected) return;
    setEditingMsg(null); setEditBody(""); setErr("");
    let live = true;
    const load = async () => {
      const { data } = await supabase.from("client_messages").select("*").eq("client_id", selected.id).order("created_at", { ascending:true });
      if (!live) return;
      setMessages(prev => {
        if (prev.length !== (data || []).length) scrollBottom();
        return data || [];
      });
      try { await fetch("/api/client", { method:"POST", headers:{ "Content-Type":"application/json", ...(await ptAuthHeader()) }, body: JSON.stringify({ action:"message-read", client_id: selected.id }) }); } catch {}
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { live = false; clearInterval(iv); };
  }, [selected && selected.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch("/api/client", {
        method:"POST", headers:{ "Content-Type":"application/json", ...(await ptAuthHeader()) },
        body: JSON.stringify({ action:"message-send", client_id: selected.id, body: reply }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setErr(data.error || "Could not send the message."); setBusy(false); return; }
      setReply(""); setMessages(m => [...m, data.message]); scrollBottom();
    } catch { setErr("Network error — please try again."); }
    setBusy(false);
  };

  const startEdit = (msg) => {
    setErr("");
    setEditingMsg(msg);
    setEditBody(msg.body || "");
  };

  const cancelEdit = () => {
    setEditingMsg(null);
    setEditBody("");
  };

  const saveEdit = async () => {
    if (!editingMsg || !selected || !editBody.trim()) return;
    setEditBusy(true); setErr("");
    try {
      const res = await fetch("/api/client", {
        method:"POST", headers:{ "Content-Type":"application/json", ...(await ptAuthHeader()) },
        body: JSON.stringify({ action:"message-edit", client_id: selected.id, message_id: editingMsg.id, body: editBody }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setErr(data.error || "Could not edit the message."); setEditBusy(false); return; }
      setMessages(m => m.map(msg => msg.id === data.message.id ? data.message : msg));
      cancelEdit();
    } catch { setErr("Network error — please try again."); }
    setEditBusy(false);
  };

  return (
    <div className="page-content" style={{ maxWidth: 1060 }}>
      <div style={{ marginBottom: 28 }}>
        <div className="accent-line" />
        <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.5px" }}>Messages</h1>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isPT ? "300px 1fr" : "1fr", gap: 20, alignItems: "start" }}>
        {/* Thread list (PT only) */}
        {isPT && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {loadingThreads && <div style={{ padding: 24, color: C.textMuted, fontSize: 13, textAlign: "center" }}>Loading…</div>}
            {!loadingThreads && threads.length === 0 && (
              <div style={{ padding: 24, color: C.textMuted, fontSize: 13, textAlign: "center" }}>No clients yet — register a client to start messaging.</div>
            )}
            {threads.map(t => (
              <div key={t.id} onClick={() => { setSelected(t); setReply(""); setErr(""); }}
                style={{ padding: "13px 16px", borderBottom: `1px solid ${C.border}`, cursor: "pointer", display:"flex", alignItems:"center", gap:10,
                  background: selected?.id === t.id ? C.primaryGlow : "transparent", transition: "background 0.15s" }}>
                <Avatar name={t.name} size={34} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>{t.user_id ? "Active" : "Invited — not signed up yet"}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Thread detail */}
        <div className="card" style={{ minHeight: 420, display:"flex", flexDirection:"column" }}>
          {selected ? (
            <>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{selected.name}</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 18 }}>{isPT ? "Your client" : "Your trainer"}</div>
              <div ref={threadRef} style={{ flex:1, marginBottom: 16, display: "flex", flexDirection: "column", gap: 12, overflowY:"auto", maxHeight: 460 }}>
                {messages.length === 0 && <div style={{ color: C.textMuted, fontSize: 13 }}>No messages yet — say hello 👋</div>}
	                {messages.map(msg => {
	                  const isMe = msg.sender === mySender;
	                  const editing = editingMsg?.id === msg.id;
	                  return (
	                    <div key={msg.id} style={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
	                      <div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: isMe ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
	                        background: isMe ? C.primaryGlow : C.surfaceAlt, border: `1px solid ${isMe ? C.primary + "30" : C.border}` }}>
	                        {editing ? (
	                          <>
	                            <textarea className="input" rows={3} value={editBody} onChange={e=>setEditBody(e.target.value)}
	                              style={{ minWidth:260, fontSize:13, marginBottom:8 }} />
	                            <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
	                              <button className="btn btn-outline btn-sm" onClick={cancelEdit} disabled={editBusy}>Cancel</button>
	                              <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={editBusy || !editBody.trim()}>{editBusy ? "Saving…" : "Save"}</button>
	                            </div>
	                          </>
	                        ) : (
	                          <>
	                            <div style={{ fontSize: 14, lineHeight: 1.6, color: C.text, whiteSpace:"pre-wrap", wordBreak:"break-word" }}>{msg.body}</div>
	                            <div style={{ display:"flex", justifyContent: isMe ? "flex-end" : "flex-start", alignItems:"center", gap:8, marginTop:6 }}>
	                              <span style={{ fontSize: 11, color: C.textMuted }}>{fmtTs(msg.created_at)}</span>
	                              {canEditMessage(msg) && (
	                                <button onClick={()=>startEdit(msg)} style={{ background:"none", border:"none", color:C.primary, cursor:"pointer", fontSize:11, padding:0 }}>
	                                  Edit
	                                </button>
	                              )}
	                            </div>
	                          </>
	                        )}
	                      </div>
	                    </div>
	                  );
                })}
              </div>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                <textarea className="input" rows={3} placeholder="Write a message…" value={reply}
                  onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} />
                {err && <div style={{ fontSize: 12, color: C.danger, marginTop: 6 }}>{err}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={send}>{busy ? "Sending…" : "Send ↵"}</button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 280, color: C.textMuted, fontSize: 14 }}>
              {loadingThreads ? "Loading…" : "Select a client to message"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PENDING APPROVAL ──────────────────────────────────────────────────────────
function PendingApprovalPage({ onSignOut }) {
  return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:420, textAlign:"center" }}>
        <div style={{ width:64, height:64, borderRadius:"50%", background:C.surfaceAlt, border:`2px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 20px" }}>⏳</div>
        <h1 style={{ fontSize:24, fontWeight:700, marginBottom:10 }}>Account pending approval</h1>
        <p style={{ fontSize:14, color:C.textSub, lineHeight:1.7, marginBottom:28 }}>
          Your account has been created and is awaiting activation by an admin.<br />
          You'll be able to log in once your account is approved.
        </p>
        <button className="btn btn-outline" onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}

// ── SUSPENDED ACCOUNT ─────────────────────────────────────────────────────────
// Shown to a PT whose account has been suspended by an admin (pts.status =
// 'suspended'). Blocks access to the dashboard — the routing in App sends
// suspended profiles here instead of "dashboard".
function SuspendedPage({ onSignOut }) {
  return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:420, textAlign:"center" }}>
        <div style={{ width:64, height:64, borderRadius:"50%", background:C.dangerLight, border:`2px solid ${C.danger}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 20px" }}>🚫</div>
        <h1 style={{ fontSize:24, fontWeight:700, marginBottom:10 }}>Account suspended</h1>
        <p style={{ fontSize:14, color:C.textSub, lineHeight:1.7, marginBottom:28 }}>
          Your myPTOnline account has been suspended and can't be accessed right now.<br />
          If you think this is a mistake, please contact us at <a href="mailto:hello@myptonline.ai" style={{ color:C.primary }}>hello@myptonline.ai</a>.
        </p>
        <button className="btn btn-outline" onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}

// ── CLIENT SIGN-UP (from the PT invite email: /?client=<username> + PIN) ──────
// Phase 1: client confirms the PIN from their email (no login set here).
function ClientSignupPage({ setView, presetUsername }) {
  const [username, setUsername] = useState(presetUsername || "");
  const [pin, setPin]           = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");
  const [done, setDone]         = useState(null);

  const ready = username.trim() && /^\d{6}$/.test(pin.trim());

  const submit = async () => {
    if (!ready) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch("/api/client", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "confirm-pin", username: username.trim(), pin: pin.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setErr(data.error || "Could not confirm your PIN."); setBusy(false); return; }
      setDone({ name: data.name, ptName: data.ptName });
    } catch { setErr("Network error — please try again."); }
    setBusy(false);
  };

  if (done) {
    return (
      <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
        <div style={{ width:"100%", maxWidth:460, textAlign:"center" }}>
          <div style={{ width:60, height:60, borderRadius:"50%", background:C.greenLight, border:`2px solid ${C.green}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, margin:"0 auto 18px" }}>✓</div>
          <h1 style={{ fontSize:24, fontWeight:700, marginBottom:12 }}>PIN confirmed!</h1>
          <p style={{ fontSize:14.5, color:C.textSub, lineHeight:1.75, marginBottom:22 }}>
            Thanks{done.name ? `, ${done.name.split(" ")[0]}` : ""} — your email is confirmed.<br />
            <strong style={{ color:C.text }}>Please contact {done.ptName || "your trainer"} to activate your account.</strong><br />
            Once they activate it, you'll get an email with a link to set your password and log in.
          </p>
          <button className="btn btn-outline" onClick={() => { window.history.replaceState(null, "", window.location.pathname); setView("home"); }}>Back to home</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:440 }}>
        <div style={{ textAlign:"center", marginBottom:26 }}>
          <div className="accent-line" style={{ margin:"0 auto 14px" }} />
          <h1 style={{ fontSize:28, fontWeight:800, letterSpacing:"-0.5px" }}>Confirm your sign-up</h1>
          <p style={{ fontSize:14, color:C.textSub, marginTop:6 }}>Enter the username and PIN your trainer emailed you.</p>
        </div>
        <div className="card" style={{ padding:"26px 24px" }}>
          <div style={{ marginBottom:14 }}><label className="label">Username</label>
            <input className="input" value={username} onChange={e=>setUsername(e.target.value)} placeholder="your.username" /></div>
          <div style={{ marginBottom:16 }}><label className="label">Sign-up PIN (6 digits)</label>
            <input className="input" value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,"").slice(0,6))} inputMode="numeric" placeholder="123456" style={{ letterSpacing:"4px" }} /></div>
          {err && <div style={{ padding:"10px 12px", background:C.dangerLight, color:C.danger, borderRadius:8, fontSize:13, marginBottom:14 }}>{err}</div>}
          <button className="btn btn-primary" style={{ width:"100%", opacity: ready&&!busy?1:0.5, cursor: ready&&!busy?"pointer":"not-allowed" }} disabled={!ready||busy} onClick={submit}>{busy ? "Confirming…" : "Confirm PIN →"}</button>
          <div style={{ textAlign:"center", marginTop:16, fontSize:13, color:C.textSub }}>
            Already set up? <button className="btn-link" style={{ color:C.primary, background:"none", border:"none", cursor:"pointer", fontWeight:600 }} onClick={()=>setView("login")}>Log in</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Phase 3: client sets their password via the link the PT's activation emailed.
function ClientSetupLoginPage({ setView, token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [show, setShow]         = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");
  const [done, setDone]         = useState(null);

  const rules = [
    { label: "At least 8 characters", ok: password.length >= 8 },
    { label: "Passwords match",       ok: password.length > 0 && password === confirm },
  ];
  const ready = rules.every(r => r.ok);

  const submit = async () => {
    if (!ready) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch("/api/client", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "setup-login", token, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setErr(data.error || "Could not set up your login."); setBusy(false); return; }
      setDone({ email: data.email });
    } catch { setErr("Network error — please try again."); }
    setBusy(false);
  };

  if (done) {
    return (
      <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
        <div style={{ width:"100%", maxWidth:420, textAlign:"center" }}>
          <div style={{ width:60, height:60, borderRadius:"50%", background:C.greenLight, border:`2px solid ${C.green}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, margin:"0 auto 18px" }}>✓</div>
          <h1 style={{ fontSize:24, fontWeight:700, marginBottom:10 }}>You're all set!</h1>
          <p style={{ fontSize:14, color:C.textSub, lineHeight:1.7, marginBottom:24 }}>
            Your login is ready. Sign in with your email<br /><strong style={{color:C.text}}>{done.email}</strong> and the password you just set.
          </p>
          <button className="btn btn-primary" onClick={() => { window.history.replaceState(null, "", window.location.pathname); setView("login"); }}>Go to login →</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:440 }}>
        <div style={{ textAlign:"center", marginBottom:26 }}>
          <div className="accent-line" style={{ margin:"0 auto 14px" }} />
          <h1 style={{ fontSize:28, fontWeight:800, letterSpacing:"-0.5px" }}>Set up your login</h1>
          <p style={{ fontSize:14, color:C.textSub, marginTop:6 }}>Your trainer activated your account — choose a password to finish.</p>
        </div>
        <div className="card" style={{ padding:"26px 24px" }}>
          <div style={{ marginBottom:14 }}><label className="label">Create a password</label>
            <div style={{ position:"relative" }}>
              <input className="input" type={show?"text":"password"} value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 8 characters" />
              <button type="button" onClick={()=>setShow(s=>!s)} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:16 }}>{show?"🙈":"👁"}</button>
            </div></div>
          <div style={{ marginBottom:16 }}><label className="label">Confirm password</label>
            <input className="input" type={show?"text":"password"} value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Re-enter password" /></div>
          <div style={{ display:"flex", gap:14, flexWrap:"wrap", marginBottom:16 }}>
            {rules.map(r => <span key={r.label} style={{ fontSize:12, color: r.ok ? C.green : C.textMuted }}>{r.ok ? "✓" : "○"} {r.label}</span>)}
          </div>
          {err && <div style={{ padding:"10px 12px", background:C.dangerLight, color:C.danger, borderRadius:8, fontSize:13, marginBottom:14 }}>{err}</div>}
          <button className="btn btn-primary" style={{ width:"100%", opacity: ready&&!busy?1:0.5, cursor: ready&&!busy?"pointer":"not-allowed" }} disabled={!ready||busy} onClick={submit}>{busy ? "Setting up…" : "Set password & finish →"}</button>
        </div>
      </div>
    </div>
  );
}

// ── FORGOT PAGE ───────────────────────────────────────────────────────────────
// ── SET NEW PASSWORD (after clicking reset link in email) ─────────────────────
function SetPasswordPage({ setView, clearRecovery }) {
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [done, setDone]           = useState(false);
  const [email, setEmail]         = useState(""); // recovery account — lets the password manager update the right saved login
  useEffect(() => { supabase.auth.getUser().then(({ data }) => setEmail(data?.user?.email || "")).catch(() => {}); }, []);

  const rules = [
    { label: "At least 8 characters",        ok: password.length >= 8 },
    { label: "One uppercase letter",          ok: /[A-Z]/.test(password) },
    { label: "One number",                    ok: /\d/.test(password) },
    { label: "Passwords match",               ok: password.length > 0 && password === confirm },
  ];
  const allOk = rules.every(r => r.ok);

  const save = async () => {
    if (!allOk) return;
    setLoading(true); setError("");
    const { error: sbErr } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (sbErr) {
      const isSessionGone = sbErr.message?.toLowerCase().includes("session") || sbErr.message?.toLowerCase().includes("missing");
      setError(isSessionGone
        ? "Your reset link has expired. Please go back and request a new one."
        : (sbErr.message || "Could not update password. Please try again."));
      return;
    }
    clearRecovery?.();  // unblock SIGNED_IN for future normal logins
    setDone(true);      // show success screen — sign out happens when user clicks Go to login
  };

  const goToLogin = async () => {
    await supabase.auth.signOut(); // end the recovery session cleanly
    setView("login");
  };

  return (
    <div style={{ minHeight:"85vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:420 }}>
        {done ? (
          <div style={{ textAlign:"center" }}>
            <div style={{ width:64, height:64, borderRadius:"50%", background:C.primaryGlow, border:`2px solid ${C.primary}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 20px" }}>🔒</div>
            <h1 style={{fontSize:24,fontWeight:700,marginBottom:10}}>Password updated!</h1>
            <p style={{fontSize:14,color:C.textSub,marginBottom:28,lineHeight:1.7}}>Your new password is set. You can now sign in.</p>
            <button className="btn btn-primary" onClick={goToLogin}>Go to login →</button>
          </div>
        ) : (
          <>
            <div style={{ textAlign:"center", marginBottom:32 }}>
              <div className="accent-line" style={{margin:"0 auto 20px"}} />
              <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.5px",marginBottom:8}}>Set a new password</h1>
              <p style={{fontSize:14,color:C.textSub,lineHeight:1.6}}>Choose something strong that you haven't used before.</p>
            </div>
            <form className="card" onSubmit={e=>{ e.preventDefault(); save(); }}>
              {/* Username field so the password manager (Apple Keychain) updates the saved login for THIS account */}
              <input type="email" name="email" autoComplete="username" value={email} readOnly aria-hidden="true" tabIndex={-1}
                style={{ position:"absolute", width:1, height:1, padding:0, margin:-1, overflow:"hidden", clip:"rect(0 0 0 0)", border:0 }} />
              <div style={{marginBottom:14}}>
                <label className="label">New password</label>
                <PasswordInput value={password} placeholder="Enter new password" autoFocus autoComplete="new-password" onChange={e=>{setPassword(e.target.value);setError("");}} />
              </div>
              <div style={{marginBottom:16}}>
                <label className="label">Confirm password</label>
                <PasswordInput value={confirm} placeholder="Repeat new password" autoComplete="new-password" onChange={e=>{setConfirm(e.target.value);setError("");}} />
              </div>

              {/* Password strength checklist */}
              <div style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:8, padding:"12px 14px", marginBottom:16 }}>
                {rules.map(r => (
                  <div key={r.label} style={{ display:"flex", alignItems:"center", gap:8, padding:"3px 0", fontSize:12 }}>
                    <span style={{ color: r.ok ? C.primary : C.textMuted, fontSize:14 }}>{r.ok ? "✓" : "○"}</span>
                    <span style={{ color: r.ok ? C.text : C.textMuted }}>{r.label}</span>
                  </div>
                ))}
              </div>

              {error && (
                <div style={{ background:C.dangerLight, color:C.danger, border:`1px solid ${C.danger}30`, borderRadius:8, padding:"10px 14px", fontSize:13, marginBottom:14 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                style={{width:"100%", padding:12, opacity: (!allOk||loading) ? 0.5 : 1}}
                disabled={!allOk || loading}
              >
                {loading ? "Saving…" : "Set new password →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ForgotPage({ setView }) {
  const [email, setEmail]       = useState("");
  const [sent, setSent]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [cooldown, setCooldown] = useState(0);   // seconds until resend is available

  // countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Branded reset email via our own Resend endpoint (not Supabase's default
  // mailer). Always shows success — never reveals whether an account exists.
  const requestReset = async () => {
    try {
      await fetch("/api/send-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch (_) {}
  };

  const send = async () => {
    setError("");
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError("Please enter a valid email address."); return; }
    setLoading(true);
    await requestReset();
    setLoading(false);
    setSent(true);
    setCooldown(60);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    await requestReset();
    setLoading(false);
    setCooldown(60);
  };

  return (
    <div style={{ minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:400 }}>

        {/* ── Step 1: Email entry ── */}
        {!sent ? (
          <>
            <div style={{ textAlign:"center", marginBottom:32 }}>
              <div style={{ width:56, height:56, borderRadius:"50%", background:C.primaryGlow, border:`2px solid ${C.primary}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, margin:"0 auto 20px" }}>🔑</div>
              <h1 style={{fontSize:26,fontWeight:700,letterSpacing:"-0.5px",marginBottom:8}}>Forgot your password?</h1>
              <p style={{fontSize:14,color:C.textSub,lineHeight:1.6}}>No problem. Enter your email and we'll send you a secure reset link — valid for 1 hour.</p>
            </div>

            {IS_EXPIRED_LINK && (
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, padding:"12px 16px", background:C.amberLight, border:`1px solid ${C.amber}30`, borderRadius:10 }}>
                <span style={{ fontSize:18 }}>⏱️</span>
                <span style={{ fontSize:13, color:C.amber, lineHeight:1.6 }}>That link has expired or was already used. Enter your email below to get a fresh one.</span>
              </div>
            )}

            <div className="card">
              <div style={{marginBottom:16}}>
                <label className="label">Email address</label>
                <input
                  className="input"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(""); }}
                  onKeyDown={e => e.key === "Enter" && send()}
                  autoFocus
                />
              </div>

              {error && (
                <div style={{ background:C.dangerLight, color:C.danger, border:`1px solid ${C.danger}30`, borderRadius:8, padding:"10px 14px", fontSize:13, marginBottom:14 }}>
                  {error}
                </div>
              )}

              <button
                className="btn btn-primary"
                style={{ width:"100%", padding:12, opacity: loading ? 0.7 : 1 }}
                onClick={send}
                disabled={loading}
              >
                {loading ? "Sending…" : "Send reset link →"}
              </button>

              <div style={{ textAlign:"center", marginTop:16 }}>
                <button className="btn btn-ghost" onClick={() => setView("login")} style={{ fontSize:13, color:C.textMuted }}>
                  ← Back to login
                </button>
              </div>
            </div>
          </>
        ) : (

          /* ── Step 2: Sent confirmation ── */
          <>
            <div style={{ textAlign:"center", marginBottom:28 }}>
              {/* Animated envelope icon */}
              <div style={{ position:"relative", width:72, height:72, margin:"0 auto 24px" }}>
                <div style={{ width:72, height:72, borderRadius:"50%", background:C.primaryGlow, border:`2px solid ${C.primary}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:32 }}>
                  ✉️
                </div>
                <div style={{ position:"absolute", top:-4, right:-4, width:22, height:22, borderRadius:"50%", background:C.primary, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#0c0c0e" }}>✓</div>
              </div>
              <h1 style={{fontSize:24,fontWeight:700,letterSpacing:"-0.5px",marginBottom:10}}>Check your inbox</h1>
              <p style={{fontSize:14,color:C.textSub,lineHeight:1.7}}>
                We've sent a password reset link to<br />
                <strong style={{color:C.text}}>{email}</strong>
              </p>
            </div>

            <div className="card">
              {/* What to do next */}
              <div style={{ marginBottom:20 }}>
                {[
                  ["📩", "Open the email from myPTOnline"],
                  ["🔗", "Click the secure reset link"],
                  ["🔒", "Choose a new password"],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 0", borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:18, width:28, textAlign:"center" }}>{icon}</span>
                    <span style={{ fontSize:13, color:C.textSub }}>{text}</span>
                  </div>
                ))}
              </div>

              {/* Spam tip */}
              <div style={{ background:C.amberLight, border:`1px solid ${C.amber}30`, borderRadius:8, padding:"10px 14px", fontSize:12, color:C.amber, marginBottom:20, lineHeight:1.6 }}>
                Can't find it? Check your <strong>spam or junk folder</strong>. The link expires in 1 hour.
              </div>

              {/* Resend with cooldown */}
              <div style={{ textAlign:"center", marginBottom:16 }}>
                <span style={{ fontSize:13, color:C.textMuted }}>Didn't receive it?&nbsp;</span>
                <button
                  className="btn btn-ghost"
                  onClick={resend}
                  disabled={cooldown > 0 || loading}
                  style={{ fontSize:13, color: cooldown > 0 ? C.textMuted : C.primary, fontWeight:600, cursor: cooldown > 0 ? "default" : "pointer" }}
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
                </button>
              </div>

              <button className="btn btn-outline" style={{ width:"100%" }} onClick={() => setView("login")}>
                ← Back to login
              </button>
            </div>

            {/* Wrong email escape hatch */}
            <div style={{ textAlign:"center", marginTop:16 }}>
              <button className="btn btn-ghost" onClick={() => { setSent(false); setCooldown(0); }} style={{ fontSize:12, color:C.textMuted }}>
                Wrong email address? Try again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── FOOTER ────────────────────────────────────────────────────────────────────
const SOCIAL_LINKS = [
  { label: "LinkedIn",  href: "https://linkedin.com/company/myptonline",  icon: "in",  color: "#0A66C2" },
  { label: "X / Twitter", href: "https://twitter.com/myptonline",         icon: "𝕏",   color: "#fff" },
  { label: "Instagram", href: "https://instagram.com/myptonline",         icon: "IG",  color: "#E1306C" },
  { label: "Facebook",  href: "https://facebook.com/myptonline",          icon: "f",   color: "#1877F2" },
  { label: "Email us",  href: "mailto:hello@myptonline.ai",               icon: "✉",   color: C.primary },
];

function ChangelogModal({ onClose }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 500 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:17 }}>What's New</div>
            <div style={{ fontSize:12, color:C.textMuted }}>myPTOnline version history</div>
          </div>
          <button onClick={onClose} style={{ background:"none", color:C.textMuted, fontSize:20 }}>✕</button>
        </div>
        <div style={{ maxHeight:420, overflowY:"auto" }}>
          {CHANGELOG.map((release, i) => (
            <div key={release.version} style={{ marginBottom:20 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
                <span style={{ background: i === 0 ? C.primary : C.surfaceAlt, color: i === 0 ? C.bg : C.textSub, fontSize:11, fontWeight:700, padding:"2px 10px", borderRadius:20 }}>
                  v{release.version}
                </span>
                {release.name && (
                  <span style={{ fontSize:12, fontWeight:600, color: i === 0 ? C.primary : C.textSub }}>
                    "{release.name}"
                  </span>
                )}
                <span style={{ fontSize:12, color:C.textMuted }}>{release.date}</span>
                {i === 0 && <span style={{ fontSize:11, color:C.primary }}>← current</span>}
              </div>
              <ul style={{ listStyle:"none", padding:0, margin:0 }}>
                {release.notes.map(note => (
                  <li key={note} style={{ fontSize:13, color:C.textSub, marginBottom:5, display:"flex", gap:8 }}>
                    <span style={{ color:C.primary, flexShrink:0 }}>·</span>{note}
                  </li>
                ))}
              </ul>
              {i < CHANGELOG.length - 1 && <div style={{ borderBottom:`1px solid ${C.border}`, marginTop:16 }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Footer({ setView }) {
  const [showContact, setShowContact] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const legalLinks = [
    { label: "Privacy Policy", view: "privacy" },
    { label: "Terms of Service", view: "terms" },
  ];
  return (
    <>
      <footer style={{ borderTop:`1px solid ${C.border}`, padding:"24px 28px", marginTop:48 }}>
        <div style={{ maxWidth:1200, margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <span style={{fontSize:16,fontWeight:700,letterSpacing:"-0.3px"}}>my<span style={{color:C.primary}}>PT</span><span style={{color:C.textMuted,fontWeight:400}}>Online</span></span>
          <div style={{display:"flex",gap:20,alignItems:"center"}}>
            {legalLinks.map(l => (
              <a key={l.label} href="#" onClick={e => { e.preventDefault(); setView(l.view); }}
                style={{fontSize:12,color:C.textMuted,transition:"color 0.15s",cursor:"pointer"}}
                onMouseEnter={e=>e.target.style.color=C.text} onMouseLeave={e=>e.target.style.color=C.textMuted}>{l.label}</a>
            ))}
            <a href="#" onClick={e => { e.preventDefault(); setShowContact(true); }}
              style={{fontSize:12,color:C.textMuted,transition:"color 0.15s",cursor:"pointer"}}
              onMouseEnter={e=>e.target.style.color=C.text} onMouseLeave={e=>e.target.style.color=C.textMuted}>Contact</a>
            <a href="#" onClick={e => { e.preventDefault(); setView("admin"); }}
              style={{fontSize:10,color:"#808080",transition:"color 0.15s",cursor:"pointer",letterSpacing:"0.5px"}}
              aria-hidden="true" tabIndex={-1}
              onMouseEnter={e=>e.target.style.color=C.textMuted} onMouseLeave={e=>e.target.style.color="#808080"}>Admin</a>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <span style={{fontSize:12,color:C.textMuted}}>© 2026 myPTOnline. All rights reserved.</span>
            <button onClick={() => setShowChangelog(true)} style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:20, padding:"2px 10px", fontSize:11, color:C.textMuted, cursor:"pointer", transition:"all 0.15s" }}
              onMouseEnter={e=>{e.target.style.borderColor=C.primary;e.target.style.color=C.primary;}}
              onMouseLeave={e=>{e.target.style.borderColor=C.border;e.target.style.color=C.textMuted;}}>
              v{APP_VERSION} · {RELEASE_NAME}
            </button>
          </div>
        </div>
      </footer>

      {showChangelog && <ChangelogModal onClose={() => setShowChangelog(false)} />}
      {showContact && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{fontWeight:700,fontSize:18}}>Get in touch</span>
              <CloseBtn onClick={() => setShowContact(false)} />
            </div>
            <p style={{fontSize:14,color:C.textSub,marginBottom:28,lineHeight:1.6}}>
              We'd love to hear from you — whether you're a trainer, a client, or just curious.<br />Find us on any of the channels below.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:28}}>
              {SOCIAL_LINKS.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 18px",
                    background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:10,
                    textDecoration:"none", color:C.text, transition:"border-color 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=s.color}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                  <div style={{ width:34, height:34, borderRadius:"50%", background:s.color+"22",
                    border:`1px solid ${s.color}40`, display:"flex", alignItems:"center",
                    justifyContent:"center", fontSize:13, fontWeight:700, color:s.color, flexShrink:0 }}>
                    {s.icon}
                  </div>
                  <div style={{textAlign:"left"}}>
                    <div style={{fontWeight:600,fontSize:14}}>{s.label}</div>
                    <div style={{fontSize:12,color:C.textMuted}}>{s.href.replace("mailto:","")}</div>
                  </div>
                  <div style={{marginLeft:"auto",color:C.textMuted,fontSize:12}}>↗</div>
                </a>
              ))}
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setShowContact(false)} style={{width:"100%"}}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── LEGAL PAGES ───────────────────────────────────────────────────────────────
function LegalPage({ title, sections, setView }) {
  return (
    <div className="page-content" style={{ maxWidth: 820 }}>
      <button className="btn btn-outline btn-sm" onClick={() => setView("home")} style={{ marginBottom: 24 }}>← Back to Home</button>
      <div className="accent-line" />
      <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 8 }}>{title}</h1>
      <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 32 }}>Last updated: 6 April 2026</p>
      {sections.map(s => (
        <div key={s.heading} style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, color: C.text }}>{s.heading}</h2>
          <p style={{ fontSize: 14, lineHeight: 1.75, color: C.textSub, whiteSpace: "pre-line" }}>{s.body}</p>
        </div>
      ))}
    </div>
  );
}

function PrivacyPage({ setView }) {
  const sections = [
    { heading: "1. Introduction",
      body: "myPTOnline (\"we\", \"us\") provides a platform for personal trainers and their clients. This Privacy Policy explains what personal data we collect, why we collect it, and how we protect it. By using the service you agree to the practices described here." },
    { heading: "2. Information we collect",
      body: "Account data: name, email address, phone number, password hash, and professional credentials for trainers.\nClient data: contact details, fitness objectives, body metrics (height, weight), PAR-Q answers, session bookings, performance records, and any notes you choose to enter.\nTechnical data: IP address, device type, browser, and basic interaction logs used for security and to keep the service working." },
    { heading: "3. How we use your data",
      body: "We use your data to operate the platform, manage bookings and sessions, communicate with you about your account, comply with our legal obligations, and improve the service. We never sell personal data." },
    { heading: "4. PAR-Q and health information",
      body: "Health-screening answers (PAR-Q) are stored solely so the trainer can deliver a safe service. Access is restricted to the responsible PT and the client. We do not share this information with third parties." },
    { heading: "5. Sharing",
      body: "Personal data is shared only with: (a) the personal trainer or client connected to the account, (b) service providers strictly necessary to run the platform (hosting, email delivery), and (c) authorities when required by law." },
    { heading: "6. Your rights",
      body: "Under UK GDPR you have the right to access, correct, export, or delete your personal data, and to object to or restrict processing. To exercise any of these rights contact privacy@myptonline.example." },
    { heading: "7. Retention",
      body: "We keep account and session data for as long as your account is active and for up to 24 months after closure, after which records are deleted or anonymised." },
    { heading: "8. Cookies",
      body: "We use a small number of strictly-necessary cookies and local storage entries to keep you signed in and to remember your preferences. No third-party advertising cookies are used." },
    { heading: "9. Contact",
      body: "Questions about this policy can be sent to privacy@myptonline.example." },
  ];
  return <LegalPage title="Privacy Policy" sections={sections} setView={setView} />;
}

function TermsPage({ setView }) {
  const sections = [
    { heading: "1. Acceptance",
      body: "By creating an account or using myPTOnline you agree to these Terms of Service. If you do not agree, please do not use the platform." },
    { heading: "2. Eligibility",
      body: "Personal trainers must hold a recognised professional qualification (e.g. REPs, NRPT, YMCA) and provide accurate registration details. Clients must be 18 or older, or have parental consent." },
    { heading: "3. Use of the service",
      body: "You agree to use myPTOnline only for lawful personal-training purposes. You will not misuse the platform, attempt to access data that is not yours, or upload content that is unlawful, harmful, or infringes the rights of others." },
    { heading: "4. PT–client relationship",
      body: "myPTOnline is a tool that connects PTs and clients. The professional relationship — including duty of care, suitability of programmes, and pricing — is between the PT and the client. We are not a party to that contract." },
    { heading: "5. Health & safety (PAR-Q)",
      body: "Trainers must screen each client with the PAR-Q before assigning a fitness plan. Clients must answer truthfully and seek medical advice where indicated. Use of the platform is at your own risk." },
    { heading: "6. Content ownership",
      body: "You retain ownership of the exercises, plans, notes, and media you upload. By uploading, you grant myPTOnline a non-exclusive licence to host and display that content as needed to provide the service to your clients." },
    { heading: "7. Account suspension",
      body: "We may suspend or terminate any account that breaches these terms, behaves abusively, or compromises platform security." },
    { heading: "8. Limitation of liability",
      body: "To the fullest extent permitted by law, myPTOnline is provided \"as is\" without warranties of any kind. We are not liable for indirect, incidental, or consequential losses arising from use of the platform." },
    { heading: "9. Changes to these terms",
      body: "We may update these terms from time to time. Material changes will be announced in the dashboard and take effect 14 days after notice." },
    { heading: "10. Governing law",
      body: "These terms are governed by the laws of England and Wales. Any disputes will be resolved in the courts of England and Wales." },
  ];
  return <LegalPage title="Terms of Service" sections={sections} setView={setView} />;
}

// ── TRIAL BANNER ─────────────────────────────────────────────────────────────
function TrialBanner({ daysLeft, onUpgrade }) {
  const urgent = daysLeft <= 7;
  return (
    <div role="region" aria-label="Free trial notice" style={{
      background: urgent ? "rgba(255,179,71,0.12)" : "rgba(200,255,87,0.08)",
      borderBottom: `1px solid ${urgent ? "#ffb34740" : "#c8ff5730"}`,
      padding: "9px 20px", display: "flex", alignItems: "center",
      justifyContent: "space-between", fontSize: 13, gap: 12,
    }}>
      <span>
        <span style={{ color: urgent ? "#ffb347" : "#c8ff57" }}>
          {urgent ? "⚠ " : "⏱ "}
          <strong>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong>
        </span>
        <span style={{ color: "#b8b8c0" }}> remaining on your free trial</span>
      </span>
      <button className="btn btn-primary btn-sm" onClick={onUpgrade} style={{ whiteSpace: "nowrap" }}>
        Upgrade →
      </button>
    </div>
  );
}

// ── PRICING MODAL ─────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: "annual",
    name: "Annual Access",
    price: "£79",
    period: "/year",
    desc: "Everything you need to run your PT business",
    features: [
      "Unlimited clients",
      "Session management & booking",
      "Fitness plan library",
      "Progress tracking & charts",
      "Client messaging",
      "Contact Me enquiries",
    ],
  },
  {
    id: "annual-insurance",
    name: "Annual + Insurance",
    price: "£149",
    period: "/year",
    badge: "Best Value",
    desc: "Full access plus professional insurance cover",
    features: [
      "Everything in Annual",
      "PT Professional Insurance",
      "£5M public liability cover",
      "Treatment liability included",
      "Instant certificate download",
    ],
  },
];

function PricingModal({ expired, ptId, onClose, onSubscribed }) {
  const [selected, setSelected] = useState("annual-insurance");
  const [loading, setLoading] = useState(false);

  const handleCheckout = () => {
    setLoading(true);
    // ── Production: create a Stripe Checkout Session on your backend, then:
    // window.location.href = checkoutUrl;
    // Mock success for demo:
    setTimeout(() => { markSubscribed(ptId, selected); onSubscribed(); }, 1800);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px", overflowY:"auto" }}>
      <div style={{ background:"#141417", borderRadius:16, maxWidth:660, width:"100%", padding:"32px 24px", border:"1px solid rgba(255,255,255,0.10)", position:"relative" }}>
        {!expired && (
          <button onClick={onClose} style={{ position:"absolute", top:14, right:16, background:"none", color:"#8e8e98", fontSize:20, lineHeight:1 }}>✕</button>
        )}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          {expired && <div style={{ fontSize:38, marginBottom:10 }}>🔒</div>}
          <h2 style={{ fontSize:22, fontWeight:700, marginBottom:6 }}>
            {expired ? "Your free trial has ended" : "Upgrade myPTOnline"}
          </h2>
          <p style={{ color:"#b8b8c0", fontSize:14 }}>
            {expired ? "Choose a plan to continue using the platform" : "Choose an annual plan to unlock your full account"}
          </p>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:24 }}>
          {PLANS.map(p => (
            <div key={p.id} onClick={() => setSelected(p.id)} style={{
              background: selected === p.id ? "rgba(200,255,87,0.08)" : "#1c1c20",
              border: `2px solid ${selected === p.id ? "#c8ff57" : "rgba(255,255,255,0.10)"}`,
              borderRadius:12, padding:18, cursor:"pointer", position:"relative",
              transition:"border-color 0.15s",
            }}>
              {p.badge && (
                <div style={{ position:"absolute", top:-10, right:12, background:"#c8ff57", color:"#0c0c0e", fontSize:10, fontWeight:700, padding:"2px 10px", borderRadius:20 }}>
                  {p.badge}
                </div>
              )}
              <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{p.name}</div>
              <div style={{ marginBottom:10 }}>
                <span style={{ fontSize:28, fontWeight:800, color:"#c8ff57" }}>{p.price}</span>
                <span style={{ fontSize:12, color:"#8e8e98" }}>{p.period}</span>
              </div>
              <div style={{ fontSize:12, color:"#b8b8c0", marginBottom:12 }}>{p.desc}</div>
              <ul style={{ listStyle:"none", padding:0, margin:0 }}>
                {p.features.map(f => (
                  <li key={f} style={{ fontSize:12, color:"#b8b8c0", marginBottom:5, display:"flex", gap:6, alignItems:"flex-start" }}>
                    <span style={{ color:"#c8ff57", flexShrink:0 }}>✓</span>{f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <button
          className="btn btn-primary"
          style={{ width:"100%", padding:14, fontSize:15, fontWeight:700 }}
          onClick={handleCheckout}
          disabled={loading}
        >
          {loading ? "Redirecting to Stripe…" : `Continue with ${PLANS.find(p => p.id === selected)?.name} →`}
        </button>
        <p style={{ textAlign:"center", fontSize:11, color:"#8e8e98", marginTop:10 }}>
          Secured by Stripe · Renews annually · VAT included
        </p>
      </div>
    </div>
  );
}

// ── EMAIL CONFIRMED WELCOME ───────────────────────────────────────────────────
function EmailConfirmedPage({ user, setView }) {
  return (
    <div style={{ minHeight: "75vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 500 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", background: C.primaryGlow, border: `2px solid ${C.primary}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 38, margin: "0 auto 28px" }}>🎉</div>
        <h1 style={{ fontSize: 30, fontWeight: 700, marginBottom: 12 }}>Welcome to myPTOnline!</h1>
        <p style={{ color: C.textSub, lineHeight: 1.8, marginBottom: 8 }}>
          Hi <strong style={{ color: C.text }}>{user?.name}</strong> — your email has been confirmed and your account is active.
        </p>
        <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 36 }}>
          You're ready to build your profile, manage clients, and grow your PT business.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button className="btn btn-primary btn-lg" onClick={() => setView("dashboard")}>Go to my Dashboard →</button>
          <button className="btn btn-outline btn-lg" onClick={() => setView("my-profile")}>Complete my Profile</button>
        </div>
      </div>
    </div>
  );
}

// ── APP ROOT ──────────────────────────────────────────────────────────────────
export default function App() {
  const [view, setView] = useState("home");
  const [completeEmail, setCompleteEmail] = useState(""); // email shown on the complete-profile screen for an authenticated profile-less account
  const [messageClient, setMessageClient] = useState(null); // client thread to auto-open in Messages (set from "Message" on a client row)
  const [user, setUser] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [ptProfile, setPTProfile] = useState(null);
  const [ptSlug, setPtSlug] = useState(null);
  const [resolvedSlugPt, setResolvedSlugPt] = useState(null);
  const [slugLookingUp, setSlugLookingUp] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [subTick, setSubTick] = useState(0); // forces re-render after subscribe
  const inPasswordRecovery = useRef(IS_RECOVERY_LINK);  // true if page loaded from a reset link

  const isPT = user?.role === "pt";
  const ptId = user?.id;
  const subbed = isPT && isSubscribed(ptId);
  const trialDaysLeft = isPT && !subbed ? getTrialDaysLeft(ptId) : null;
  const trialExpired  = trialDaysLeft === 0;

  const handleSubscribed = () => { setShowPricing(false); setSubTick(n => n + 1); };

  // ── Auth session + email-verification callback ──────────────────────────────
  useEffect(() => {
    // 0a. Restore admin session from localStorage (a signed admin token from
    // /api/admin-login — not a Supabase session). AdminPanel re-validates the
    // token; if it has expired, admin writes 401 and it drops back to its login.
    if (localStorage.getItem("mypt_admin_token")) {
      setUser({ role: "admin", name: "Admin", id: "admin" });
      setView("admin");
      return;
    }

    // 0b. If page loaded from a recovery link, go straight to set-password
    if (IS_RECOVERY_LINK) setView("set-password");

    // 0c. Signup invite link (/?signup=1) → open the signup form directly.
    // If a session also exists, getSession() below routes a logged-in user onward.
    if (WANTS_SIGNUP) setView("signup");

    // 0c-ii. Client invite link (/?client=<username>) → client sign-up form.
    if (CLIENT_SIGNUP_USER) setView("client-signup");

    // 0c-iii. Client activation link (/?setup=<token>) → set-password form.
    if (CLIENT_SETUP_TOKEN) setView("client-setup");

    // 0d. Expired/used reset or magic link → send to forgot-password for a fresh one.
    if (IS_EXPIRED_LINK) setView("forgot");

    // 1. Restore existing session on page load (skip if in recovery or PKCE callback)
    // With PKCE, the code exchange triggers onAuthStateChange which handles routing;
    // calling getSession() here would race PASSWORD_RECOVERY and may route to dashboard first.
    if (!inPasswordRecovery.current && !IS_PKCE_CALLBACK) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user && !user) {
          buildProfile(session.user).then(profile => {
            if (profile?.status === "orphan") { setCompleteEmail(session.user.email || ""); setView("complete-profile"); return; }
            if (profile) { setUser(profile); setView(profile.status === "suspended" ? "suspended" : profile.status === "pending" ? "pending-approval" : profile.role === "pt" ? "dashboard" : "client-dash"); }
          });
        } else if (!session) {
          // No session — if a returning client has a stored PT slug, redirect to their portal
          const savedSlug = localStorage.getItem("mypt_client_pt_slug");
          if (savedSlug && !IS_RECOVERY_LINK && !window.location.hash.includes("/pt/")) {
            window.location.hash = `#/pt/${savedSlug}`;
          }
        }
      });
    }

    // 2. Subscribe to auth events (login, logout, email confirmation)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Password reset link clicked — belt-and-braces catch in case hash check missed it
      if (event === "PASSWORD_RECOVERY") {
        inPasswordRecovery.current = true;
        setView("set-password");
        return;
      }
      // Skip auto-login while user is on the set-password screen
      if (event === "SIGNED_IN" && inPasswordRecovery.current) return;

      if (event === "SIGNED_IN" && session?.user) {
        buildProfile(session.user).then(profile => {
          if (profile?.status === "orphan") { setCompleteEmail(session.user.email || ""); setView("complete-profile"); return; }
          if (profile) {
            setUser(profile);
            setView(profile.status === "suspended" ? "suspended" : profile.status === "pending" ? "pending-approval" : profile.role === "pt" ? (IS_CONFIRM_LINK ? "email-confirmed" : "dashboard") : "client-dash");
          }
        });
      }
      if (event === "SIGNED_OUT") {
        setUser(null);
        localStorage.removeItem("mypt_client_pt_slug");
        setView("home");
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // After an authenticated profile-less account finishes the complete-profile
  // form, drop them straight into the app (no re-login) using the freshly
  // created pts row's id + status.
  async function handleProfileCreated({ id, status } = {}, name = "") {
    const { data: { session } } = await supabase.auth.getSession();
    const email = session?.user?.email || "";
    if (status === "pending") {
      setUser({ role: "pt", status: "pending", name, id, email });
      setView("pending-approval");
    } else {
      setUser({ role: "pt", name, id, email, contactMeCount: 0, msgCount: 0 });
      setView("dashboard");
    }
  }

  // Build a lightweight profile object from a Supabase user record
  async function buildProfile(sbUser) {
    try {
      const role = sbUser.user_metadata?.role;
      if (role === "pt") {
        const { data: ptRows } = await supabase.from("pts").select("*").eq("user_id", sbUser.id).order("id").limit(1);
        const ptRow = Array.isArray(ptRows) ? ptRows[0] : ptRows;
        if (ptRow) {
          if (ptRow.status === "suspended") return { role: "pt", status: "suspended", name: ptRow.name, id: ptRow.id, email: sbUser.email };
          if (ptRow.status === "pending") return { role: "pt", status: "pending", name: ptRow.name, id: ptRow.id, email: sbUser.email };
          return { role: "pt", name: ptRow.name, id: ptRow.id, email: sbUser.email, contactMeCount: 0, msgCount: 0 };
        }
        // No pts row for a PT-role login = an orphaned account (profile removed or
        // never created). Do NOT auto-create a blank profile — that silently
        // "resurrects" a deleted PT. Signal the caller to route to complete-profile
        // (keeping the session) so the user can finish setting up their own profile.
        return { role: "pt", status: "orphan", email: sbUser.email };
      }
      if (role === "client") {
        const { data: row } = await supabase.from("clients").select("*").eq("user_id", sbUser.id).maybeSingle();
        if (row) {
          let ptName = sbUser.user_metadata?.ptName || "";
          if (row.pt_id) {
            const { data: ptRow } = await supabase.from("pts").select("name").eq("id", row.pt_id).maybeSingle();
            ptName = ptRow?.name || ptName;
          }
          return { role: "client", name: row.name, id: row.id, email: sbUser.email, ptName };
        }
        return { role: "client", name: sbUser.user_metadata?.name || sbUser.email, id: sbUser.id, email: sbUser.email, ptName: sbUser.user_metadata?.ptName || "" };
      }
      // Authenticated but no usable role (a legacy/broken account with no role in
      // metadata — e.g. from the old magic-link backdoor). Don't stall silently:
      // treat it as an orphan so the caller routes to complete-profile, letting the
      // user finish their profile instead of being stuck on a login that goes nowhere.
      return { role: null, status: "orphan", email: sbUser.email };
    } catch { /* transient error — leave the user where they are */ }
    return null;
  }

  // Hash-based routing: detect #/pt/<slug> on load and on hash change
  useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash;
      const match = hash.match(/^#\/pt\/(.+)$/);
      if (match) {
        setPtSlug(match[1]);
        setResolvedSlugPt(null);
        setSlugLookingUp(true); // stay in loading state until lookup resolves
      } else {
        setPtSlug(null);
        setResolvedSlugPt(null);
        setSlugLookingUp(false);
      }
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  // When ptSlug is set, find the PT — local cache first, then server API fallback
  useEffect(() => {
    if (!ptSlug) return;
    const local = getAll("pts").find(p => slugify(p.name) === ptSlug);
    if (local) { setResolvedSlugPt(local); setSlugLookingUp(false); return; }
    // Not in local cache — call server API (uses service role, handles auth-only PTs)
    fetch(`/api/resolve-pt-slug?slug=${encodeURIComponent(ptSlug)}`)
      .then(r => r.json())
      .then(({ pt }) => { setResolvedSlugPt(pt || null); })
      .catch(() => { setResolvedSlugPt(null); })
      .finally(() => { setSlugLookingUp(false); });
  }, [ptSlug]);

  // If we have a PT slug, show the branded client login (or a loading/not-found state)
  if (ptSlug) {
    if (resolvedSlugPt) {
      return (
        <>
          <style>{css}</style>
          <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
            <ClientPTLogin pt={resolvedSlugPt} setView={setView} setUser={setUser} />
          </div>
        </>
      );
    }
    // Show loading while local + Supabase lookup is in flight
    if (slugLookingUp) {
      return (
        <>
          <style>{css}</style>
          <div style={{ minHeight: "100vh", background: C.bg, color: C.text, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ color: C.textMuted, fontSize: 15 }}>Loading…</div>
          </div>
        </>
      );
    }
    // Lookup complete — PT not found, fall through to home
  }

  const renderView = () => {
    // Hard block: a suspended PT can never reach any app view, whatever `view`
    // says (e.g. clicking a nav link). Only signing out escapes this.
    if (user?.status === "suspended") {
      return <SuspendedPage onSignOut={async () => { await supabase.auth.signOut(); setUser(null); setView("home"); }} />;
    }
    switch (view) {
      case "pending-approval": return <PendingApprovalPage onSignOut={async () => { await supabase.auth.signOut(); setUser(null); setView("home"); }} />;
      case "suspended": return <SuspendedPage onSignOut={async () => { await supabase.auth.signOut(); setUser(null); setView("home"); }} />;
      case "home":           return <HomePage setView={setView} />;
      case "login":          return <LoginPage setView={setView} setUser={setUser} />;
      case "signup":         return <SignUpPage setView={setView} />;
      case "complete-profile": return <SignUpPage setView={setView} completeMode sessionEmail={completeEmail} onProfileCreated={handleProfileCreated} />;
      case "forgot":         return <ForgotPage setView={setView} />;
      case "set-password":   return <SetPasswordPage setView={setView} clearRecovery={() => { inPasswordRecovery.current = false; }} />;
      case "find-pt":        return <FindPT setView={setView} setPTProfile={setPTProfile} />;
      case "dashboard":      return <Dashboard user={user} setView={setView} setSelectedClient={setSelectedClient} />;
      case "clients":        return <ClientsList user={user} setView={setView} setSelectedClient={setSelectedClient} onMessageClient={(c) => { setMessageClient(c); setView("pt-messages"); }} />;
      case "client-detail":  return <ClientDetail client={selectedClient} setView={setView} />;
      case "reg-client-detail": return <RegisteredClientDetail client={selectedClient} setView={setView} />;
      case "fitness-plans":  return <FitnessPlans user={user} />;
      case "exercises":      return <Exercises user={user} />;
      case "contact-me":     return <ContactMePage />;
      case "testimonials":   return <TestimonialsPage user={user} />;
      case "monthly-report": return <MonthlyReport user={user} onBack={() => setView("dashboard")} />;
      case "client-dash":    return <ClientDashboard user={user} />;
      case "client-sessions":return <ClientSessions user={user} />;
      case "client-messages":return <MessagesPage user={user} messageClient={messageClient} clearMessageClient={() => setMessageClient(null)} />;
      case "pt-messages":    return <MessagesPage user={user} messageClient={messageClient} clearMessageClient={() => setMessageClient(null)} />;
      case "client-signup":  return <ClientSignupPage setView={setView} presetUsername={CLIENT_SIGNUP_USER} />;
      case "client-setup":   return <ClientSetupLoginPage setView={setView} token={CLIENT_SETUP_TOKEN} />;
      case "privacy":        return <PrivacyPage setView={setView} />;
      case "terms":          return <TermsPage setView={setView} />;
      case "my-profile": {
        let myPt = user && query("pts", p => p.name === user.name)[0];
        if (!myPt && user) {
          // Real Supabase PT whose row isn't in local cache yet — build from user
          myPt = { id: user.id, name: user.name, gender: "M", rate: 0, location: "", specialism: "", membership: "REPS", level: 3, experience: "", qualifications: [] };
          // Kick off background API call to create the pts row in Supabase
          const slug = slugify(user.name);
          fetch(`/api/resolve-pt-slug?slug=${slug}`).catch(() => {});
        }
        return myPt
          ? <PTProfilePage pt={myPt} editable={true} accountEmail={user?.email || ""} onClose={() => setView("dashboard")} onContact={() => setView("dashboard")} />
          : <Dashboard user={user} setView={setView} setSelectedClient={setSelectedClient} />;
      }
      case "email-confirmed": return <EmailConfirmedPage user={user} setView={setView} />;
      case "admin":          return <AdminPanel
                                      onAuthed={() => setUser({ role: "admin", name: "Admin", id: "admin" })}
                                      onSignedOut={() => { setUser(null); setView("home"); }} />;
      default:               return <HomePage setView={setView} />;
    }
  };

  return (
    <>
      <style>{css}</style>
      <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", background:C.bg, color:C.text }}>
        <Header view={view} setView={setView} user={user} setUser={setUser} />
        {isPT && user?.status !== "suspended" && !subbed && !trialExpired && (
          <TrialBanner daysLeft={trialDaysLeft} onUpgrade={() => setShowPricing(true)} />
        )}
        <main style={{ flex:1 }}>{renderView()}</main>
        <Footer setView={setView} />
      </div>
      {isPT && trialExpired && !subbed && (
        <PricingModal expired={true} ptId={ptId} onClose={() => {}} onSubscribed={handleSubscribed} />
      )}
      {showPricing && !trialExpired && (
        <PricingModal expired={false} ptId={ptId} onClose={() => setShowPricing(false)} onSubscribed={handleSubscribed} />
      )}
    </>
  );
}
